import { useState, useCallback, useMemo, useEffect, useRef } from "react";
import {
  COLORS as RAW_COLORS, INTEREST_OPTIONS, CHILD_AVATAR_COLORS as RAW_AVATAR_COLORS,
  NORTH_LONDON_CAMPS, NORTH_LONDON_NEIGHBOURHOODS,
  TERM_TIME_ACTIVITIES, TERM_TIME_NEIGHBOURHOODS,
  SOUTH_WEST_LONDON_CAMPS, SOUTH_WEST_LONDON_NEIGHBOURHOODS,
  SOUTH_WEST_LONDON_TERM_TIME_ACTIVITIES, SOUTH_WEST_LONDON_TERM_TIME_NEIGHBOURHOODS,
  DISTANCE_OPTIONS,
} from "./data";
import {
  lookupPostcode, distanceTo, distanceLabel, distanceBadgeStyle,
} from "./geo";
import { supabase } from "./supabaseClient";
import AuthGate from "./AuthGate";

// ── Palette: deep navy · slate blue · sage green · soft gray ──────────────────
const BRAND = {
  navy:     "#1C2B3A",
  slate:    "#4A6FA5",
  sage:     "#7A9E87",
  sagePale: "#EBF2ED",
  gray:     "#F4F5F7",
  grayMid:  "#9BA8B0",
  border:   "#DDE3E8",
  white:    "#FFFFFF",
  ink:      "#1C2B3A",
  muted:    "#6B7A85",
};

// ── Calm palette override ─────────────────────────────────────────────────────
// The app-wide decorative palette. We keep the original (RAW_COLORS) as a
// fallback and remap every candy hue to a quieter cousin in the navy/slate/sage
// family. Each hue stays visually distinct so category colour-coding (coverage
// types, event types, budget bars) still reads — it's just calmer. One edit
// here recolours every tab.
const COLORS = {
  ...RAW_COLORS,
  ink:        "#1C2B3A",
  muted:      "#6B7A85",
  border:     "#DDE3E8",
  white:      "#FFFFFF",
  cream:      "#F5F2EC",
  sage:       "#7A9E87",
  sageLight:  "#EBF2ED",
  sageMid:    "#B7C9BD",
  plum:       "#5E5468",
  plumMid:    "#7C6A88",
  plumLight:  "#F1ECF3",
  blush:      "#D6E0EC",
  blushLight: "#EEF2F8",
  blushDark:  "#4A6FA5",
  orange:     "#C08A4A",
  orangeLight:"#F5EFE6",
  yellow:     "#E4D6A8",
  yellowDark: "#7E6222",
  red:        "#B4524E",
};

// Calm child-avatar bubble colours (same family as owner chips), white initials.
const CHILD_AVATAR_COLORS = ["#7A9E87", "#4A6FA5", "#8A7896", "#A8845A", "#6E8CA8", "#B4795A"];

// Strip emoji/pictographs from a string (used to clean labels drawn from data).
const stripEmoji = (s) => (s || "")
  .replace(/[\u{1F000}-\u{1FAFF}\u{2600}-\u{27BF}\u{2190}-\u{21FF}\u{2B00}-\u{2BFF}\u{FE0F}\u{20E3}]/gu, "")
  .replace(/\s{2,}/g, " ")
  .trim();

const COVERAGE_TYPES = [
  { id: "camp",        label: "At Camp",        color: COLORS.sage,      bg: COLORS.sageLight,   border: COLORS.sageMid  },
  { id: "grandparent", label: "Grandparents",    color: COLORS.plumMid,   bg: COLORS.blushLight,  border: COLORS.blush    },
  { id: "childminder", label: "Childminder",     color: "#8B5000",        bg: COLORS.orangeLight, border: COLORS.orange   },
  { id: "friend",      label: "Friend / Other",  color: "#2A6496",        bg: "#E8F4FD",          border: "#90CAF9"       },
  { id: "work",        label: "Work from home",  color: COLORS.muted,     bg: COLORS.cream,       border: COLORS.border   },
  { id: "free",        label: "Free day",        color: COLORS.yellowDark, bg: COLORS.yellow + "55", border: COLORS.yellow },
];

const S = {
  app: { fontFamily: "'Inter','Helvetica Neue',sans-serif", background: BRAND.gray, minHeight: "100vh", color: BRAND.ink },
  header: { background: BRAND.navy, padding: "16px 20px 14px", display: "flex", alignItems: "center", gap: 14 },
  logo: { width: 48, height: 48, borderRadius: 12, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center", background: BRAND.white, flexShrink: 0 },
  appName: { color: BRAND.white, fontSize: 22, fontWeight: 700, letterSpacing: "-0.5px", margin: 0 },
  tagline: { color: BRAND.grayMid, fontSize: 12, margin: 0 },
  // All six tabs share the width so none sit off-screen on a phone (Alerts was hidden at 375px).
  nav: { display: "flex", justifyContent: "center", background: BRAND.white, borderBottom: `2px solid ${BRAND.border}`, padding: "0 4px", gap: 0, overflowX: "auto" },
  navBtn: (a) => ({ flex: "1 1 0", maxWidth: 120, padding: "11px 4px", border: "none", borderBottom: a ? `3px solid ${BRAND.slate}` : "3px solid transparent", background: "transparent", color: a ? BRAND.navy : BRAND.muted, fontWeight: a ? 700 : 500, fontSize: 13, cursor: "pointer", whiteSpace: "nowrap" }),
  main: { padding: "18px 16px 100px", maxWidth: 680, margin: "0 auto" },
  card: { background: BRAND.white, borderRadius: 14, padding: "18px", marginBottom: 14, border: `1px solid ${BRAND.border}`, boxShadow: "0 1px 4px rgba(28,43,58,0.06)" },
  sectionTitle: { fontSize: 15, fontWeight: 600, color: BRAND.navy, marginBottom: 14 },
  label: { fontSize: 11, fontWeight: 700, color: BRAND.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 5, display: "block" },
  input: { width: "100%", padding: "10px 13px", borderRadius: 8, border: `1.5px solid ${BRAND.border}`, fontSize: 14, color: BRAND.ink, background: BRAND.white, boxSizing: "border-box", outline: "none" },
  row: { display: "flex", gap: 10, marginBottom: 12, flexWrap: "wrap" },
  col: { flex: 1, minWidth: 130 },
  btnPrimary: { background: BRAND.navy, color: BRAND.white, border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" },
  btnSecondary: { background: BRAND.sagePale, color: BRAND.sage, border: `1.5px solid ${BRAND.sage}`, borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" },
  btnGhost: { background: "transparent", color: BRAND.muted, border: `1.5px solid ${BRAND.border}`, borderRadius: 8, padding: "7px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer" },
  chip: (active, bg, col) => ({ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${active ? col || BRAND.slate : BRAND.border}`, background: active ? (bg || BRAND.sagePale) : BRAND.white, color: active ? (col || BRAND.navy) : BRAND.muted, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.12s" }),
  badge: (c) => ({ display: "inline-block", padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: c === "sage" ? BRAND.sagePale : c === "blush" ? "#EDF2F7" : c === "yellow" ? "#FFF3CD" : c === "red" ? "#FDEAED" : BRAND.gray, color: c === "sage" ? BRAND.sage : c === "blush" ? BRAND.slate : c === "yellow" ? "#856404" : c === "red" ? "#C0392B" : BRAND.muted }),
  actCard: { background: BRAND.sagePale, borderRadius: 10, padding: "10px 13px", marginBottom: 8, display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 10 },
  summaryBox: { background: BRAND.navy, borderRadius: 12, padding: 18, color: BRAND.white, fontFamily: "'Inter','Helvetica Neue',sans-serif", fontSize: 13, lineHeight: 1.7, whiteSpace: "pre-wrap", wordBreak: "break-word" },
  toast: { position: "fixed", bottom: 24, left: "50%", transform: "translateX(-50%)", background: BRAND.navy, color: BRAND.white, borderRadius: 20, padding: "10px 22px", fontSize: 13, fontWeight: 600, zIndex: 999, boxShadow: "0 4px 20px rgba(0,0,0,0.25)", pointerEvents: "none" },
  emptyState: { textAlign: "center", color: BRAND.muted, padding: "28px 10px", fontSize: 13 },
  footer: { background: BRAND.white, borderTop: `1px solid ${BRAND.border}`, padding: "14px 16px", display: "flex", justifyContent: "center" },
  footerBtn: { background: "transparent", border: "none", color: BRAND.muted, fontSize: 12, fontWeight: 600, cursor: "pointer", padding: "6px 10px", letterSpacing: "0.3px" },
  homeSectionTitle: { fontSize: 12, fontWeight: 700, color: BRAND.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 },
  holidaySectionTitle: { fontSize: 12, fontWeight: 700, color: BRAND.slate, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 },
  italic: { fontSize: 12, color: COLORS.muted, fontStyle: "italic" },
  helpText: { fontSize: 13, color: COLORS.muted, marginBottom: 14, lineHeight: 1.5 },
  helpText16: { fontSize: 13, color: COLORS.muted, marginBottom: 16, lineHeight: 1.5 },
  budgetBar: (pct) => ({ height: 8, borderRadius: 4, background: `linear-gradient(90deg,${pct > 90 ? COLORS.red : pct > 70 ? COLORS.orange : COLORS.sage},${pct > 90 ? COLORS.blushDark : pct > 70 ? COLORS.blushDark : COLORS.sageMid})`, width: `${Math.min(pct, 100)}%`, transition: "width 0.4s ease" }),
};

// ── Calm category palette ─────────────────────────────────────────────────────
// One harmonised, low-saturation set drawn from the navy/slate/sage brand family,
// replacing the brighter COLORS pastels for event/coverage categories. dot = the
// small marker; bg/border = the soft tint chip behind an event row.
const CAT = {
  school:    { dot: "#4A6FA5", bg: "#EEF2F8", border: "#D6E0EC" },
  activity:  { dot: "#7A9E87", bg: "#EBF2ED", border: "#D7E4DB" },
  holiday:   { dot: "#8A7896", bg: "#F1ECF3", border: "#E1D7E6" },
  home:      { dot: "#A8845A", bg: "#F5EFE6", border: "#E9DECB" },
  childcare: { dot: "#6E8CA8", bg: "#EDF2F6", border: "#D6E1EA" },
};

// To-do list categories (calm, distinct tones from the same family) and a small
// colour cycle for owner chips (assigned by the owner's position in helpers).
const TODO_CATEGORIES = [
  { id: "school",     label: "School",     dot: "#4A6FA5", bg: "#EEF2F8", text: "#3a5a8a" },
  { id: "health",     label: "Health",     dot: "#8A7896", bg: "#F1ECF3", text: "#6e5c7a" },
  { id: "finance",    label: "Finance",    dot: "#A8845A", bg: "#F5EFE6", text: "#7a5a2e" },
  { id: "household",  label: "Household",  dot: "#6E8CA8", bg: "#EDF2F6", text: "#3f647f" },
  { id: "activities", label: "Activities", dot: "#7A9E87", bg: "#EBF2ED", text: "#4f6b58" },
];
const OWNER_COLORS = ["#7A9E87", "#4A6FA5", "#8A7896", "#A8845A", "#6E8CA8", "#B4795A"];

const TODO_REPEATS = [
  { id: "none", label: "Does not repeat" },
  { id: "weekly", label: "Weekly" },
  { id: "monthly", label: "Monthly" },
  { id: "quarterly", label: "Every 3 months" },
  { id: "yearly", label: "Yearly" },
];
const TODO_REPEAT_LABEL = { weekly: "Repeats weekly", monthly: "Repeats monthly", quarterly: "Repeats every 3 months", yearly: "Repeats yearly" };
// Advance a YYYY-MM-DD due date by one repeat interval (local, DST-safe).
function advanceDue(ds, repeat) {
  const [y, mo, d] = ds.split("-").map(Number);
  const dt = new Date(y, mo - 1, d);
  if (repeat === "weekly") dt.setDate(dt.getDate() + 7);
  else if (repeat === "monthly") dt.setMonth(dt.getMonth() + 1);
  else if (repeat === "quarterly") dt.setMonth(dt.getMonth() + 3);
  else if (repeat === "yearly") dt.setFullYear(dt.getFullYear() + 1);
  else return ds;
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

// Minimal outline icons (Lucide calendar/mail paths). Step 2 swaps these for
// lucide-react across the whole app; keeping them local for now avoids a new
// dependency while the Calendar tab is being reviewed.
const IconCalendar = ({ size = 18, color = BRAND.muted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="3" y="4" width="18" height="18" rx="2" />
    <line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" />
  </svg>
);
const IconMail = ({ size = 16, color = BRAND.muted }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0 }} aria-hidden="true">
    <rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" />
  </svg>
);

// ── Shared line-icon set ──────────────────────────────────────────────────────
// Minimal inline-SVG icons (Lucide-style geometry) used across the app in place
// of emoji for UI chrome. Kept local to avoid adding a dependency; render with
// <Icon name="calendar" size={16} color={...} />. Emoji is retained only inside
// shareable export text, where it reads as friendly rather than as UI.
const ICON = {
  image: <><rect x="3" y="3" width="18" height="18" rx="2" /><circle cx="9" cy="9" r="2" /><path d="m21 15-4.5-4.5L5 21" /></>,
  calendar: <><rect x="3" y="4" width="18" height="18" rx="2" /><line x1="16" y1="2" x2="16" y2="6" /><line x1="8" y1="2" x2="8" y2="6" /><line x1="3" y1="10" x2="21" y2="10" /></>,
  mail: <><rect x="2" y="4" width="20" height="16" rx="2" /><path d="m22 7-10 5L2 7" /></>,
  user: <><path d="M19 21v-2a4 4 0 0 0-4-4H9a4 4 0 0 0-4 4v2" /><circle cx="12" cy="7" r="4" /></>,
  users: <><path d="M16 21v-2a4 4 0 0 0-4-4H6a4 4 0 0 0-4 4v2" /><circle cx="9" cy="7" r="4" /><path d="M22 21v-2a4 4 0 0 0-3-3.87" /><path d="M16 3.13a4 4 0 0 1 0 7.75" /></>,
  target: <><circle cx="12" cy="12" r="10" /><circle cx="12" cy="12" r="6" /><circle cx="12" cy="12" r="2" /></>,
  mapPin: <><path d="M20 10c0 6-8 12-8 12s-8-6-8-12a8 8 0 0 1 16 0Z" /><circle cx="12" cy="10" r="3" /></>,
  map: <><path d="m3 6 6-3 6 3 6-3v15l-6 3-6-3-6 3Z" /><path d="M9 3v15" /><path d="M15 6v15" /></>,
  search: <><circle cx="11" cy="11" r="8" /><path d="m21 21-4.3-4.3" /></>,
  home: <><path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" /><path d="M9 22V12h6v10" /></>,
  plane: <><path d="M17.8 19.2 16 11l3.5-3.5C21 6 21.5 4 21 3c-1-.5-3 0-4.5 1.5L13 8 4.8 6.2c-.5-.1-.9.1-1.1.5l-.3.5c-.2.5-.1 1 .3 1.3L9 12l-2 3H4l-1 1 3 2 2 3 1-1v-3l3-2 3.5 5.3c.3.4.8.5 1.3.3l.5-.2c.4-.3.6-.7.5-1.2z" /></>,
  bed: <><path d="M2 4v16" /><path d="M2 8h18a2 2 0 0 1 2 2v10" /><path d="M2 17h20" /><path d="M6 8v9" /></>,
  utensils: <><path d="M3 2v7c0 1.1.9 2 2 2a2 2 0 0 0 2-2V2" /><path d="M7 2v20" /><path d="M21 15V2a5 5 0 0 0-5 5v6c0 1.1.9 2 2 2h3Zm0 0v7" /></>,
  tree: <><path d="M12 3 7 10h3l-4 6h5v5h2v-5h5l-4-6h3z" /></>,
  ticket: <><path d="M3 7a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2v3a2 2 0 0 0 0 4v3a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-3a2 2 0 0 0 0-4z" /><path d="M9 5v14" strokeDasharray="2 3" /></>,
  palette: <><circle cx="13.5" cy="6.5" r="1.2" /><circle cx="17" cy="10.5" r="1.2" /><circle cx="8.5" cy="7.5" r="1.2" /><circle cx="6.5" cy="12" r="1.2" /><path d="M12 2C6.5 2 2 6.5 2 12s4.5 10 10 10c.9 0 1.5-.7 1.5-1.5 0-.4-.2-.8-.5-1.1-.3-.3-.5-.7-.5-1.1 0-.8.7-1.5 1.5-1.5H16c3.3 0 6-2.7 6-6 0-4.4-4.5-8-10-8Z" /></>,
  edit: <><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" /><path d="M18.5 2.5a2.1 2.1 0 0 1 3 3L12 15l-4 1 1-4z" /></>,
  star: <><path d="m12 2 2.9 6.3 6.8.8-5 4.7 1.3 6.7L12 17.9 5.9 21.5l1.3-6.7-5-4.7 6.8-.8z" /></>,
  briefcase: <><rect x="2" y="7" width="20" height="14" rx="2" /><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" /></>,
  tent: <><path d="M3.5 21 14 3" /><path d="M20.5 21 10 3" /><path d="M15.5 21 12 15l-3.5 6" /><path d="M2 21h20" /></>,
  clock: <><circle cx="12" cy="12" r="10" /><path d="M12 6v6l4 2" /></>,
  bell: <><path d="M6 8a6 6 0 0 1 12 0c0 7 3 9 3 9H3s3-2 3-9" /><path d="M10.3 21a1.94 1.94 0 0 0 3.4 0" /></>,
  list: <><line x1="8" y1="6" x2="21" y2="6" /><line x1="8" y1="12" x2="21" y2="12" /><line x1="8" y1="18" x2="21" y2="18" /><line x1="3" y1="6" x2="3.01" y2="6" /><line x1="3" y1="12" x2="3.01" y2="12" /><line x1="3" y1="18" x2="3.01" y2="18" /></>,
  repeat: <><path d="m17 2 4 4-4 4" /><path d="M3 11v-1a4 4 0 0 1 4-4h14" /><path d="m7 22-4-4 4-4" /><path d="M21 13v1a4 4 0 0 1-4 4H3" /></>,
  check: <><path d="M20 6 9 17l-5-5" /></>,
  heart: <><path d="M20.8 4.6a5.5 5.5 0 0 0-7.8 0L12 5.6l-1-1a5.5 5.5 0 0 0-7.8 7.8l1 1L12 21l7.8-7.6 1-1a5.5 5.5 0 0 0 0-7.8z" /></>,
  external: <><path d="M15 3h6v6" /><path d="M10 14 21 3" /><path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" /></>,
};
// Contact address shown in the privacy policy and error messages.
const CONTACT_EMAIL = "sjsinnovationsltd@gmail.com";
// Public web address, added to every shared schedule so it doubles as free promotion.
// Update here if the app moves to its own domain.
const APP_URL = "https://mumhq.co.uk";
const SHARE_SIGNOFF = `Shared from MumHQ · ${APP_URL.replace(/^https?:\/\//, "")}`;

const Icon = ({ name, size = 16, color = BRAND.muted, style }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, verticalAlign: "-0.15em", ...style }} aria-hidden="true">
    {ICON[name] || null}
  </svg>
);

// ── Activity website links ────────────────────────────────────────────────────
// Every listing by id, so cards always use the CURRENT data.js record. Saved
// favourites hold a copy of the listing taken when they were saved, so without
// this they would keep old website text (e.g. "Contact club directly") forever.
const ACTIVITY_BY_ID = Object.fromEntries(
  [...NORTH_LONDON_CAMPS, ...SOUTH_WEST_LONDON_CAMPS, ...TERM_TIME_ACTIVITIES, ...SOUTH_WEST_LONDON_TERM_TIME_ACTIVITIES]
    .map(a => [a.id, a])
);

// Turns a stored website value into a safe link, or null when there isn't one.
// Only http(s) links are allowed. Bare domains ("example.co.uk") from older
// saved favourites get https:// added; placeholder text never becomes a link.
function websiteHref(raw) {
  const s = (raw || "").trim();
  if (!s || /\s/.test(s)) return null;
  const candidate = /^https?:\/\//i.test(s) ? s
    : /^[a-z0-9-]+(\.[a-z0-9-]+)+([/?#]\S*)?$/i.test(s) ? `https://${s}`
    : null;
  if (!candidate) return null;
  try {
    const u = new URL(candidate);
    return (u.protocol === "https:" || u.protocol === "http:") ? u.href : null;
  } catch { return null; }
}

function formatDate(d) { return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); }
function fmtDay(ds) {
  const d = ds instanceof Date ? ds : new Date(ds + "T12:00:00");
  return d.toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" });
}

// Returns the list of carer ids assigned to a childcare day, reading every
// shape a carer can be stored in: the new multi-select array, plus the two
// older single-carer fields (ChildcareTab's coveredById and ScheduleTab's
// inline carerId). Keeps old saved data working.
function assignedCarerIds(rec) {
  if (!rec) return [];
  if (Array.isArray(rec.coveredByIds) && rec.coveredByIds.length) return rec.coveredByIds;
  const single = rec.coveredById ?? rec.carerId;
  return single ? [single] : [];
}

function parseCost(str) {
  if (!str) return 0;
  const m = str.match(/[\d.]+/);
  return m ? parseFloat(m[0]) : 0;
}

// ── Reminders ────────────────────────────────────────────────────────────────
// Rules (both editable per-reminder):
//   • Holiday-camp day (date falls inside a school break) → one reminder at
//     8:00pm the NIGHT BEFORE.
//   • Term-time / weekend activity (any other activity day) → one reminder per
//     activity, 1 HOUR BEFORE its start time.
// Edits (custom time / address / notes / on-off) are stored ON the schedule
// record itself (camp: rec.campReminder; activity: activity.reminder), so they
// persist through the existing family_data sync with no schema change.

const CAMP_DEFAULT_TIME = "20:00";
const pad2 = (n) => String(n).padStart(2, "0");

// Tolerant start-time parser. Handles "09:30", "9", "9am", "9:30am", and the
// first time in a range like "9am–3pm". Returns { h, m } or null.
function parseStartTime(str) {
  if (!str) return null;
  const s = String(str).trim().toLowerCase();
  const m = s.match(/(\d{1,2})(?::(\d{2}))?\s*(am|pm)?/);
  if (!m) return null;
  let h = parseInt(m[1], 10);
  const min = m[2] ? parseInt(m[2], 10) : 0;
  const ap = m[3];
  if (ap === "pm" && h < 12) h += 12;
  if (ap === "am" && h === 12) h = 0;
  if (h > 23 || min > 59) return null;
  return { h, m: min };
}

// Local Date from "YYYY-MM-DD" + hour + minute (avoids UTC day-roll).
function localDT(ds, h, m) {
  const [y, mo, d] = ds.split("-").map(Number);
  return new Date(y, mo - 1, d, h, m, 0, 0);
}

// Shift a "YYYY-MM-DD" by n days, staying local-date-safe.
function shiftDS(ds, n) {
  const [y, mo, d] = ds.split("-").map(Number);
  const dt = new Date(y, mo - 1, d + n);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(dt.getDate())}`;
}

function fmtWhen(dt) {
  return dt
    .toLocaleString("en-GB", {
      weekday: "short", day: "numeric", month: "short",
      hour: "numeric", minute: "2-digit", hour12: true,
    })
    .replace(",", "");
}

// Holiday periods = school breaks PLUS any half-term entered under School Terms
// (de-duplicated). Used by camp search, coverage and reminders so they all agree.
function holidayPeriods(breaks, terms) {
  const list = (breaks || []).filter(b => b.name && b.startDate && b.endDate);
  const seen = new Set(list.map(b => `${b.startDate}|${b.endDate}`));
  (terms || []).forEach(t => {
    if (!t.halfTermStart || !t.halfTermEnd) return;
    const key = `${t.halfTermStart}|${t.halfTermEnd}`;
    if (seen.has(key)) return;
    seen.add(key);
    list.push({ id: `ht-${t.id}`, name: `${(t.name || "Term").trim()} half-term`, startDate: t.halfTermStart, endDate: t.halfTermEnd });
  });
  return list.sort((x, y) => x.startDate.localeCompare(y.startDate));
}

// Build the full list of upcoming reminders from the schedule, merging any
// per-reminder edits. Returns objects sorted by when they fire.
function buildReminders(schedule, breaks, terms) {
  const bks = holidayPeriods(breaks, terms);
  const isCampDay = (ds) => bks.some((b) => ds >= b.startDate && ds <= b.endDate);
  const out = [];

  Object.keys(schedule || {}).forEach((ds) => {
    const rec = schedule[ds] || {};
    if (rec.dayType !== "activity") return;
    const acts = rec.dayData?.activities || [];
    if (!acts.length) return;

    if (isCampDay(ds)) {
      const ov = rec.campReminder || {};
      const time = ov.time || CAMP_DEFAULT_TIME;
      const [h, m] = time.split(":").map(Number);
      const fireAt = localDT(shiftDS(ds, -1), h, m);
      const names = acts.map((a) => a.name).filter(Boolean).join(", ");
      const address = ov.address ?? (acts.find((a) => a.location)?.location || "");
      const notes = ov.notes ?? (rec.dayData?.notes || "");
      out.push({
        key: `camp|${ds}`, kind: "camp", eventDate: ds, fireAt,
        // e.g. "Tilly: The Study Prep Wimbledon Holiday Camp tomorrow" — names the camp and child
        title: (() => {
          const who = [...new Set(acts.map((a) => (a.childName || "").trim()).filter(Boolean))].join(" & ");
          const what = acts.length > 1 ? `${acts[0].name} + ${acts.length - 1} more` : (acts[0]?.name || "Holiday camp");
          return `${who ? who + ": " : ""}${what} tomorrow`.slice(0, 90);
        })(),
        summary: names || "Holiday camp",
        time, address, notes,
        enabled: ov.enabled !== false,
        ruleLabel: ov.time ? "custom time, night before" : "8:00pm the night before",
      });
    } else {
      acts.forEach((a, i) => {
        const ov = a.reminder || {};
        const st = parseStartTime(a.time);
        let time = ov.time;
        if (!time) {
          if (st) {
            let mins = st.h * 60 + st.m - 60;
            if (mins < 0) mins = 0;
            time = `${pad2(Math.floor(mins / 60))}:${pad2(mins % 60)}`;
          } else {
            time = "08:00"; // start time wasn't a clock time — sensible default
          }
        }
        const [h, m] = time.split(":").map(Number);
        const fireAt = localDT(ds, h, m);
        const actId = a.id || `${i}`;
        const ruleLabel = ov.time
          ? "custom time"
          : st
          ? "1 hour before it starts"
          : "couldn't read the start time — set it below";
        out.push({
          key: `act|${ds}|${actId}`, kind: "activity", eventDate: ds, actId, fireAt,
          title: `⏰ ${a.name || "Activity"}`,
          summary: [a.name, a.time ? `(${a.time})` : ""].filter(Boolean).join(" "),
          time, address: ov.address ?? (a.location || ""), notes: ov.notes ?? "",
          enabled: ov.enabled !== false,
          ruleLabel,
        });
      });
    }
  });

  out.sort((x, y) => x.fireAt - y.fireAt);
  return out;
}

// Build reminders from to-dos that have a due date (fires 9am on the day).
// Same shape as buildReminders so it flows through the same display/push path.
function buildTodoReminders(todos) {
  const out = [];
  (todos || []).forEach((t) => {
    if (!t || t.status === "done" || !t.due) return;
    const fireAt = localDT(t.due, 9, 0);
    const rep = TODO_REPEAT_LABEL[t.repeat] || "";
    out.push({
      key: `todo|${t.id}`, kind: "todo", todoId: t.id, eventDate: t.due, fireAt,
      title: "✅ To-do due", summary: t.text,
      time: "09:00", address: "", notes: t.notes || "",
      enabled: t.remind !== false,
      ruleLabel: rep ? `${rep} · 9:00am on the day` : "9:00am on the day",
    });
  });
  return out;
}
function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = atob(base64);
  const arr = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) arr[i] = raw.charCodeAt(i);
  return arr;
}

function getBreakDays(b) {
  const days = [], cur = new Date(b.startDate), end = new Date(b.endDate);
  while (cur <= end) {
    if (cur.getDay() !== 0 && cur.getDay() !== 6) days.push(new Date(cur));
    cur.setDate(cur.getDate() + 1);
  }
  return days;
}

// ── Term-time helpers ────────────────────────────────────────────────────────

const CLASS_DAYS = [
  { id: 1, label: "Monday", short: "Mon" },
  { id: 2, label: "Tuesday", short: "Tue" },
  { id: 3, label: "Wednesday", short: "Wed" },
  { id: 4, label: "Thursday", short: "Thu" },
  { id: 5, label: "Friday", short: "Fri" },
  { id: 6, label: "Saturday", short: "Sat" },
  { id: 0, label: "Sunday", short: "Sun" },
];

const toDS = (d) => {
  // Local-date safe. `toISOString()` shifts to UTC and can roll a BST evening
  // back to the previous day — which would silently book classes on the wrong date.
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/**
 * Every occurrence of `weekday` inside a term, skipping the half-term week.
 * Returns an array of "YYYY-MM-DD" strings.
 */
function getTermSessions(term, weekday) {
  if (!term?.startDate || !term?.endDate || weekday == null) return [];
  const out = [];
  const end = new Date(term.endDate + "T12:00:00");
  const cur = new Date(term.startDate + "T12:00:00");
  const htStart = term.halfTermStart ? new Date(term.halfTermStart + "T00:00:00") : null;
  const htEnd = term.halfTermEnd ? new Date(term.halfTermEnd + "T23:59:59") : null;

  while (cur <= end) {
    if (cur.getDay() === Number(weekday)) {
      const inHalfTerm = htStart && htEnd && cur >= htStart && cur <= htEnd;
      if (!inHalfTerm) out.push(toDS(cur));
    }
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

const validTerms = (terms) => (terms || []).filter(t => t.name && t.startDate && t.endDate);

/** Days of a term that actually have something scheduled (a term is ~100 days — we only render the used ones). */
function getTermDays(term, schedule) {
  if (!term?.startDate || !term?.endDate) return [];
  return Object.keys(schedule || {})
    .filter(ds => ds >= term.startDate && ds <= term.endDate)
    .filter(ds => {
      const d = schedule[ds]?.dayData || {};
      return (d.activities?.length || d.shows?.length || d.playdates?.length);
    })
    .sort()
    .map(ds => new Date(ds + "T12:00:00"));
}

function generateICS(breaks, schedule) {
  const lines = ["BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//MumHQ//EN", "CALSCALE:GREGORIAN"];
  const esc = (s) => String(s || "").replace(/([,;\\])/g, "\\$1").replace(/\n/g, "\\n");

  Object.entries(schedule).forEach(([ds, rec]) => {
    if (!rec || typeof rec !== "object") return;
    const d = rec.dayData || {};
    const allActs = [...(d.activities || []), ...(d.shows || []), ...(d.playdates || [])];
    allActs.forEach(a => {
      const stamp = ds.replace(/-/g, "");
      lines.push("BEGIN:VEVENT");

      if (a.time) {
        // Floating local time — correct for a London-only app and avoids
        // shipping a VTIMEZONE block that some calendar clients mishandle.
        const t1 = a.time.replace(":", "") + "00";
        const t2 = (a.endTime || addHour(a.time)).replace(":", "") + "00";
        lines.push(`DTSTART:${stamp}T${t1}`, `DTEND:${stamp}T${t2}`);
      } else {
        lines.push(`DTSTART;VALUE=DATE:${stamp}`, `DTEND;VALUE=DATE:${stamp}`);
      }

      lines.push(
        `SUMMARY:${esc(a.name)}${a.childName ? ` (${esc(a.childName)})` : ""}`,
        `DESCRIPTION:${esc(a.notes)}\\nCost: ${esc(a.cost)}\\nLocation: ${esc(a.location)}`,
        `LOCATION:${esc(a.location)}`,
        `UID:mumhq-${a.seriesId || "x"}-${stamp}-${Math.random().toString(36).slice(2)}`,
        "END:VEVENT"
      );
    });
  });
  lines.push("END:VCALENDAR");
  return lines.join("\r\n");
}

function addHour(hhmm) {
  const [h, m] = hhmm.split(":").map(Number);
  return `${String((h + 1) % 24).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

function generateSummary(breaks, schedule, children, childcare = {}, helpers = []) {
  let t = "🏡 MUMHQ HOLIDAY SCHEDULE\n═══════════════════════════\n\n";
  if (children.length) t += `👧 Children: ${children.map(c => `${c.name} (${c.age})`).join(", ")}\n\n`;
  if (helpers.length) t += `🤝 Helpers: ${helpers.map(h => `${h.name} (${h.relationship})`).join(", ")}\n\n`;
  let grandTotal = 0;
  breaks.filter(b => b.name && b.startDate && b.endDate).forEach(b => {
    t += `📅 ${b.name.toUpperCase()}\n   ${new Date(b.startDate).toLocaleDateString("en-GB")} – ${new Date(b.endDate).toLocaleDateString("en-GB")}\n\n`;
    let breakTotal = 0;
    getBreakDays(b).forEach(day => {
      const ds = day.toISOString().slice(0, 10);
      const rec = schedule[ds] || {};
      const d = rec.dayData || {};
      const cc = childcare[ds] || {};
      const ct = COVERAGE_TYPES.find(ctOpt => ctOpt.id === cc.type);
      t += `   ${formatDate(day)}`;
      if (rec.dayType === "holiday") t += "  ✈️";
      else if (rec.dayType === "home") t += "  🏠";
      else if (rec.dayType === "activity") t += "  🎯";
      if (ct) t += `  ${ct.label}${cc.coveredBy ? ` (${cc.coveredBy})` : ""}`;
      t += "\n";
      if (cc.dropoff || cc.pickup) t += `     🕐 Drop: ${cc.dropoff || "–"}  Pick-up: ${cc.pickup || "–"}\n`;
      if (cc.notes) t += `     📝 ${cc.notes}\n`;
      if (!rec.dayType) { t += `     · (unplanned)\n`; return; }
      if (rec.dayType === "activity") {
        (d.activities || []).forEach(a => {
          const cost = parseCost(a.cost); breakTotal += cost;
          t += `     · ${a.name}${a.location ? ` @ ${a.location}` : ""}${a.time ? ` · ${a.time}` : ""}${a.cost ? ` · ${a.cost}` : ""}\n`;
        });
        if (!(d.activities || []).length) t += `     · (no activities added)\n`;
      } else if (rec.dayType === "holiday") {
        if (d.hotel) t += `     🏨 ${d.hotel}\n`;
        (d.activities || []).forEach(a => {
          const cost = parseCost(a.cost); breakTotal += cost;
          t += `     🎡 ${a.name}${a.location ? ` @ ${a.location}` : ""}${a.time ? ` · ${a.time}` : ""}${a.cost ? ` · ${a.cost}` : ""}\n`;
        });
        (d.restaurants || []).forEach(r => t += `     🍽 ${r.text}\n`);
        (d.parks || []).forEach(p => t += `     🌳 ${p.text}\n`);
      } else if (rec.dayType === "home") {
        (d.playdates || []).forEach(p => t += `     👫 ${p.name}${p.location ? ` @ ${p.location}` : ""}${p.time ? ` · ${p.time}` : ""}\n`);
        (d.shows || []).forEach(s => {
          const cost = parseCost(s.cost); breakTotal += cost;
          t += `     🎭 ${s.name}${s.location ? ` @ ${s.location}` : ""}${s.time ? ` · ${s.time}` : ""}${s.cost ? ` · ${s.cost}` : ""}\n`;
        });
        (d.outings || []).forEach(o => t += `     🚶 ${o.text}\n`);
        (d.homeActivities || []).forEach(a => t += `     🎨 ${a.text}\n`);
        if (d.notes) t += `     📝 ${d.notes}\n`;
      }
    });
    grandTotal += breakTotal;
    t += "\n";
  });
  t += "─────────────────────────────\n" + SHARE_SIGNOFF;
  return t;
}

function Avatar({ child, size = 36 }) {
  const idx = child.id % CHILD_AVATAR_COLORS.length;
  return (
    <div style={{ width: size, height: size, borderRadius: "50%", background: CHILD_AVATAR_COLORS[idx], display: "flex", alignItems: "center", justifyContent: "center", fontSize: size * 0.45, fontWeight: 800, color: BRAND.white, flexShrink: 0 }}>
      {child.name ? child.name[0].toUpperCase() : "?"}
    </div>
  );
}

function BudgetSummary({ breaks, schedule, budget }) {
  const allCosts = useMemo(() => {
    const result = {};
    breaks.filter(b => b.name && b.startDate && b.endDate).forEach(b => {
      let total = 0;
      const dayCosts = {};
      getBreakDays(b).forEach(day => {
        const ds = day.toISOString().slice(0, 10);
        const rec = schedule[ds] || {};
        const d = rec.dayData || {};
        const allActs = [...(d.activities || []), ...(d.shows || [])];
        const dayTotal = allActs.reduce((s, a) => s + parseCost(a.cost), 0);
        dayCosts[ds] = dayTotal;
        total += dayTotal;
      });
      result[b.name] = { total, dayCosts, budget: parseCost(b.budget) };
    });
    return result;
  }, [breaks, schedule]);

  const grandTotal = Object.values(allCosts).reduce((s, b) => s + b.total, 0);
  const grandBudget = Object.values(allCosts).reduce((s, b) => s + (b.budget || 0), 0);

  if (!breaks.filter(b => b.name && b.startDate).length) return null;

  return (
    <div style={{ ...S.card, background: `linear-gradient(135deg, ${BRAND.navy}, ${BRAND.slate})`, color: COLORS.white }}>
      <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.7, marginBottom: 10, textTransform: "uppercase", letterSpacing: "0.5px" }}>Holiday Budget Overview</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 30, fontWeight: 800, lineHeight: 1 }}>£{grandTotal.toFixed(2)}</div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 3 }}>{grandBudget > 0 ? `of £${grandBudget.toFixed(2)} budget` : "total booked"}</div>
        </div>
        {grandBudget > 0 && (
          <div style={{ textAlign: "right" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: grandTotal > grandBudget ? COLORS.blush : COLORS.sageMid }}>
              {grandTotal > grandBudget ? `£${(grandTotal - grandBudget).toFixed(2)} over` : `£${(grandBudget - grandTotal).toFixed(2)} left`}
            </div>
          </div>
        )}
      </div>
      {grandBudget > 0 && (
        <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 14 }}>
          <div style={S.budgetBar(grandBudget ? (grandTotal / grandBudget) * 100 : 0)} />
        </div>
      )}
      {Object.entries(allCosts).map(([name, data]) => (
        <div key={name} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "7px 0", borderTop: "1px solid rgba(255,255,255,0.12)", fontSize: 13 }}>
          <span style={{ opacity: 0.85 }}>{name}</span>
          <div style={{ textAlign: "right" }}>
            <span style={{ fontWeight: 700 }}>£{data.total.toFixed(2)}</span>
            {data.budget > 0 && <span style={{ opacity: 0.6, fontSize: 11, marginLeft: 6 }}>/ £{data.budget.toFixed(2)}</span>}
          </div>
        </div>
      ))}
    </div>
  );
}

function ChildrenTab({ children, setChildren, helpers, setHelpers, showToast }) {
  const [editing, setEditing] = useState(null);
  const blank = () => ({ id: Date.now(), name: "", age: "", interests: [] });

  const save = (draft) => {
    if (!draft.name.trim()) return;
    const child = { ...draft, name: draft.name.trim() };   // no stray spaces ("Billy 's schedule")
    if (children.find(c => c.id === child.id)) {
      setChildren(children.map(c => c.id === child.id ? child : c));
    } else {
      setChildren([...children, child]);
    }
    setEditing(null);
    showToast(`${child.name}'s profile saved ✓`);
  };

  const remove = (id) => setChildren(children.filter(c => c.id !== id));

  return (
    <div>
      <div style={S.card}>
        <div style={S.sectionTitle}><Icon name="user" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Child Profiles</div>
        <p style={S.helpText16}>
          Add your children's ages and interests so activity searches are personalised for them.
        </p>
        {children.length === 0 && <div style={S.emptyState}><div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="user" size={26} color={BRAND.grayMid} /></div>No children added yet</div>}
        {children.map(child => (
          <div key={child.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
            <Avatar child={child} size={44} />
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.plum }}>{child.name}</div>
              <div style={{ fontSize: 12, color: COLORS.muted }}>Age {child.age}</div>
              <div style={{ display: "flex", flexWrap: "wrap", gap: 5, marginTop: 5 }}>
                {child.interests.map(id => {
                  const opt = INTEREST_OPTIONS.find(o => o.id === id);
                  return opt ? <span key={id} style={{ fontSize: 11, background: COLORS.blushLight, color: COLORS.plum, borderRadius: 10, padding: "2px 8px", fontWeight: 600 }}>{stripEmoji(opt.label)}</span> : null;
                })}
              </div>
            </div>
            <div style={{ display: "flex", gap: 6 }}>
              <button style={S.btnGhost} onClick={() => setEditing({ ...child })}>Edit</button>
              <button style={{ ...S.btnGhost, color: COLORS.blushDark, borderColor: COLORS.blush }} onClick={() => remove(child.id)}>✕</button>
            </div>
          </div>
        ))}
        <button style={{ ...S.btnPrimary, marginTop: 14 }} onClick={() => setEditing(blank())}>＋ Add a child</button>
      </div>

      {/* Edit / Add modal card */}
      {editing && <ChildForm child={editing} onSave={save} onCancel={() => setEditing(null)} />}

      {/* ── Carers & Helpers — always visible below child profiles ── */}
      {!editing && <CarersPanel helpers={helpers} setHelpers={setHelpers} showToast={showToast} />}
    </div>
  );
}

function ChildForm({ child, onSave, onCancel }) {
  const [form, setForm] = useState(child);
  const toggle = (id) => setForm(f => ({ ...f, interests: f.interests.includes(id) ? f.interests.filter(x => x !== id) : [...f.interests, id] }));

  return (
    <div style={{ ...S.card, border: `2px solid ${COLORS.plumMid}` }}>
      <div style={S.sectionTitle}>{child.name ? `Edit ${child.name}` : "New child"}</div>
      <div style={S.row}>
        <div style={{ flex: 2 }}>
          <label style={S.label}>Name</label>
          <input style={S.input} placeholder="e.g. Lily" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Age</label>
          <input style={S.input} type="number" min={1} max={17} placeholder="7" value={form.age} onChange={e => setForm({ ...form, age: e.target.value })} />
        </div>
      </div>
      <label style={S.label}>Interests (select all that apply)</label>
      <div style={{ display: "flex", flexWrap: "wrap", gap: 8, marginBottom: 16 }}>
        {INTEREST_OPTIONS.map(opt => (
          <button key={opt.id} style={S.chip(form.interests.includes(opt.id), opt.color + "33", COLORS.plum)} onClick={() => toggle(opt.id)}>
            {stripEmoji(opt.label)}
          </button>
        ))}
      </div>
      <div style={{ display: "flex", gap: 8 }}>
        <button style={S.btnPrimary} onClick={() => onSave(form)}>Save profile</button>
        <button style={S.btnGhost} onClick={onCancel}>Cancel</button>
      </div>
    </div>
  );
}


// ─── CarersPanel — lives inside Children tab ─────────────────────────────────
const RELATIONSHIP_OPTIONS = ["Mum","Dad","Grandparent","Childminder","Nanny","Friend","Neighbour","Partner","Au pair","Other"];

function CarersPanel({ helpers, setHelpers, showToast }) {
  const [showForm, setShowForm] = useState(false);
  const [draft, setDraft] = useState({ name: "", relationship: "", contact: "", notes: "" });

  const save = () => {
    if (!draft.name.trim()) return;
    setHelpers([...helpers, { ...draft, id: Date.now() }]);
    setDraft({ name: "", relationship: "", contact: "", notes: "" });
    setShowForm(false);
    showToast(`${draft.name} added as a carer ✓`);
  };

  const remove = (id) => setHelpers(helpers.filter(h => h.id !== id));

  return (
    <div style={S.card}>
      <div style={S.sectionTitle}><Icon name="users" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Carers & Helpers</div>
      <p style={S.helpText}>
        Add the people who help look after your children — mum, dad, grandparents, childminder. You can then assign them to days directly on the Schedule.
      </p>

      {helpers.length === 0 && !showForm && (
        <div style={S.emptyState}><div style={{ fontSize: 24, marginBottom: 6 }}><Icon name="users" size={26} color={BRAND.grayMid} /></div>No carers added yet</div>
      )}

      {helpers.map((h, i) => (
        <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "11px 0", borderBottom: `1px solid ${COLORS.border}` }}>
          <div style={{ width: 38, height: 38, borderRadius: "50%", background: CHILD_AVATAR_COLORS[i % CHILD_AVATAR_COLORS.length], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: BRAND.white, flexShrink: 0 }}>
            {h.name[0].toUpperCase()}
          </div>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.plum }}>{h.name}</div>
            <div style={{ fontSize: 12, color: COLORS.muted }}>{h.relationship}{h.contact && ` · ${h.contact}`}</div>
            {h.notes && <div style={{ fontSize: 11, color: COLORS.muted, fontStyle: "italic" }}>{h.notes}</div>}
          </div>
          <button style={{ ...S.btnGhost, color: COLORS.blushDark, borderColor: COLORS.blush, padding: "6px 10px" }} onClick={() => remove(h.id)}>✕</button>
        </div>
      ))}

      {showForm && (
        <div style={{ marginTop: 14, padding: 14, background: COLORS.blushLight, borderRadius: 12 }}>
          <div style={S.row}>
            <div style={{ flex: 2 }}>
              <label style={S.label}>Name</label>
              <input style={S.input} placeholder="e.g. Grandma Sue" value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
            </div>
            <div style={S.col}>
              <label style={S.label}>Role</label>
              <select style={S.input} value={draft.relationship} onChange={e => setDraft({ ...draft, relationship: e.target.value })}>
                <option value="">Select…</option>
                {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
          <div style={{ marginBottom: 10 }}>
            <label style={S.label}>Contact (phone or email)</label>
            <input style={S.input} placeholder="07700 900000 or email@example.com" value={draft.contact} onChange={e => setDraft({ ...draft, contact: e.target.value })} />
          </div>
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Notes</label>
            <input style={S.input} placeholder="e.g. Only available mornings" value={draft.notes} onChange={e => setDraft({ ...draft, notes: e.target.value })} />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <button style={S.btnPrimary} onClick={save}>Save carer</button>
            <button style={S.btnGhost} onClick={() => setShowForm(false)}>Cancel</button>
          </div>
        </div>
      )}

      {!showForm && (
        <button style={{ ...S.btnPrimary, marginTop: 14 }} onClick={() => setShowForm(true)}>＋ Add a carer</button>
      )}
    </div>
  );
}

function BreaksTab({ breaks, setBreaks, terms, setTerms }) {
  const add = () => setBreaks([...breaks, { id: Date.now(), name: "", startDate: "", endDate: "", budget: "" }]);
  const update = (id, field, val) => setBreaks(breaks.map(b => b.id === id ? { ...b, [field]: val } : b));
  const remove = (id) => setBreaks(breaks.filter(b => b.id !== id));

  const addTerm = () => setTerms([...(terms || []), { id: Date.now(), name: "", startDate: "", endDate: "", halfTermStart: "", halfTermEnd: "" }]);
  const updateTerm = (id, field, val) => setTerms((terms || []).map(t => t.id === id ? { ...t, [field]: val } : t));
  const removeTerm = (id) => setTerms((terms || []).filter(t => t.id !== id));

  return (
    <div>
      {/* ── School terms (for weekly clubs) ── */}
      <div style={S.card}>
        <div style={S.sectionTitle}><Icon name="calendar" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />School Terms</div>
        <p style={S.helpText16}>
          Add your school term dates so you can enrol in weekly after-school and weekend clubs.
          Adding the half-term week means we won't book a class on a week the club isn't running.
        </p>
        {(!terms || terms.length === 0) && <div style={S.emptyState}><div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="calendar" size={26} color={BRAND.grayMid} /></div>No terms added yet</div>}
        {(terms || []).map(t => (
          <div key={t.id} style={{ ...S.card, background: COLORS.sageLight, marginBottom: 12, padding: 14 }}>
            <div style={{ ...S.row, marginBottom: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={S.label}>Term name</label>
                <input aria-label="Term name" style={S.input} placeholder="e.g. Autumn Term 2026" value={t.name} onChange={e => updateTerm(t.id, "name", e.target.value)} />
              </div>
              <button onClick={() => removeTerm(t.id)} style={{ ...S.btnGhost, alignSelf: "flex-end", color: COLORS.blushDark, borderColor: COLORS.blush, padding: "9px 12px" }}>✕</button>
            </div>
            <div style={{ ...S.row, marginBottom: 10 }}>
              <div style={S.col}>
                <label style={S.label}>Term starts</label>
                <input aria-label="Term start date" type="date" style={S.input} value={t.startDate} onChange={e => updateTerm(t.id, "startDate", e.target.value)} />
              </div>
              <div style={S.col}>
                <label style={S.label}>Term ends</label>
                <input aria-label="Term end date" type="date" style={S.input} value={t.endDate} onChange={e => updateTerm(t.id, "endDate", e.target.value)} />
              </div>
            </div>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.muted, marginBottom: 6, textTransform: "uppercase", letterSpacing: "0.4px" }}>
              Half-term break (optional, but recommended)
            </div>
            <div style={S.row}>
              <div style={S.col}>
                <label style={S.label}>Half-term starts</label>
                <input aria-label="Half-term start date" type="date" style={S.input} value={t.halfTermStart || ""} onChange={e => updateTerm(t.id, "halfTermStart", e.target.value)} />
              </div>
              <div style={S.col}>
                <label style={S.label}>Half-term ends</label>
                <input aria-label="Half-term end date" type="date" style={S.input} value={t.halfTermEnd || ""} onChange={e => updateTerm(t.id, "halfTermEnd", e.target.value)} />
              </div>
            </div>
            {t.startDate && t.endDate && (
              <div style={{ marginTop: 10, fontSize: 12, color: COLORS.muted }}>
                {(() => {
                  const wks = getTermSessions(t, 1).length;
                  return `A Monday club in this term would run ${wks} time${wks === 1 ? "" : "s"}${t.halfTermStart && t.halfTermEnd ? " (half-term excluded)" : ""}.`;
                })()}
              </div>
            )}
            {/* Catch dates entered the wrong way round or half-term outside the term */}
            {(() => {
              const msgs = [];
              if (t.startDate && t.endDate && t.endDate < t.startDate) msgs.push("The term ends before it starts — check the dates.");
              if (t.halfTermStart && t.halfTermEnd && t.halfTermEnd < t.halfTermStart) msgs.push("Half-term ends before it starts.");
              if (t.startDate && t.endDate && t.halfTermStart && (t.halfTermStart < t.startDate || t.halfTermStart > t.endDate))
                msgs.push("Half-term falls outside this term's dates — is one of them in the wrong term?");
              return msgs.length ? <div role="alert" style={{ marginTop: 8, fontSize: 12, color: COLORS.red, fontWeight: 600 }}>{msgs.join(" ")}</div> : null;
            })()}
          </div>
        ))}
        <button onClick={addTerm} style={S.btnPrimary}>＋ Add a school term</button>
      </div>

      <div style={S.card}>
        <div style={S.sectionTitle}><Icon name="calendar" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />School Breaks</div>
        <p style={S.helpText16}>Add your school holiday dates.</p>
        {breaks.length === 0 && <div style={S.emptyState}><div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="calendar" size={26} color={BRAND.grayMid} /></div>No breaks added yet</div>}
        {breaks.map(b => (
          <div key={b.id} style={{ ...S.card, background: COLORS.blushLight, marginBottom: 12, padding: 14 }}>
            <div style={{ ...S.row, marginBottom: 10 }}>
              <div style={{ flex: 2 }}>
                <label style={S.label}>Break name</label>
                <input aria-label="Holiday name" style={S.input} placeholder="e.g. Summer Holidays" value={b.name} onChange={e => update(b.id, "name", e.target.value)} />
              </div>
              <button onClick={() => remove(b.id)} style={{ ...S.btnGhost, alignSelf: "flex-end", color: COLORS.blushDark, borderColor: COLORS.blush, padding: "9px 12px" }}>✕</button>
            </div>
            <div style={S.row}>
              <div style={S.col}>
                <label style={S.label}>Start date</label>
                <input aria-label="Holiday start date" type="date" style={S.input} value={b.startDate} onChange={e => update(b.id, "startDate", e.target.value)} />
              </div>
              <div style={S.col}>
                <label style={S.label}>End date</label>
                <input aria-label="Holiday end date" type="date" style={S.input} value={b.endDate} onChange={e => update(b.id, "endDate", e.target.value)} />
              </div>
            </div>
          </div>
        ))}
        <button onClick={add} style={S.btnPrimary}>＋ Add a school break</button>
      </div>
      {breaks.filter(b => b.name && b.startDate && b.endDate).length > 0 && (
        <div style={S.card}>
          <div style={S.sectionTitle}><Icon name="calendar" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Your Holiday Calendar</div>
          {breaks.filter(b => b.name && b.startDate && b.endDate).map(b => {
            const s = new Date(b.startDate), e = new Date(b.endDate);
            const days = Math.round((e - s) / 86400000) + 1;
            return (
              <div key={b.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                <div>
                  <div style={{ fontWeight: 700, color: COLORS.plum, fontSize: 14 }}>{b.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{s.toLocaleDateString("en-GB")} – {e.toLocaleDateString("en-GB")}</div>
                </div>
                <div style={{ textAlign: "right" }}>
                  <span style={S.badge("blush")}>{days} days</span>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

// Derive which dataset to search from the entered postcode.
// MumHQ currently covers North London and South West London only.
function regionFromPostcode(info) {
  if (!info) return "north";
  const oc = (info.postcode || "").replace(/\s+/g, "").toUpperCase();
  const area = oc.match(/^[A-Z]{1,2}/)?.[0] || "";
  const swAreas = ["SW", "TW", "KT", "CR", "SM"];
  if (swAreas.includes(area)) return "southwest";
  if (/^W[46]/.test(oc)) return "southwest"; // Chiswick / Hammersmith
  return "north";
}

function SearchTab({ children, breaks, terms, schedule, setSchedule, favourites, setFavourites, reviews, setReviews, showToast, user, onOpenBreaks }) {
  const [query, setQuery] = useState("");
  const [selectedBreak, setSelectedBreak] = useState("");
  const [selectedChild, setSelectedChild] = useState("all");
  const [selectedNeighbourhood, setSelectedNeighbourhood] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [aiNote, setAiNote] = useState("");
  const [activityMode, setActivityMode] = useState("camps"); // "camps" | "termtime"
  const [addingTo, setAddingTo] = useState(null);
  // ── Term-time enrolment state ──
  const [enrolTerm, setEnrolTerm] = useState("");
  const [enrolWeekday, setEnrolWeekday] = useState("");
  const [enrolStart, setEnrolStart] = useState("");
  const [enrolEnd, setEnrolEnd] = useState("");
  const [enrolFee, setEnrolFee] = useState("");
  const [reviewingAct, setReviewingAct] = useState(null); // camp being reviewed
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewBody, setReviewBody] = useState("");
  const [addDates, setAddDates] = useState([]); // array of selected date strings
  const [addChild, setAddChild] = useState("");
  const [addNotes, setAddNotes] = useState("");
  const [showFavourites, setShowFavourites] = useState(false);

  // Location state
  const [postcode, setPostcode] = useState("");
  const [postcodeInput, setPostcodeInput] = useState("");
  const [postcodeInfo, setPostcodeInfo] = useState(null); // {lat,lon,district,postcode}
  const [postcodeError, setPostcodeError] = useState("");
  const [postcodeLoading, setPostcodeLoading] = useState(false);
  const [maxDistance, setMaxDistance] = useState(999); // miles; 999 = any

  // Holiday periods a camp can be booked into — includes half-terms entered under
  // School Terms (before, those were invisible here: dead end on first add).
  const validBreaks = useMemo(() => holidayPeriods(breaks, terms), [breaks, terms]);
  const child = children.find(c => String(c.id) === selectedChild);

  // Region is now inferred from the entered postcode (no manual toggle).
  const region = useMemo(() => regionFromPostcode(postcodeInfo), [postcodeInfo]);

  // ── Data source: pick the right dataset based on region + activity mode ────
  const sourceCamps = useMemo(() => {
    if (region === "southwest") {
      return activityMode === "termtime" ? SOUTH_WEST_LONDON_TERM_TIME_ACTIVITIES : SOUTH_WEST_LONDON_CAMPS;
    }
    return activityMode === "termtime" ? TERM_TIME_ACTIVITIES : NORTH_LONDON_CAMPS;
  }, [region, activityMode]);

  const sourceNeighbourhoods = useMemo(() => {
    if (region === "southwest") {
      return activityMode === "termtime" ? SOUTH_WEST_LONDON_TERM_TIME_NEIGHBOURHOODS : SOUTH_WEST_LONDON_NEIGHBOURHOODS;
    }
    return activityMode === "termtime" ? TERM_TIME_NEIGHBOURHOODS : NORTH_LONDON_NEIGHBOURHOODS;
  }, [region, activityMode]);

  // Attach a REAL distance to each result, computed from the user's postcode.
  // `_dist` is { miles, precise } — or null when we can't place the venue.
  const resultsWithDistance = useMemo(() => {
    if (!postcodeInfo) return results.map(a => ({ ...a, _dist: null }));
    return results
      .map(a => ({ ...a, _dist: distanceTo(postcodeInfo, a) }))
      // Nearest first — the whole point of entering a postcode.
      .sort((a, b) => (a._dist?.miles ?? Infinity) - (b._dist?.miles ?? Infinity));
  }, [results, postcodeInfo]);

  // Friendly area name for the entered postcode. postcodes.io gives a council ward
  // ("Hillside" for SW19 5AE) that parents don't recognise, so use the neighbourhood
  // of the nearest listing in our own data instead ("Wimbledon").
  const nearestArea = useMemo(() => {
    if (!postcodeInfo) return "";
    let best = null;
    for (const a of sourceCamps) {
      const d = distanceTo(postcodeInfo, a);
      if (d && a.neighbourhood && (!best || d.miles < best.miles)) best = { miles: d.miles, area: a.neighbourhood };
    }
    return best && best.miles <= 1.5 ? best.area : "";
  }, [postcodeInfo, sourceCamps]);

  const visibleResults = useMemo(() => {
    if (!postcodeInfo || maxDistance === 999) return resultsWithDistance;
    // Fail open: if we couldn't locate a venue, show it rather than silently
    // dropping it. Better an unlabelled card than a missing one.
    return resultsWithDistance.filter(a => a._dist == null || a._dist.miles <= maxDistance);
  }, [resultsWithDistance, postcodeInfo, maxDistance]);

  const favouritesList = Object.values(favourites);
  const displayResults = showFavourites ? favouritesList : visibleResults;

  // Show results in pages of 20: rendering all 280–400 cards at once made phones
  // stutter and briefly show a blank screen while scrolling.
  const PAGE_SIZE = 20;
  const [shownCount, setShownCount] = useState(PAGE_SIZE);
  useEffect(() => { setShownCount(PAGE_SIZE); }, [results, maxDistance, showFavourites]);
  const pagedResults = displayResults.slice(0, shownCount);

  // ── Review helpers ──────────────────────────────────────────────────────────
  // Parents can hide reviews from a particular person (Apple requires a way to
  // block abusive content). Stored on this device only.
  const [hiddenReviewers, setHiddenReviewers] = useState(() => {
    try { return JSON.parse(localStorage.getItem("mumhq_hidden_reviewers") || "[]"); } catch { return []; }
  });
  const hideReviewer = (uid) => {
    const next = [...new Set([...hiddenReviewers, uid])];
    setHiddenReviewers(next);
    try { localStorage.setItem("mumhq_hidden_reviewers", JSON.stringify(next)); } catch {}
    showToast("Reviews from this person are hidden");
  };
  const reportReview = (act, r) => {
    const subject = encodeURIComponent(`Report a review – ${act.name}`);
    const body = encodeURIComponent(
      `I'd like to report this review in MumHQ.\n\nActivity: ${act.name} (${act.id})\nReview ID: ${r.id ?? "n/a"}\nDate: ${r.date}\nText: "${(r.body || "").slice(0, 500)}"\n\nWhy I'm reporting it:\n`
    );
    window.location.href = `mailto:${CONTACT_EMAIL}?subject=${subject}&body=${body}`;
  };
  const getReviews = (actId) => (reviews[actId] || []).filter(r => !r.userId || r.userId === user?.id || !hiddenReviewers.includes(r.userId));
  const avgRating = (actId) => {
    const rs = getReviews(actId);
    if (!rs.length) return null;
    return (rs.reduce((s, r) => s + r.rating, 0) / rs.length).toFixed(1);
  };
  const submitReview = async (act) => {
    if (!reviewRating || !user) return;
    const key = act.id || act.name;
    const { data, error } = await supabase
      .from("camp_reviews")
      .insert({
        camp_id: key,
        user_id: user.id,
        // Reviews are shared with other parents, so never store any part of the email.
        author_name: "MumHQ parent",
        rating: reviewRating,
        body: reviewBody.trim(),
      })
      .select()
      .single();

    if (error) {
      showToast("Couldn't save review — try again");
      return;
    }

    const newReview = { id: data.id, userId: user.id, rating: data.rating, body: data.body, date: new Date(data.created_at).toLocaleDateString("en-GB") };
    setReviews(prev => ({ ...prev, [key]: [...(prev[key] || []), newReview] }));
    setReviewingAct(null); setReviewRating(0); setReviewBody("");
    showToast("Review saved");
  };

  const toggleFavourite = (act) => {
    const key = act.id ?? act.name;
    if (favourites[key]) {
      const next = { ...favourites };
      delete next[key];
      setFavourites(next);
    } else {
      setFavourites({ ...favourites, [key]: act });
    }
  };
  const isFavourite = (act) => !!favourites[act.id ?? act.name];

  const handlePostcodeLookup = async () => {
    if (!postcodeInput.trim()) return;
    setPostcodeLoading(true); setPostcodeError("");
    try {
      const info = await lookupPostcode(postcodeInput.trim());
      setPostcodeInfo(info);
      setPostcode(info.postcode);
      showToast(`📍 Location set to ${info.postcode}`);
    } catch (err) {
      setPostcodeError(
        err.message ||
        "Couldn't look that postcode up — check your connection and try again."
      );
      setPostcodeInfo(null);
    }
    setPostcodeLoading(false);
  };

  const clearPostcode = () => { setPostcodeInfo(null); setPostcode(""); setPostcodeInput(""); setPostcodeError(""); setMaxDistance(999); };

  const buildPrompt = () => {
    let prompt = `Find holiday activity suggestions`;
    if (child) {
      prompt += ` for a ${child.age}-year-old`;
      if (child.interests.length) {
        const labels = child.interests.map(id => stripEmoji(INTEREST_OPTIONS.find(o => o.id === id)?.label || "")).filter(Boolean);
        prompt += ` who loves ${labels.join(", ")}`;
      }
    }
    if (query.trim()) prompt += `. Search focus: "${query}"`;
    if (selectedBreak) prompt += `. Holiday period: ${selectedBreak}`;
    if (postcodeInfo) prompt += `. Located near ${postcodeInfo.district} (${postcodeInfo.postcode}). Include realistic UK area names and street-level venue names close to this area. The location field of each result must end with a real full UK postcode for that venue — do not estimate distances, we calculate those ourselves.`;
    return prompt;
  };

  const search = async () => {
    setLoading(true); setResults([]); setAiNote("");

    // ── Step 1: Filter the local database (region + activity mode aware) ────
    // Listings marked `active: false` in data.js are no longer running — hide them.
    let localResults = sourceCamps.filter(c => c.active !== false);

    // Filter by neighbourhood
    if (selectedNeighbourhood) {
      localResults = localResults.filter(c =>
        c.neighbourhoods.includes(selectedNeighbourhood) || c.neighbourhood === selectedNeighbourhood
      );
    }

    // Filter by child age
    if (child?.age) {
      localResults = localResults.filter(c => {
        const ageRange = c.age.match(/(\d+)[–-](\d+)/);
        if (!ageRange) return true;
        const childAge = parseInt(child.age);
        return childAge >= parseInt(ageRange[1]) && childAge <= parseInt(ageRange[2]);
      });
    }

    // Filter by interests (child profile)
    if (child?.interests.length) {
      const withMatch = localResults.filter(c => c.interests.some(i => child.interests.includes(i)));
      if (withMatch.length) localResults = withMatch;
    }

    // Filter by text query
    if (query.trim()) {
      const q = query.toLowerCase();
      const withQuery = localResults.filter(c =>
        c.name.toLowerCase().includes(q) ||
        c.description.toLowerCase().includes(q) ||
        c.type.toLowerCase().includes(q) ||
        c.interests.some(i => i.includes(q)) ||
        c.neighbourhood.toLowerCase().includes(q) ||
        c.provider.toLowerCase().includes(q)
      );
      if (withQuery.length) localResults = withQuery;
    }

    // Filter by distance if postcode set — computed, not the old static field.
    if (postcodeInfo && maxDistance !== 999) {
      localResults = localResults.filter(c => {
        const d = distanceTo(postcodeInfo, c);
        return d == null || d.miles <= maxDistance;   // fail open on unknown venues
      });
    }

    if (localResults.length > 0) {
      setResults(localResults);
      const note = [
        `${localResults.length} ${activityMode === "termtime" ? "clubs" : "camps"} found`,
        selectedNeighbourhood && `in ${selectedNeighbourhood}`,
        child && `for ${child.name}`,
        postcodeInfo && `near ${postcodeInfo.postcode}`,
      ].filter(Boolean).join(" · ");
      setAiNote(note);
      setLoading(false);
      return;
    }

    // ── Step 2: No local results ────────────────────────────────────────────
    // The client-side AI fallback was removed so the Anthropic key never ships in
    // the browser bundle. (A server-side search Edge Function can restore AI
    // suggestions later if wanted.) Local DB covers the vast majority of queries.
    setResults([]);
    setAiNote("No camps matched your filters — try widening your search");
    setLoading(false);
  };

  const getDays = () => {
    const b = validBreaks.find(b => b.name === selectedBreak) || validBreaks[0];
    if (!b) return [];
    return getBreakDays(b);
  };

  const doAdd = (act) => {
    if (!addDates.length) return;
    const childObj = children.find(c => String(c.id) === addChild);
    const newSchedule = { ...schedule };
    addDates.forEach(ds => {
      const existing = newSchedule[ds] || {};
      const existingData = existing.dayData || {};
      const existingActivities = existingData.activities || [];
      const newActivity = {
        id: Date.now() + Math.random(),
        name: act.name,
        location: act.location || "",
        time: "",
        cost: act.cost || "",
        notes: addNotes,
        childName: childObj?.name || "",
      };
      newSchedule[ds] = {
        ...existing,
        dayType: existing.dayType || "activity",
        dayData: { ...existingData, activities: [...existingActivities, newActivity] },
      };
    });
    setSchedule(newSchedule);
    setAddingTo(null); setAddDates([]); setAddChild(""); setAddNotes("");
    showToast(`"${act.name}" added to ${addDates.length} day${addDates.length > 1 ? "s" : ""} ✓`);
  };

  // ── Term-time enrolment ────────────────────────────────────────────────────
  const termList = validTerms(terms);
  const activeTerm = termList.find(t => String(t.id) === String(enrolTerm)) || null;
  const enrolSessions = useMemo(
    () => (activeTerm && enrolWeekday !== "" ? getTermSessions(activeTerm, Number(enrolWeekday)) : []),
    [activeTerm, enrolWeekday]
  );

  const resetEnrol = () => {
    setAddingTo(null); setEnrolTerm(""); setEnrolWeekday("");
    setEnrolStart(""); setEnrolEnd(""); setEnrolFee(""); setAddChild(""); setAddNotes("");
  };

  const doEnrol = (act) => {
    if (!enrolSessions.length || !enrolStart) return;
    const childObj = children.find(c => String(c.id) === addChild);
    const seriesId = `s${Date.now().toString(36)}`;
    const dayLabel = CLASS_DAYS.find(w => w.id === Number(enrolWeekday))?.label || "";
    const newSchedule = { ...schedule };

    enrolSessions.forEach((ds, i) => {
      const existing = newSchedule[ds] || {};
      const existingData = existing.dayData || {};
      const existingActivities = existingData.activities || [];

      const session = {
        id: `${seriesId}-${i}`,
        seriesId,
        seriesName: `${act.name} — ${dayLabel}s, ${activeTerm.name}`,
        name: act.name,
        location: act.location || "",
        time: enrolStart,
        endTime: enrolEnd || "",
        // The whole-term fee is charged ONCE, against the first session. If we
        // put it on every session the budget would report 12× the real cost.
        cost: i === 0 ? (enrolFee || "") : "",
        notes: i === 0
          ? addNotes
          : [addNotes, "Included in term fee"].filter(Boolean).join(" · "),
        childName: childObj?.name || "",
      };

      newSchedule[ds] = {
        ...existing,
        dayType: existing.dayType || "activity",
        dayData: { ...existingData, activities: [...existingActivities, session] },
      };
    });

    setSchedule(newSchedule);
    resetEnrol();
    showToast(`Enrolled — ${enrolSessions.length} ${dayLabel} sessions added ✓`);
  };

  const matchScore = (act) => {
    if (!child?.interests.length) return 0;
    return (act.interests || []).filter(i => child.interests.includes(i)).length;
  };

  const filteredCount = results.length - visibleResults.length;

  return (
    <div>
      {/* ── Location card ── */}
      <div style={S.card}>
        <div style={S.sectionTitle}><Icon name="mapPin" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Your Location</div>
        {!postcodeInfo ? (
          <>
            <p style={{ fontSize: 13, color: COLORS.muted, marginBottom: 12, lineHeight: 1.5 }}>
              Enter your <strong>full postcode</strong> for accurate distances — e.g. <strong>N16 8EA</strong>. A partial code like <strong>N16</strong> works too, but distances will only be approximate.
            </p>
            <div style={{ display: "flex", gap: 8, marginBottom: 6 }}>
              <input
                style={{ ...S.input, flex: 1, textTransform: "uppercase", letterSpacing: "1px" }}
                placeholder="e.g. N16 8EA"
                value={postcodeInput}
                onChange={e => { setPostcodeInput(e.target.value.toUpperCase()); setPostcodeError(""); }}
                onKeyDown={e => e.key === "Enter" && handlePostcodeLookup()}
                inputMode="text"
                autoComplete="postal-code"
                maxLength={9}
              />
              <button style={S.btnPrimary} onClick={handlePostcodeLookup} disabled={postcodeLoading}>
                {postcodeLoading ? "…" : "Set"}
              </button>
            </div>
            {postcodeError && <p style={{ fontSize: 12, color: COLORS.red, margin: 0 }}>{postcodeError}</p>}
          </>
        ) : (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 15, color: COLORS.plum }}>📍 {postcodeInfo.postcode}</div>
              <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                {nearestArea || postcodeInfo.district}
                {postcodeInfo.precise === false && " · area only — add your full postcode for exact distances"}
              </div>
            </div>
            <button style={{ ...S.btnGhost, fontSize: 11 }} onClick={clearPostcode}>Change</button>
          </div>
        )}
      </div>

      {/* ── Distance filter (only when postcode set) ── */}
      {postcodeInfo && results.length > 0 && (
        <div style={S.card}>
          <div style={{ ...S.sectionTitle, marginBottom: 4 }}><Icon name="map" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Distance from {postcodeInfo.postcode}</div>
          <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 10, lineHeight: 1.4 }}>
            Straight-line distance. Actual travel time will be longer — a “~” means the venue only publishes a postcode area.
          </div>
          <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
            {DISTANCE_OPTIONS.map(opt => (
              <button
                key={opt.miles}
                style={{
                  ...S.chip(maxDistance === opt.miles),
                  padding: "8px 14px",
                  fontSize: 13,
                }}
                onClick={() => setMaxDistance(opt.miles)}
              >
                {opt.label}
              </button>
            ))}
          </div>
          {maxDistance !== 999 && (
            <div style={{ marginTop: 10, fontSize: 12, color: COLORS.muted }}>
              Showing {visibleResults.length} of {results.length} results within {DISTANCE_OPTIONS.find(o => o.miles === maxDistance)?.label}
              {filteredCount > 0 && <span style={{ color: COLORS.blushDark }}> · {filteredCount} hidden by distance filter</span>}
            </div>
          )}
        </div>
      )}

      {/* ── Search filters ── */}
      <div style={S.card}>
        <div style={S.sectionTitle}>Find Activities</div>

        {/* ── Activity type toggle ── */}
        <div style={{ display: "flex", gap: 8, marginBottom: 16 }}>
          {[{ id: "camps", label: "Holiday Camps" }, { id: "termtime", label: "After School & Weekend" }].map(opt => (
            <button key={opt.id} onClick={() => { setActivityMode(opt.id); setSelectedNeighbourhood(""); setResults([]); }}
              style={{ flex: 1, padding: "10px", borderRadius: 10, border: `2px solid ${activityMode === opt.id ? BRAND.slate : BRAND.border}`, background: activityMode === opt.id ? BRAND.sagePale : BRAND.white, color: activityMode === opt.id ? BRAND.navy : BRAND.muted, fontSize: 13, fontWeight: 700, cursor: "pointer" }}>
              {opt.label}
            </button>
          ))}
        </div>

        {children.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Searching for</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={S.chip(selectedChild === "all")} onClick={() => setSelectedChild("all")}>All children</button>
              {children.map(c => (
                <button key={c.id} style={S.chip(selectedChild === String(c.id))} onClick={() => setSelectedChild(String(c.id))}>
                  <span style={{ marginRight: 4 }}>{c.name}</span><span style={{ opacity: 0.6, fontSize: 11 }}>age {c.age}</span>
                </button>
              ))}
            </div>
          </div>
        )}
        {child?.interests.length > 0 && (
          <div style={{ marginBottom: 12, padding: "8px 12px", background: COLORS.blushLight, borderRadius: 10, fontSize: 12, color: COLORS.plumMid }}>
            ✨ Personalising for {child.name}: {child.interests.map(id => stripEmoji(INTEREST_OPTIONS.find(o => o.id === id)?.label || "")).filter(Boolean).join(", ")}
          </div>
        )}
        {validBreaks.length > 0 && (
          <div style={{ marginBottom: 12 }}>
            <label style={S.label}>Holiday period</label>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <button style={S.chip(!selectedBreak)} onClick={() => setSelectedBreak("")}>Any</button>
              {validBreaks.map(b => <button key={b.id} style={S.chip(selectedBreak === b.name)} onClick={() => setSelectedBreak(b.name)}>{b.name}</button>)}
            </div>
          </div>
        )}
        <div style={{ marginBottom: 6 }}>
          <button style={{ ...S.btnPrimary, width: "100%", justifyContent: "center" }} onClick={search} disabled={loading}>{loading ? "…" : "Search"}</button>
        </div>
        {favouritesList.length > 0 && (
          <button
            onClick={() => setShowFavourites(v => !v)}
            style={{
              width: "100%", justifyContent: "center", marginBottom: 6,
              display: "flex", alignItems: "center", gap: 6, padding: "10px 14px",
              borderRadius: 10, cursor: "pointer", fontSize: 13, fontWeight: 700,
              border: `2px solid ${showFavourites ? COLORS.plumMid : COLORS.border}`,
              background: showFavourites ? COLORS.blushLight : COLORS.white,
              color: showFavourites ? COLORS.plum : COLORS.muted,
            }}>
            {showFavourites ? "← Back to search" : `♥ Saved (${favouritesList.length})`}
          </button>
        )}
        <p style={{ fontSize: 11, color: COLORS.muted }}>Always confirm dates & availability with providers</p>
      </div>

      {loading && (
        <div style={{ ...S.card, textAlign: "center", color: COLORS.muted, padding: 30 }}>
          <div style={{ fontSize: 24, marginBottom: 8 }}><Icon name="search" size={26} color={BRAND.grayMid} /></div>
          Finding activities{postcodeInfo ? ` near ${postcodeInfo.postcode}` : ""}…
        </div>
      )}
      {aiNote && !loading && <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 10, paddingLeft: 4 }}>{aiNote}</div>}

      {/* ── Results ── */}
      {showFavourites && (
        <div style={{ ...S.card, padding: "12px 16px", marginBottom: 12, background: COLORS.blushLight, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
          <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.plum }}>♥ Your saved {favouritesList.length === 1 ? "activity" : "activities"} ({favouritesList.length})</div>
          <button onClick={() => setShowFavourites(false)} style={{ ...S.btnGhost, fontSize: 12, padding: "6px 12px" }}>← Search</button>
        </div>
      )}
      {pagedResults.map(act => {
        const score = matchScore(act);
        const dist = act._dist;                       // { miles, precise } | null
        const dStyle = dist ? distanceBadgeStyle(dist.miles, COLORS) : null;
        const live = ACTIVITY_BY_ID[act.id] || act;   // current record (favourites may be stale copies)
        const href = websiteHref(live.website);
        const noLongerRunning = live.active === false;
        return (
          <div key={act.id} style={{ ...S.card, border: score >= 2 ? `2px solid ${COLORS.sageMid}` : `1px solid ${COLORS.border}`, padding: 16 }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 10 }}>
              <div style={{ flex: 1 }}>
                <div style={{ display: "flex", alignItems: "center", gap: 7, flexWrap: "wrap", marginBottom: 5 }}>
                  {href ? (
                    <a href={href} target="_blank" rel="noopener noreferrer" title={`Open the ${act.name} website`}
                      style={{ fontWeight: 700, fontSize: 14, color: COLORS.ink, textDecoration: "underline", textDecorationColor: BRAND.slate, textDecorationThickness: 1.5, textUnderlineOffset: 3 }}>
                      {act.name}
                      <Icon name="external" size={13} color={BRAND.slate} style={{ marginLeft: 4 }} />
                    </a>
                  ) : (
                    <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.ink }}>{act.name}</span>
                  )}
                  {noLongerRunning && <span style={{ fontSize: 11, fontWeight: 700, color: COLORS.red, background: "#F6E9E8", borderRadius: 20, padding: "2px 9px" }}>No longer running</span>}
                  <span style={S.badge(act.type === "Camp" ? "blush" : act.type === "Club" ? "sage" : "yellow")}>{act.type}</span>
                  {score >= 2 && <span style={{ fontSize: 11, color: COLORS.sage, fontWeight: 700 }}>★ Great match</span>}
                </div>
                <div style={{ fontSize: 12, color: COLORS.muted, marginBottom: 7 }}>{act.description}</div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", fontSize: 12, marginBottom: 4 }}>
                  <span style={{ color: COLORS.muted }}>📍 {act.location || act.neighbourhood}</span>
                  <span style={{ fontWeight: 700, color: COLORS.plumMid }}>💷 {act.cost}</span>
                  <span style={{ color: COLORS.muted }}>Ages {act.age}</span>
                  {(act.neighbourhood || act.postcodeSector) && (
                    <span style={{ fontSize: 11, fontWeight: 700, background: COLORS.blushLight, color: COLORS.plum, borderRadius: 20, padding: "2px 9px" }}>
                      {act.neighbourhood}
                    </span>
                  )}
                  {postcodeInfo && dist && (
                    <span
                      title={dist.precise
                        ? `Straight-line distance from ${postcodeInfo.postcode}`
                        : `Approximate — this venue only lists an outward code, so we've measured to the centre of its postcode area`}
                      style={{ display: "inline-flex", alignItems: "center", gap: 4, padding: "2px 9px", borderRadius: 20, fontSize: 11, fontWeight: 700, background: dStyle.bg, color: dStyle.color, border: `1px solid ${dStyle.border}` }}
                    >
                      📍 {distanceLabel(dist)}
                    </span>
                  )}
                </div>
                {act.dayOfWeek && <div style={{ fontSize: 11, color: COLORS.muted }}>📅 {act.dayOfWeek}</div>}
                {act.times && <div style={{ fontSize: 11, color: COLORS.muted }}>🕐 {act.times}</div>}
              </div>
              <div style={{ display: "flex", flexDirection: "column", gap: 8, alignItems: "flex-end" }}>
                <button
                  onClick={() => toggleFavourite(act)}
                  title={isFavourite(act) ? "Remove from favourites" : "Save to favourites"} aria-label={isFavourite(act) ? `Remove ${act.name} from saved` : `Save ${act.name}`}
                  style={{ background: "none", border: `1.5px solid ${isFavourite(act) ? "#C0392B" : BRAND.border}`, borderRadius: 20, padding: "5px 10px", cursor: "pointer", fontSize: 16, color: isFavourite(act) ? "#C0392B" : BRAND.muted, transition: "all 0.15s", lineHeight: 1 }}>
                  <Icon name="heart" size={18} color={isFavourite(act) ? "#B4524E" : BRAND.grayMid} style={isFavourite(act) ? { fill: "#B4524E" } : {}} />
                </button>
                <button style={S.btnSecondary} onClick={() => setAddingTo(act)}>+ Add</button>
              </div>
            </div>

            {/* ── Term-time enrolment panel ── */}
            {addingTo?.id === act.id && activityMode === "termtime" && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
                {termList.length === 0 ? (
                  <p style={{ fontSize: 12, color: COLORS.blushDark, marginBottom: 10 }}>
                    Add your school term dates first (Breaks &amp; Terms → Add a school term), then you can enrol in weekly classes.
                  </p>
                ) : (
                  <>
                    {act.dayOfWeek && (
                      <div style={{ fontSize: 11, color: COLORS.muted, marginBottom: 10, padding: "8px 10px", background: COLORS.cream, borderRadius: 8 }}>
                        Provider says: <strong>{act.dayOfWeek}</strong> — confirm the exact day and time with them before enrolling.
                      </div>
                    )}

                    <div style={{ ...S.row, marginBottom: 10 }}>
                      <div style={S.col}>
                        <label style={S.label}>Term</label>
                        <select style={S.input} value={enrolTerm} onChange={e => setEnrolTerm(e.target.value)}>
                          <option value="">Choose a term…</option>
                          {termList.map(t => <option key={t.id} value={t.id}>{t.name}</option>)}
                        </select>
                      </div>
                      <div style={S.col}>
                        <label style={S.label}>Day of the week</label>
                        <select style={S.input} value={enrolWeekday} onChange={e => setEnrolWeekday(e.target.value)}>
                          <option value="">Choose a day…</option>
                          {CLASS_DAYS.map(w => <option key={w.id} value={w.id}>{w.label}</option>)}
                        </select>
                      </div>
                    </div>

                    <div style={{ ...S.row, marginBottom: 10 }}>
                      <div style={S.col}>
                        <label style={S.label}>Class starts</label>
                        <input type="time" style={S.input} value={enrolStart} onChange={e => setEnrolStart(e.target.value)} />
                      </div>
                      <div style={S.col}>
                        <label style={S.label}>Class ends (optional)</label>
                        <input type="time" style={S.input} value={enrolEnd} onChange={e => setEnrolEnd(e.target.value)} />
                      </div>
                    </div>

                    <div style={{ marginBottom: 10, maxWidth: 220 }}>
                      <label style={S.label}>Fee for the whole term</label>
                      <input style={S.input} placeholder="£ e.g. 180" value={enrolFee} onChange={e => setEnrolFee(e.target.value)} />
                    </div>

                    {/* Preview — show exactly what will land in the calendar */}
                    {enrolSessions.length > 0 && (
                      <div style={{ marginBottom: 12, padding: "10px 12px", background: COLORS.sageLight, borderRadius: 10 }}>
                        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.sage, marginBottom: 6 }}>
                          {enrolSessions.length} session{enrolSessions.length === 1 ? "" : "s"} will be added
                          {enrolStart ? ` at ${enrolStart}` : ""}
                        </div>
                        <div style={{ fontSize: 11, color: COLORS.muted, lineHeight: 1.6 }}>
                          {enrolSessions.slice(0, 4).map(ds => fmtDay(ds)).join(" · ")}
                          {enrolSessions.length > 4 && ` … through ${fmtDay(enrolSessions[enrolSessions.length - 1])}`}
                        </div>
                        {activeTerm?.halfTermStart && activeTerm?.halfTermEnd && (
                          <div style={{ fontSize: 11, color: COLORS.sage, marginTop: 6 }}>Half-term week skipped.</div>
                        )}
                        {!activeTerm?.halfTermStart && (
                          <div style={{ fontSize: 11, color: COLORS.orangeLight ? "#8B5000" : COLORS.muted, marginTop: 6 }}>
                            No half-term dates set for this term — sessions may be booked on a week the club is closed.
                          </div>
                        )}
                      </div>
                    )}
                    {activeTerm && enrolWeekday !== "" && enrolSessions.length === 0 && (
                      <div style={{ fontSize: 12, color: COLORS.red, marginBottom: 10 }}>
                        No {CLASS_DAYS.find(w => w.id === Number(enrolWeekday))?.label}s fall inside this term. Check the term dates.
                      </div>
                    )}
                  </>
                )}

                {children.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={S.label}>For which child?</label>
                    <select style={S.input} value={addChild} onChange={e => setAddChild(e.target.value)}>
                      <option value="">All / unassigned</option>
                      {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ marginBottom: 10 }}>
                  <label style={S.label}>Notes</label>
                  <input style={S.input} placeholder="Kit needed, pick-up arrangements…" value={addNotes} onChange={e => setAddNotes(e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.btnPrimary} onClick={() => doEnrol(act)} disabled={!enrolSessions.length || !enrolStart}>
                    {enrolSessions.length ? `Enrol — add ${enrolSessions.length} session${enrolSessions.length === 1 ? "" : "s"}` : "Enrol"}
                  </button>
                  <button style={S.btnGhost} onClick={resetEnrol}>Cancel</button>
                </div>
              </div>
            )}

            {/* ── Holiday camp day picker (unchanged) ── */}
            {addingTo?.id === act.id && activityMode !== "termtime" && (
              <div style={{ marginTop: 14, paddingTop: 14, borderTop: `1px solid ${COLORS.border}` }}>
                {validBreaks.length === 0 ? (
                  <div style={{ background: COLORS.blushLight, borderRadius: 8, padding: "10px 12px", marginBottom: 12 }}>
                    <p style={{ fontSize: 12, color: COLORS.ink, margin: "0 0 8px", lineHeight: 1.5 }}>To pick days for this camp, first add your school holiday dates (half-term, Christmas, summer).</p>
                    <button style={{ ...S.btnSecondary, fontSize: 12, padding: "7px 12px" }} onClick={() => onOpenBreaks && onOpenBreaks()}>Add holiday dates</button>
                  </div>
                ) : (
                  <>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                      <label style={S.label}>Pick days to add this camp</label>
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...S.btnGhost, fontSize: 11, padding: "4px 10px" }}
                          onClick={() => setAddDates(getDays().map(d => d.toISOString().slice(0, 10)))}>
                          Select all
                        </button>
                        <button style={{ ...S.btnGhost, fontSize: 11, padding: "4px 10px" }}
                          onClick={() => setAddDates([])}>
                          Clear
                        </button>
                      </div>
                    </div>
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 7, marginBottom: 14 }}>
                      {getDays().map(day => {
                        const ds = day.toISOString().slice(0, 10);
                        const selected = addDates.includes(ds);
                        return (
                          <button key={ds}
                            onClick={() => setAddDates(selected ? addDates.filter(d => d !== ds) : [...addDates, ds])}
                            style={{
                              padding: "7px 13px", borderRadius: 20, fontSize: 12, fontWeight: 600,
                              cursor: "pointer", border: `2px solid ${selected ? COLORS.plumMid : COLORS.border}`,
                              background: selected ? COLORS.blushLight : COLORS.white,
                              color: selected ? COLORS.plum : COLORS.muted,
                              transition: "all 0.12s",
                            }}>
                            {selected ? "✓ " : ""}{formatDate(day)}
                          </button>
                        );
                      })}
                    </div>
                    {addDates.length > 0 && (
                      <div style={{ fontSize: 12, color: COLORS.plumMid, fontWeight: 600, marginBottom: 10 }}>
                        {addDates.length} day{addDates.length > 1 ? "s" : ""} selected
                      </div>
                    )}
                  </>
                )}
                {children.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <label style={S.label}>For which child?</label>
                    <select style={S.input} value={addChild} onChange={e => setAddChild(e.target.value)}>
                      <option value="">All / unassigned</option>
                      {children.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
                    </select>
                  </div>
                )}
                <div style={{ marginBottom: 10 }}>
                  <label style={S.label}>Notes</label>
                  <input style={S.input} placeholder="Bring packed lunch, 9am drop-off…" value={addNotes} onChange={e => setAddNotes(e.target.value)} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={{ ...S.btnPrimary, ...(addDates.length ? {} : { opacity: 0.45, cursor: "not-allowed" }) }} onClick={() => doAdd(act)} disabled={!addDates.length} title={addDates.length ? "" : "Pick at least one day first"}>
                    Add to {addDates.length > 0 ? `${addDates.length} day${addDates.length > 1 ? "s" : ""}` : "schedule"}
                  </button>
                  <button style={S.btnGhost} onClick={() => { setAddingTo(null); setAddDates([]); }}>Cancel</button>
                </div>
              </div>
            )}

            {/* ── Reviews section ── */}
            {(() => {
              const actKey = act.id || act.name;
              const actReviews = getReviews(actKey);
              const avg = avgRating(actKey);
              return (
                <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${BRAND.border}` }}>
                  {/* Aggregate rating */}
                  <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                    {avg ? (
                      <span style={{ fontSize: 13, fontWeight: 700, color: BRAND.navy }}>
                        {"★".repeat(Math.round(avg))}{"☆".repeat(5 - Math.round(avg))} {avg} · {actReviews.length} review{actReviews.length !== 1 ? "s" : ""}
                      </span>
                    ) : (
                      <span style={{ fontSize: 12, color: BRAND.muted }}>No reviews yet — be the first!</span>
                    )}
                    <button
                      onClick={() => setReviewingAct(reviewingAct?.id === act.id ? null : act)}
                      style={{ ...S.btnGhost, fontSize: 11, padding: "4px 10px", marginLeft: "auto" }}>
                      {reviewingAct?.id === act.id ? "Cancel" : "Leave a review"}
                    </button>
                  </div>

                  {/* Existing reviews */}
                  {actReviews.map((r, ri) => (
                    <div key={ri} style={{ background: BRAND.gray, borderRadius: 8, padding: "8px 12px", marginBottom: 6 }}>
                      <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.navy, marginBottom: 2 }}>
                        {"★".repeat(r.rating)}{"☆".repeat(5 - r.rating)} · {r.date}
                      </div>
                      {r.body && <div style={{ fontSize: 12, color: BRAND.muted }}>{r.body}</div>}
                      {r.userId && r.userId !== user?.id && (
                        <div style={{ display: "flex", gap: 12, marginTop: 6 }}>
                          <button onClick={() => reportReview(act, r)}
                            style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: BRAND.slate, cursor: "pointer", fontWeight: 600 }}>
                            Report
                          </button>
                          <button onClick={() => hideReviewer(r.userId)}
                            style={{ background: "none", border: "none", padding: 0, fontSize: 11, color: BRAND.muted, cursor: "pointer", fontWeight: 600 }}>
                            Hide reviews from this person
                          </button>
                        </div>
                      )}
                    </div>
                  ))}

                  {/* Leave a review form */}
                  {reviewingAct?.id === act.id && (
                    <div style={{ background: BRAND.sagePale, borderRadius: 10, padding: 14, marginTop: 8 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.navy, marginBottom: 10 }}>Your review</div>
                      <div style={{ display: "flex", gap: 6, marginBottom: 12 }}>
                        {[1,2,3,4,5].map(n => (
                          <button key={n} onClick={() => setReviewRating(n)}
                            style={{ background: "none", border: "none", cursor: "pointer", fontSize: 24, color: n <= reviewRating ? "#F4A42B" : BRAND.border, padding: 0, lineHeight: 1 }}>
                            ★
                          </button>
                        ))}
                      </div>
                      <textarea
                        style={{ ...S.input, minHeight: 70, resize: "vertical", marginBottom: 10, fontSize: 13 }}
                        placeholder="What did your child think? Would you recommend it?"
                        value={reviewBody}
                        onChange={e => setReviewBody(e.target.value)}
                      />
                      <div style={{ display: "flex", gap: 8 }}>
                        <button style={{ ...S.btnPrimary, opacity: reviewRating ? 1 : 0.5 }}
                          disabled={!reviewRating} onClick={() => submitReview(act)}>
                          Submit review
                        </button>
                        <button style={S.btnGhost} onClick={() => { setReviewingAct(null); setReviewRating(0); setReviewBody(""); }}>Cancel</button>
                      </div>
                      <p style={{ fontSize: 11, color: BRAND.muted, marginTop: 8, lineHeight: 1.5 }}>
                        Your review will be shared with other parents using MumHQ. It's shown without your name or email address. Please don't include children's names or other personal details — reviews that do, or are abusive, will be removed.
                      </p>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        );
      })}

      {displayResults.length > shownCount && (
        <button
          style={{ ...S.btnGhost, width: "100%", justifyContent: "center", marginBottom: 12, padding: "12px 16px" }}
          onClick={() => setShownCount(n => n + PAGE_SIZE)}>
          Show {Math.min(PAGE_SIZE, displayResults.length - shownCount)} more · {shownCount} of {displayResults.length} shown
        </button>
      )}

      {!loading && !showFavourites && results.length > 0 && visibleResults.length === 0 && (
        <div style={{ ...S.card, background: COLORS.orangeLight, border: `1px solid ${COLORS.orange}`, textAlign: "center", padding: 24 }}>
          <div style={{ fontSize: 22, marginBottom: 8 }}><Icon name="map" size={26} color={BRAND.grayMid} /></div>
          <div style={{ fontWeight: 700, color: COLORS.ink, marginBottom: 4 }}>No results within {DISTANCE_OPTIONS.find(o => o.miles === maxDistance)?.label}</div>
          <div style={{ fontSize: 13, color: COLORS.muted }}>Try increasing the distance or searching for a different activity.</div>
        </div>
      )}

      {!loading && !showFavourites && !results.length && favouritesList.length === 0 && (
        <div style={S.emptyState}><div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="palette" size={26} color={BRAND.grayMid} /></div>Search above to find activities</div>
      )}
    </div>
  );
}

const DAY_TYPES = [
  { id: "activity", label: "Activity Day", color: COLORS.sage,     bg: COLORS.sageLight,  border: COLORS.sageMid  },
  { id: "holiday",  label: "On Holiday",   color: COLORS.plumMid,  bg: COLORS.blushLight, border: COLORS.blush    },
  { id: "home",     label: "At Home",       color: "#8B5000",       bg: COLORS.orangeLight,border: COLORS.orange   },
];

function ListEditor({ items, onChange, placeholder, icon }) {
  const [draft, setDraft] = useState("");
  const add = () => { if (!draft.trim()) return; onChange([...items, { id: Date.now(), text: draft.trim() }]); setDraft(""); };
  const remove = (id) => onChange(items.filter(i => i.id !== id));
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 8 }}>
        <input style={{ ...S.input, flex: 1 }} placeholder={placeholder} value={draft}
          onChange={e => setDraft(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} />
        <button style={{ ...S.btnGhost, padding: "9px 14px", fontSize: 16 }} onClick={add}>＋</button>
      </div>
      {items.map(item => (
        <div key={item.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0", borderBottom: `1px solid ${COLORS.border}` }}>
          <span style={{ width: 5, height: 5, borderRadius: "50%", background: BRAND.grayMid, flexShrink: 0 }} />
          <span style={{ flex: 1, fontSize: 13, color: COLORS.ink }}>{item.text}</span>
          <button onClick={() => remove(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.muted, fontSize: 14 }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function StructuredListEditor({ items, onChange, placeholders, icon }) {
  const blank = { id: null, name: "", location: "", time: "", cost: "" };
  const [draft, setDraft] = useState(blank);
  const add = () => {
    if (!draft.name.trim()) return;
    onChange([...items, { ...draft, id: Date.now() }]);
    setDraft(blank);
  };
  const remove = (id) => onChange(items.filter(i => i.id !== id));
  return (
    <div>
      <div style={{ marginBottom: 8 }}>
        <div style={S.row}>
          <div style={{ flex: 2 }}>
            <input style={S.input} placeholder={placeholders.name || "Name"} value={draft.name} onChange={e => setDraft({ ...draft, name: e.target.value })} />
          </div>
          <div style={S.col}>
            <input style={S.input} placeholder={placeholders.time || "Time"} value={draft.time} onChange={e => setDraft({ ...draft, time: e.target.value })} />
          </div>
        </div>
        <div style={S.row}>
          <div style={{ flex: 2 }}>
            <input style={S.input} placeholder={placeholders.location || "Location"} value={draft.location} onChange={e => setDraft({ ...draft, location: e.target.value })} />
          </div>
          {placeholders.cost !== false && (
            <div style={S.col}>
              <input style={S.input} placeholder="Cost (optional)" value={draft.cost} onChange={e => setDraft({ ...draft, cost: e.target.value })} />
            </div>
          )}
        </div>
        <button style={{ ...S.btnGhost, fontSize: 12 }} onClick={add}>＋ Add</button>
      </div>
      {items.map(item => (
        <div key={item.id} style={{ display: "flex", alignItems: "flex-start", gap: 8, padding: "8px 0", borderBottom: `1px solid ${COLORS.border}` }}>
          <span style={{ fontSize: 16, marginTop: 1 }}>{icon}</span>
          <div style={{ flex: 1 }}>
            <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.ink }}>{item.name}</div>
            <div style={{ fontSize: 12, color: COLORS.muted }}>
              {item.location && `📍 ${item.location}`}{item.time && `  🕐 ${item.time}`}{item.cost && `  💷 ${item.cost}`}
            </div>
          </div>
          <button onClick={() => remove(item.id)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.muted, fontSize: 14 }}>✕</button>
        </div>
      ))}
    </div>
  );
}

function ActivityDayPanel({ data, onChange, childList }) {
  const d = data || { activities: [], notes: "" };
  const upd = (field, val) => onChange({ ...d, [field]: val });
  return (
    <div>
      <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.sage, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>🎯 Activities planned</div>
      <StructuredListEditor
        items={d.activities || []}
        onChange={v => upd("activities", v)}
        icon="🎯"
        placeholders={{ name: "Activity name", location: "Venue / location", time: "e.g. 9am–3pm" }}
      />
      <div style={{ marginTop: 14 }}>
        <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.sage, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 8 }}><Icon name="edit" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Notes</div>
        <textarea style={{ ...S.input, height: 60, resize: "vertical" }} placeholder="e.g. packed lunch needed, drop-off at 9am…"
          value={d.notes || ""} onChange={e => upd("notes", e.target.value)} />
      </div>
    </div>
  );
}

function HolidayDayPanel({ data, onChange }) {
  const d = data || { hotel: "", restaurants: [], parks: [], activities: [] };
  const upd = (field, val) => onChange({ ...d, [field]: val });
  return (
    <div>
      {/* Hotel */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.holidaySectionTitle}><Icon name="bed" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Accommodation</div>
        <input style={S.input} placeholder="Hotel or accommodation name & address" value={d.hotel || ""} onChange={e => upd("hotel", e.target.value)} />
      </div>
      {/* Restaurants */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.holidaySectionTitle}><Icon name="utensils" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Lunch / Dinner ideas</div>
        <ListEditor items={d.restaurants || []} onChange={v => upd("restaurants", v)} icon="🍽" placeholder="e.g. Pizza Express, High Street" />
      </div>
      {/* Parks / Attractions */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.holidaySectionTitle}><Icon name="tree" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Parks & attractions to try</div>
        <ListEditor items={d.parks || []} onChange={v => upd("parks", v)} icon="🌳" placeholder="e.g. Hampstead Heath, local beach" />
      </div>
      {/* Activities */}
      <div>
        <div style={S.holidaySectionTitle}><Icon name="ticket" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Activities planned</div>
        <StructuredListEditor
          items={d.activities || []}
          onChange={v => upd("activities", v)}
          icon="🎡"
          placeholders={{ name: "Activity name", location: "Location", time: "e.g. 10am" }}
        />
      </div>
    </div>
  );
}

function HomeDayPanel({ data, onChange, childList }) {
  const d = data || { playdates: [], shows: [], outings: [], homeActivities: [], notes: "" };
  const upd = (field, val) => onChange({ ...d, [field]: val });
  return (
    <div>
      {/* Playdates */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.homeSectionTitle}><Icon name="users" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Playdates</div>
        <StructuredListEditor
          items={d.playdates || []}
          onChange={v => upd("playdates", v)}
          icon="👫"
          placeholders={{ name: "Friend's name / who's coming", location: "Hosting at / going to", time: "e.g. 2pm", cost: false }}
        />
      </div>
      {/* Shows / events */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.homeSectionTitle}><Icon name="ticket" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Shows & events</div>
        <StructuredListEditor
          items={d.shows || []}
          onChange={v => upd("shows", v)}
          icon="🎭"
          placeholders={{ name: "Show / event name", location: "Venue", time: "e.g. 3:30pm" }}
        />
      </div>
      {/* Local outings */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.homeSectionTitle}><Icon name="mapPin" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Local outings</div>
        <ListEditor items={d.outings || []} onChange={v => upd("outings", v)} icon="🚶" placeholder="e.g. park, library, bike ride" />
      </div>
      {/* Home activities */}
      <div style={{ marginBottom: 18 }}>
        <div style={S.homeSectionTitle}><Icon name="palette" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />At-home activities</div>
        <ListEditor items={d.homeActivities || []} onChange={v => upd("homeActivities", v)} icon="🎨" placeholder="e.g. baking, board games, movie afternoon" />
      </div>
      {/* Notes */}
      <div>
        <div style={S.homeSectionTitle}><Icon name="edit" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Notes</div>
        <textarea style={{ ...S.input, height: 64, resize: "vertical" }} placeholder="Anything else for this day…"
          value={d.notes || ""} onChange={e => upd("notes", e.target.value)} />
      </div>
    </div>
  );
}

function DaySummary({ dayType, dayData }) {
  if (!dayType) return <span style={S.italic}>Tap to plan this day</span>;
  const dt = DAY_TYPES.find(t => t.id === dayType);
  const d = dayData || {};
  let detail = "";
  if (dayType === "activity") {
    const acts = d.activities || [];
    detail = acts.length ? acts.map(a => a.name).join(", ") : "No activities yet";
  } else if (dayType === "holiday") {
    const parts = [];
    if (d.hotel) parts.push(`${d.hotel}`);
    const actCount = (d.activities || []).length;
    if (actCount) parts.push(`${actCount} activity${actCount > 1 ? "ies" : ""}`);
    detail = parts.join(" · ") || "Add hotel & plans";
  } else if (dayType === "home") {
    const parts = [];
    if ((d.playdates || []).length) parts.push(`${d.playdates.length} playdate${d.playdates.length > 1 ? "s" : ""}`);
    if ((d.shows || []).length) parts.push(`${d.shows.length} show${d.shows.length > 1 ? "s" : ""}`);
    if ((d.outings || []).length) parts.push(`${d.outings.length} outing${d.outings.length > 1 ? "s" : ""}`);
    detail = parts.join(" · ") || "Add plans";
  }
  return (
    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
      <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: dt.bg, color: dt.color, border: `1px solid ${dt.border}` }}>{dt.label}</span>
      {detail && <span style={{ fontSize: 12, color: COLORS.muted }}>{detail}</span>}
    </div>
  );
}

function ScheduleTab({ children, breaks, terms, schedule, setSchedule, childcare, setChildcare, helpers, showToast }) {
  const [selBreak, setSelBreak] = useState("");
  // expandedDay: ds string or null
  const [expandedDay, setExpandedDay] = useState(null);

  // Periods = school breaks AND school terms. Term sessions live on dates that
  // fall outside every break, so without this they'd save but never render.
  const periods = [
    ...breaks.filter(b => b.name && b.startDate && b.endDate).map(b => ({ ...b, kind: "break" })),
    ...validTerms(terms).map(t => ({ ...t, kind: "term" })),
  ];
  const validBreaks = periods;
  const activeBreak = periods.find(p => p.name === selBreak) || periods[0];

  // A break shows every weekday. A term is ~100 days, so we only show the days
  // that actually have something booked.
  const periodDays = (p) =>
    !p ? [] : p.kind === "term" ? getTermDays(p, schedule) : getBreakDays(p);

  // schedule[ds] shape:
  // { dayType: "activity"|"holiday"|"home", dayData: {...}, activities: [...], cost: "" }
  // We keep existing .activities array for budget calc compatibility

  const getDayRecord = (ds) => schedule[ds] || {};
  const setDayRecord = (ds, record) => setSchedule({ ...schedule, [ds]: record });

  const setDayType = (ds, type) => {
    const rec = getDayRecord(ds);
    setDayRecord(ds, { ...rec, dayType: type, dayData: rec.dayData || {} });
    showToast(`Day set to ${DAY_TYPES.find(t => t.id === type)?.label} ✓`);
  };

  const setDayData = (ds, data) => {
    const rec = getDayRecord(ds);
    // Sync activities array for budget tracking (activity day type only)
    const acts = data.activities || [];
    setDayRecord(ds, { ...rec, dayData: data, activities: acts });
  };

  // Cost total for a day — pull from structured activities in any day type
  const getDayTotal = (ds) => {
    const rec = getDayRecord(ds);
    const allActs = [
      ...(rec.dayData?.activities || []),
      ...(rec.dayData?.shows || []),
    ];
    return allActs.reduce((s, a) => s + parseCost(a.cost), 0);
  };

  const breakTotal = useMemo(() => {
    if (!activeBreak) return 0;
    return periodDays(activeBreak).reduce((s, d) => s + getDayTotal(toDS(d)), 0);
  }, [activeBreak, schedule]);

  const breakBudget = parseCost(activeBreak?.budget);

  return (
    <div>
      {validBreaks.length === 0
        ? <div style={{ ...S.card, ...S.emptyState }}><div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="calendar" size={26} color={BRAND.grayMid} /></div>Add your school breaks or terms first</div>
        : <>
          {/* Break selector + budget bar */}
          <div style={S.card}>
            <div style={S.sectionTitle}><Icon name="list" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Schedule</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
              {validBreaks.map(b => (
                <button key={b.id} style={S.chip(selBreak === b.name || (!selBreak && activeBreak?.name === b.name))} onClick={() => setSelBreak(b.name)}>{b.name}</button>
              ))}
            </div>
          </div>

          {/* Day legend */}
          <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12, paddingLeft: 2 }}>
            {DAY_TYPES.map(dt => (
              <div key={dt.id} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 11, color: COLORS.muted }}>
                <div style={{ width: 10, height: 10, borderRadius: 3, background: dt.bg, border: `1.5px solid ${dt.border}` }} />
                {dt.label}
              </div>
            ))}
          </div>

          {/* Day cards */}
          {/* ── Enrolled weekly classes in this term ── */}
          {activeBreak?.kind === "term" && (() => {
            const series = {};
            periodDays(activeBreak).forEach(day => {
              (schedule[toDS(day)]?.dayData?.activities || []).forEach(a => {
                if (!a.seriesId) return;
                series[a.seriesId] = series[a.seriesId] || { name: a.seriesName || a.name, time: a.time, endTime: a.endTime, child: a.childName, count: 0, cost: 0 };
                series[a.seriesId].count += 1;
                series[a.seriesId].cost += parseCost(a.cost);
              });
            });
            const list = Object.entries(series);
            if (!list.length) return null;

            const cancelSeries = (sid, label) => {
              if (!window.confirm(`Cancel all sessions of "${label}"? This removes them from your schedule and calendar.`)) return;
              const next = {};
              Object.entries(schedule).forEach(([ds, rec]) => {
                const acts = rec?.dayData?.activities || [];
                const kept = acts.filter(a => a.seriesId !== sid);
                if (kept.length === acts.length) { next[ds] = rec; return; }
                const d = { ...(rec.dayData || {}), activities: kept };
                const empty = !kept.length && !(d.shows || []).length && !(d.playdates || []).length;
                if (!empty) next[ds] = { ...rec, dayData: d, activities: kept };
                // a day left with nothing at all is dropped entirely
              });
              setSchedule(next);
              showToast("Enrolment cancelled ✓");
            };

            return (
              <div style={S.card}>
                <div style={{ ...S.sectionTitle, marginBottom: 10 }}><Icon name="repeat" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Weekly classes this term</div>
                {list.map(([sid, s]) => (
                  <div key={sid} style={{ display: "flex", alignItems: "center", gap: 10, padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 600, fontSize: 13, color: COLORS.ink }}>{s.name}</div>
                      <div style={{ fontSize: 12, color: COLORS.muted }}>
                        {s.count} session{s.count === 1 ? "" : "s"}
                        {s.time && ` · ${s.time}${s.endTime ? `–${s.endTime}` : ""}`}
                        {s.child && ` · ${s.child}`}
                        {s.cost > 0 && ` · £${s.cost.toFixed(2)} for the term`}
                      </div>
                    </div>
                    <button style={{ ...S.btnGhost, fontSize: 11, padding: "5px 10px" }} onClick={() => cancelSeries(sid, s.name)}>Cancel</button>
                  </div>
                ))}
              </div>
            );
          })()}

          {activeBreak?.kind === "term" && periodDays(activeBreak).length === 0 && (
            <div style={{ ...S.card, ...S.emptyState }}>
              <div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="calendar" size={26} color={BRAND.grayMid} /></div>
              Nothing booked in {activeBreak.name} yet.
              <div style={{ fontSize: 12, marginTop: 6 }}>Find a weekly club in the Find tab and enrol — its sessions will appear here.</div>
            </div>
          )}

          {activeBreak && periodDays(activeBreak).map(day => {
            const ds = toDS(day);
            const rec = getDayRecord(ds);
            const dt = DAY_TYPES.find(t => t.id === rec.dayType);
            const isExpanded = expandedDay === ds;

            return (
              <div key={ds} style={{ ...S.card, borderLeft: dt ? `4px solid ${dt.border}` : `4px solid ${COLORS.border}`, padding: 0, overflow: "hidden" }}>

                {/* ── Collapsed header ── */}
                <div
                  style={{ padding: "14px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  onClick={() => setExpandedDay(isExpanded ? null : ds)}
                >
                  <div style={{ flex: 1 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 4 }}>
                      <span style={{ fontWeight: 700, color: COLORS.plumMid, fontSize: 13 }}>{formatDate(day)}</span>
                    </div>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                      <DaySummary dayType={rec.dayType} dayData={rec.dayData} />
                      {(childcare[ds]?.coveredBy || childcare[ds]?.carerName) && (
                        <span style={{ fontSize: 11, background: "#E8F4FD", color: "#2A6496", borderRadius: 20, padding: "2px 9px", fontWeight: 600, border: "1px solid #90CAF9" }}>
                          {childcare[ds].coveredBy || childcare[ds].carerName}
                        </span>
                      )}
                    </div>
                  </div>
                  <span style={{ color: COLORS.muted, fontSize: 18, marginLeft: 10 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>

                {/* ── Expanded detail ── */}
                {isExpanded && (
                  <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: 16 }}>

                    {/* Day type selector */}
                    <div style={{ marginBottom: 16 }}>
                      <label style={S.label}>What kind of day?</label>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {DAY_TYPES.map(t => (
                          <button
                            key={t.id}
                            style={{
                              padding: "9px 16px",
                              borderRadius: 20,
                              border: `2px solid ${rec.dayType === t.id ? t.border : COLORS.border}`,
                              background: rec.dayType === t.id ? t.bg : COLORS.white,
                              color: rec.dayType === t.id ? t.color : COLORS.muted,
                              fontSize: 13,
                              fontWeight: 700,
                              cursor: "pointer",
                              transition: "all 0.12s",
                            }}
                            onClick={() => setDayType(ds, t.id)}
                          >
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Panel per type */}
                    {/* ── Carer(s) for this day ── */}
                    {/* Multi-select: tap several carers for e.g. both parents taking */}
                    {/* the day off together. Writes coveredByIds (the array the rest */}
                    {/* of the app reads via assignedCarerIds), keeping the legacy */}
                    {/* single carerId/carerName fields in sync for old saved data. */}
                    {helpers.length > 0 && (
                      <div style={{ marginBottom: 16 }}>
                        <label style={S.label}>Who's with the kids?</label>
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 6 }}>
                          <button
                            style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${assignedCarerIds(childcare[ds]).length === 0 ? COLORS.plumMid : COLORS.border}`, background: assignedCarerIds(childcare[ds]).length === 0 ? COLORS.blushLight : COLORS.white, color: assignedCarerIds(childcare[ds]).length === 0 ? COLORS.plum : COLORS.muted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                            onClick={() => setChildcare({ ...childcare, [ds]: { ...childcare[ds], coveredByIds: [], coveredById: null, coveredBy: "", carerName: "", carerId: null } })}>
                            Unassigned
                          </button>
                          {helpers.map(h => {
                            const ids = assignedCarerIds(childcare[ds]);
                            const sel = ids.includes(h.id);
                            return (
                              <button key={h.id}
                                style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${sel ? "#90CAF9" : COLORS.border}`, background: sel ? "#E8F4FD" : COLORS.white, color: sel ? "#2A6496" : COLORS.muted, fontSize: 12, fontWeight: 600, cursor: "pointer", transition: "all 0.12s" }}
                                onClick={() => {
                                  const next = sel ? ids.filter(id => id !== h.id) : [...ids, h.id];
                                  const names = next.map(id => helpers.find(x => x.id === id)?.name).filter(Boolean);
                                  setChildcare({
                                    ...childcare,
                                    [ds]: {
                                      ...childcare[ds],
                                      coveredByIds: next,
                                      coveredById: next[0] ?? null,      // legacy single field
                                      coveredBy: names.join(" & "),
                                      carerId: next[0] ?? null,          // legacy single field
                                      carerName: names.join(" & "),      // shown on collapsed day pill
                                    },
                                  });
                                }}>
                                {sel ? "✓ " : ""}{h.name}
                                {h.relationship && <span style={{ opacity: 0.6, fontSize: 11, marginLeft: 4 }}>({h.relationship})</span>}
                              </button>
                            );
                          })}
                        </div>
                        {assignedCarerIds(childcare[ds]).length > 1 && (
                          <div style={{ fontSize: 11, color: COLORS.sage, fontWeight: 600, marginBottom: 6 }}>
                            {assignedCarerIds(childcare[ds]).length} carers on this day — it counts as a day off for each of them.
                          </div>
                        )}
                        {assignedCarerIds(childcare[ds]).length > 0 && (
                          <div style={{ fontSize: 12, color: COLORS.muted }}>
                            <input style={{ ...S.input, marginTop: 6 }} placeholder="Drop-off time, pick-up, notes for carer…" value={childcare[ds]?.carerNotes || ""} onChange={e => setChildcare({ ...childcare, [ds]: { ...childcare[ds], carerNotes: e.target.value } })} />
                          </div>
                        )}
                      </div>
                    )}

                    {rec.dayType === "activity" && (
                      <ActivityDayPanel data={rec.dayData} onChange={d => setDayData(ds, d)} childList={children} />
                    )}
                    {rec.dayType === "holiday" && (
                      <HolidayDayPanel data={rec.dayData} onChange={d => setDayData(ds, d)} />
                    )}
                    {rec.dayType === "home" && (
                      <HomeDayPanel data={rec.dayData} onChange={d => setDayData(ds, d)} childList={children} />
                    )}
                    {!rec.dayType && (
                      <div style={{ ...S.emptyState, padding: "16px 0 4px" }}>
                        Choose a day type above to start planning</div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>}
    </div>
  );
}

function ShareTab({ breaks, terms, schedule, children, childcare, helpers, showToast }) {
  const [selectedCarer, setSelectedCarer] = useState("");
  const [copyMode, setCopyMode] = useState("all");

  const actLine = (a) => a.location ? `${a.name}, ${a.location}` : a.name;

  const buildCopyText = () => {
    const validBreaks = breaks.filter(b => b.name && b.startDate && b.endDate);
    let lines = [];
    if (copyMode === "camps") lines.push("HOLIDAY CAMP SCHEDULE", "");
    else if (copyMode === "afterschool") lines.push("AFTER SCHOOL & WEEKEND ACTIVITIES", "");
    else if (copyMode !== "all") { const child = children.find(c => String(c.id) === copyMode); if (child) lines.push(`${child.name.toUpperCase()} — SCHEDULE`, ""); }
    else { lines.push("HOLIDAY SCHEDULE", ""); if (children.length) lines.push(`Children: ${children.map(c => c.name).join(", ")}`, ""); }

    // After-school / weekend activities live in TERM time, not the holidays.
    // Walk each term's dates and list only the activity days inside it.
    if (copyMode === "afterschool") {
      const termList = validTerms(terms);
      if (!termList.length) {
        lines.push("No school terms saved yet — add them in the Schedule tab so your", "term-time activities can show here.", "", SHARE_SIGNOFF);
        return lines.join("\n");
      }
      const inAnyBreak = (ds) => validBreaks.some(b => ds >= b.startDate && ds <= b.endDate);
      const dsKeys = Object.keys(schedule).sort();
      [...termList].sort((a, b) => a.startDate.localeCompare(b.startDate)).forEach(term => {
        const tDays = [];
        dsKeys.forEach(ds => {
          if (ds < term.startDate || ds > term.endDate) return;
          if (inAnyBreak(ds)) return; // a camp day never counts as after-school
          const rec = schedule[ds] || {};
          const acts = rec.dayData?.activities || [];
          if (rec.dayType !== "activity" || !acts.length) return;
          tDays.push(fmtDay(ds));
          acts.forEach(a => tDays.push(`  ${actLine(a)}`));
        });
        if (tDays.length) { lines.push(term.name.toUpperCase()); lines.push(...tDays); lines.push(""); }
      });
      lines.push(SHARE_SIGNOFF);
      return lines.join("\n");
    }

    // Holidays: "all", "camps", and per-child all read the school breaks.
    validBreaks.forEach(br => {
      const brDays = [];
      getBreakDays(br).forEach(day => {
        const ds = day.toISOString().slice(0, 10);
        const rec = schedule[ds] || {};
        const acts = rec.dayData?.activities || [];
        if (copyMode === "all") {
          if (!rec.dayType) return;
          if (rec.dayType === "home") brDays.push(`${fmtDay(day)}  At home`);
          else if (rec.dayType === "holiday") brDays.push(`${fmtDay(day)}  Away`);
          else if (rec.dayType === "activity") { brDays.push(fmtDay(day)); acts.forEach(a => brDays.push(`  ${actLine(a)}`)); }
        } else if (copyMode === "camps") {
          if (rec.dayType !== "activity" || !acts.length) return;
          brDays.push(fmtDay(day)); acts.forEach(a => brDays.push(`  ${actLine(a)}`));
        } else {
          const child = children.find(c => String(c.id) === copyMode);
          if (!child) return;
          if (rec.dayType === "activity" && acts.length) { brDays.push(fmtDay(day)); acts.filter(a => !a.childName || a.childName === child.name).forEach(a => brDays.push(`  ${actLine(a)}`)); }
          else if (rec.dayType === "home") brDays.push(`${fmtDay(day)}  At home`);
        }
      });
      if (brDays.length) { lines.push(br.name.toUpperCase()); lines.push(...brDays); lines.push(""); }
    });
    lines.push(SHARE_SIGNOFF);
    return lines.join("\n");
  };

  // Free-weekend finder for arranging playdates. Walks every Saturday and
  // Sunday inside each term's date range (half-term weekends included — they're
  // prime playdate slots), and shows any weekend clubs already booked so the
  // recipient can see what's on. Only lists today onward: past weekends are no
  // use for arranging a playdate.
  const buildPlaydateText = () => {
    const termList = validTerms(terms);
    if (!termList.length) {
      return "No school terms saved yet.\nAdd your terms in the Schedule tab first, then your free weekends will show here.";
    }
    const todayDS = toDS(new Date());
    const slotLine = (a) => {
      const when = a.time ? (a.endTime ? ` (${a.time}–${a.endTime})` : ` (${a.time})`) : "";
      return `${actLine(a)}${when}`;
    };

    const lines = ["Free weekends for a playdate 🎉"];
    if (children.length) lines.push(`Children: ${children.map(c => c.name).join(", ")}`);
    lines.push("");

    let grandFree = 0;
    let anyWeekend = false;

    [...termList].sort((a, b) => a.startDate.localeCompare(b.startDate)).forEach(term => {
      const cur = new Date(term.startDate + "T12:00:00");
      const end = new Date(term.endDate + "T12:00:00");
      const dayLines = [];
      let freeCount = 0;
      while (cur <= end) {
        const dow = cur.getDay();
        if (dow === 6 || dow === 0) {
          const ds = toDS(cur);
          if (ds >= todayDS) {
            anyWeekend = true;
            const d = schedule[ds]?.dayData || {};
            const clubs = [...(d.activities || []), ...(d.shows || []), ...(d.playdates || [])];
            if (clubs.length) {
              dayLines.push(`🗓️ ${fmtDay(ds)} — ${clubs.map(slotLine).join("; ")}`);
            } else {
              dayLines.push(`✅ ${fmtDay(ds)} — free`);
              freeCount++;
            }
          }
        }
        cur.setDate(cur.getDate() + 1);
      }
      if (dayLines.length) {
        lines.push(term.name);
        lines.push(...dayLines);
        lines.push(`${freeCount} free weekend ${freeCount === 1 ? "day" : "days"} this term`);
        lines.push("");
        grandFree += freeCount;
      }
    });

    if (!anyWeekend) {
      return "No upcoming weekends found in your saved terms.\nCheck that a current or future term is set up in the Schedule tab.";
    }
    if (termList.length > 1) lines.push(`${grandFree} free weekend ${grandFree === 1 ? "day" : "days"} in total`, "");
    lines.push(SHARE_SIGNOFF);
    return lines.join("\n");
  };

  const downloadICS = () => {
    const blob = new Blob([generateICS(breaks, schedule)], { type: "text/calendar" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = "mumhq-holidays.ics"; a.click();
    showToast("Calendar downloaded");
  };

  const buildCarerSummary = (helper) => {
    if (!helper) return "";
    let lines = [`Hi ${helper.name},`, "", "Here are the days you are looking after the children:", ""];
    let count = 0;
    breaks.filter(b => b.name && b.startDate && b.endDate).forEach(br => {
      const days = [];
      for (let d = new Date(br.startDate); d <= new Date(br.endDate); d.setDate(d.getDate() + 1)) {
        const ds = d.toISOString().slice(0, 10);
        const cc = childcare[ds];
        if (assignedCarerIds(cc).includes(helper.id)) {
          const rec = schedule[ds] || {};
          let dayLine = fmtDay(ds);
          if (rec.dayType === "activity" && rec.dayData?.activities?.length) dayLine += " — " + rec.dayData.activities.map(a => actLine(a)).join(", ");
          else if (rec.dayType === "home") dayLine += " — At home";
          if (cc.carerNotes) dayLine += `\n  Note: ${cc.carerNotes}`;
          days.push(dayLine); count++;
        }
      }
      if (days.length) { lines.push(br.name + ":"); lines.push(...days); lines.push(""); }
    });
    if (count === 0) return `No days are currently assigned to ${helper.name}. Assign days in the Schedule tab first.`;
    lines.push("Thank you!", "Sent from MumHQ");
    return lines.join("\n");
  };

  const selectedHelper = helpers.find(h => String(h.id) === selectedCarer);
  const carerText = selectedHelper ? buildCarerSummary(selectedHelper) : "";
  const previewText = copyMode === "playdate" ? buildPlaydateText() : buildCopyText();
  const copyOptions = [
    { id: "all", label: "Full schedule" },
    { id: "camps", label: "Holiday camps only" },
    { id: "afterschool", label: "After school activities" },
    { id: "playdate", label: "Free weekends" },
    ...children.map(c => ({ id: String(c.id), label: `${c.name}'s schedule` })),
  ];

  return (
    <div>
      {helpers.length > 0 && (
        <div style={S.card}>
          <div style={S.sectionTitle}>Send Schedule to a Carer</div>
          <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 14, lineHeight: 1.5 }}>Select a carer to export just their assigned days. Assign carers to days in the Schedule tab first.</p>
          <div style={{ marginBottom: 14 }}>
            <label style={S.label}>Choose a carer</label>
            <select style={S.input} value={selectedCarer} onChange={e => setSelectedCarer(e.target.value)}>
              <option value="">Select…</option>
              {helpers.map(h => <option key={h.id} value={h.id}>{h.name}{h.relationship ? ` (${h.relationship})` : ""}</option>)}
            </select>
          </div>
          {selectedHelper && (
            <>
              <div style={{ ...S.summaryBox, marginBottom: 12, fontSize: 12 }}>{carerText}</div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                {selectedHelper.contact?.includes("@") && <button style={S.btnPrimary} onClick={() => window.open(`mailto:${selectedHelper.contact}?subject=${encodeURIComponent("Your childcare days")}&body=${encodeURIComponent(carerText)}`)}>Email {selectedHelper.name}</button>}
                <button style={{ background: BRAND.sage, border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff" }} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(carerText)}`, "_blank")}>WhatsApp</button>
                <button style={S.btnGhost} onClick={() => { navigator.clipboard.writeText(carerText); showToast(`${selectedHelper.name}'s schedule copied`); }}>Copy</button>
              </div>
            </>
          )}
        </div>
      )}
      <div style={S.card}>
        <div style={S.sectionTitle}>Export to Calendar</div>
        <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 14, lineHeight: 1.5 }}>Download an .ics file to add your holiday plan to Apple Calendar, Google Calendar, or Outlook.</p>
        <button style={S.btnPrimary} onClick={downloadICS}>Download .ics file</button>
      </div>
      <div style={S.card}>
        <div style={S.sectionTitle}>Copy & Share</div>
        <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 14, lineHeight: 1.5 }}>Choose what to copy — then paste into a message, email or note.</p>
        <label style={S.label}>What to copy</label>
        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 16 }}>
          {copyOptions.map(opt => (
            <button key={opt.id} onClick={() => setCopyMode(opt.id)}
              style={{ padding: "7px 14px", borderRadius: 20, fontSize: 12, fontWeight: 600, cursor: "pointer", border: `1.5px solid ${copyMode === opt.id ? BRAND.slate : BRAND.border}`, background: copyMode === opt.id ? BRAND.sagePale : BRAND.white, color: copyMode === opt.id ? BRAND.navy : BRAND.muted }}>
              {opt.label}
            </button>
          ))}
        </div>
        <div style={{ ...S.summaryBox, marginBottom: 14, fontSize: 12 }}>{previewText}</div>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <button style={S.btnPrimary} onClick={() => { navigator.clipboard.writeText(previewText); showToast("Copied"); }}>Copy</button>
          <button style={{ background: BRAND.sage, border: "none", borderRadius: 8, padding: "10px 16px", fontSize: 13, fontWeight: 600, cursor: "pointer", color: "#fff" }} onClick={() => window.open(`https://wa.me/?text=${encodeURIComponent(previewText)}`, "_blank")}>WhatsApp</button>
        </div>
      </div>
    </div>
  );
}


// Coverage for the selected holiday periods. A day is covered for a child when:
//  · a carer / childcare type is set for that day (covers everyone), or
//  · the family is away ("On Holiday") that day (covers everyone), or
//  · a camp/activity is booked for that child — or for "All / unassigned".
// Before this, only carer entries counted, so booked camps showed 0 days covered.
function ChildcareSummaryBar({ childcare, breaks, terms = [], schedule = {}, children = [] }) {
  const counts = {};
  COVERAGE_TYPES.forEach(t => counts[t.id] = 0);
  const validBreaks = holidayPeriods(breaks, terms);
  const kids = children.filter(c => (c.name || "").trim());
  const perChild = Object.fromEntries(kids.map(c => [c.name.trim(), 0]));
  let total = 0, covered = 0;
  validBreaks.forEach(b => {
    getBreakDays(b).forEach(day => {
      const ds = day.toISOString().slice(0, 10);
      total++;
      const care = childcare[ds];
      const rec = schedule[ds] || {};
      const acts = rec.dayType === "activity" ? (rec.dayData?.activities || []) : [];
      const everyone = !!care?.type || rec.dayType === "holiday" || acts.some(a => !(a.childName || "").trim());
      const named = new Set(acts.map(a => (a.childName || "").trim()).filter(Boolean));
      if (care?.type) counts[care.type] = (counts[care.type] || 0) + 1;
      else if (acts.length) counts.camp = (counts.camp || 0) + 1;
      kids.forEach(c => { if (everyone || named.has(c.name.trim())) perChild[c.name.trim()]++; });
      const dayCovered = kids.length ? kids.every(c => everyone || named.has(c.name.trim())) : (everyone || acts.length > 0);
      if (dayCovered) covered++;
    });
  });
  if (total === 0) return null;
  return (
    <div style={{ ...S.card, background: `linear-gradient(135deg, #47654F, #62826D)`, color: COLORS.white, marginBottom: 14 }}>
      <div style={{ fontSize: 12, fontWeight: 700, opacity: 0.7, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>Childcare Coverage</div>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-end", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 28, fontWeight: 800, lineHeight: 1 }}>{covered}<span style={{ fontSize: 16, opacity: 0.7 }}>/{total}</span></div>
          <div style={{ fontSize: 12, opacity: 0.7, marginTop: 3 }}>{kids.length > 1 ? "days covered for everyone" : "days covered"}</div>
        </div>
        <div style={{ fontSize: 13, fontWeight: 700, color: covered === total ? COLORS.sageMid : COLORS.yellow }}>
          {covered === total ? "✓ All days covered!" : `${total - covered} day${total - covered === 1 ? "" : "s"} need cover`}
        </div>
      </div>
      <div style={{ background: "rgba(255,255,255,0.15)", borderRadius: 6, height: 8, overflow: "hidden", marginBottom: 12 }}>
        <div style={{ height: "100%", borderRadius: 6, background: covered === total ? COLORS.sage : COLORS.yellow, width: `${total ? (covered / total) * 100 : 0}%`, transition: "width 0.4s" }} />
      </div>
      {kids.length > 1 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          {kids.map(c => (
            <div key={c.id ?? c.name} style={{ fontSize: 12, background: "rgba(255,255,255,0.22)", borderRadius: 20, padding: "3px 10px", fontWeight: 700 }}>
              {c.name.trim()} · {perChild[c.name.trim()]}/{total}
            </div>
          ))}
        </div>
      )}
      <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
        {COVERAGE_TYPES.filter(t => counts[t.id] > 0).map(t => (
          <div key={t.id} style={{ fontSize: 11, background: "rgba(255,255,255,0.15)", borderRadius: 20, padding: "3px 10px", fontWeight: 600 }}>
            {t.label} · {counts[t.id]}d
          </div>
        ))}
      </div>
    </div>
  );
}

function ChildcareTab({ breaks, childcare, setChildcare, helpers, setHelpers, children, showToast }) {
  const [selBreak, setSelBreak] = useState("");
  const [expandedDay, setExpandedDay] = useState(null);
  const [showHelperForm, setShowHelperForm] = useState(false);
  const [helperDraft, setHelperDraft] = useState({ name: "", relationship: "", contact: "", notes: "" });

  const validBreaks = breaks.filter(b => b.name && b.startDate && b.endDate);
  const activeBreak = validBreaks.find(b => b.name === selBreak) || validBreaks[0];

  const getRec = (ds) => childcare[ds] || {};
  const setRec = (ds, rec) => setChildcare({ ...childcare, [ds]: rec });

  const saveHelper = () => {
    if (!helperDraft.name.trim()) return;
    setHelpers([...helpers, { ...helperDraft, id: Date.now() }]);
    setHelperDraft({ name: "", relationship: "", contact: "", notes: "" });
    setShowHelperForm(false);
    showToast(`${helperDraft.name} added ✓`);
  };

  const removeHelper = (id) => setHelpers(helpers.filter(h => h.id !== id));

  // Coverage stats for active break
  const breakStats = useMemo(() => {
    if (!activeBreak) return { total: 0, covered: 0, uncovered: [] };
    const days = getBreakDays(activeBreak);
    const uncovered = [];
    let covered = 0;
    days.forEach(day => {
      const ds = day.toISOString().slice(0, 10);
      if (childcare[ds]?.type) covered++;
      else uncovered.push(day);
    });
    return { total: days.length, covered, uncovered };
  }, [activeBreak, childcare]);

  // Generate shareable summary for a helper
  const generateHelperSummary = (helper) => {
    if (!activeBreak) return "";
    let text = `Hi ${helper.name}! Here are the days I need help with during ${activeBreak.name}:

`;
    getBreakDays(activeBreak).forEach(day => {
      const ds = day.toISOString().slice(0, 10);
      const rec = childcare[ds] || {};
      if (assignedCarerIds(rec).includes(helper.id) || rec.coveredBy === helper.name) {
        text += `📅 ${formatDate(day)}
`;
        if (rec.dropoff) text += `   Drop-off: ${rec.dropoff}
`;
        if (rec.pickup) text += `   Pick-up: ${rec.pickup}
`;
        if (rec.notes) text += `   Notes: ${rec.notes}
`;
        text += "\n";
      }
    });
    text += "Thank you so much! 💜\nSent from MumHQ";
    return text;
  };

  const shareWithHelper = (helper) => {
    const text = encodeURIComponent(generateHelperSummary(helper));
    if (helper.contact?.includes("@")) {
      window.open(`mailto:${helper.contact}?subject=Childcare%20days%20-%20${activeBreak?.name || "Holiday"}&body=${text}`);
    } else {
      window.open(`https://wa.me/?text=${text}`, "_blank");
    }
  };

  // RELATIONSHIP_OPTIONS now defined globally above

  return (
    <div>
      {validBreaks.length === 0 ? (
        <div style={{ ...S.card, ...S.emptyState }}>
          <div style={{ fontSize: 28, marginBottom: 8 }}><Icon name="calendar" size={26} color={BRAND.grayMid} /></div>
          Add your school breaks first to start planning childcare
        </div>
      ) : (
        <>
          {/* ── Your helpers ── */}
          <div style={S.card}>
            <div style={S.sectionTitle}><Icon name="users" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Your Childcare Helpers</div>
            <p style={S.helpText}>
              Add the people who help with childcare — grandparents, childminders, friends. You can then assign them to days and send them their schedule.
            </p>

            {helpers.length === 0 && !showHelperForm && (
              <div style={S.emptyState}><div style={{ fontSize: 24, marginBottom: 6 }}><Icon name="users" size={26} color={BRAND.grayMid} /></div>No helpers added yet</div>
            )}

            {helpers.map((h, i) => (
              <div key={h.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                <div style={{ width: 40, height: 40, borderRadius: "50%", background: CHILD_AVATAR_COLORS[i % CHILD_AVATAR_COLORS.length], display: "flex", alignItems: "center", justifyContent: "center", fontSize: 18, fontWeight: 800, color: BRAND.white, flexShrink: 0 }}>
                  {h.name[0].toUpperCase()}
                </div>
                <div style={{ flex: 1 }}>
                  <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.plum }}>{h.name}</div>
                  <div style={{ fontSize: 12, color: COLORS.muted }}>{h.relationship}{h.contact && ` · ${h.contact}`}</div>
                  {h.notes && <div style={S.italic}>{h.notes}</div>}
                </div>
                <div style={{ display: "flex", gap: 6 }}>
                  <button style={{ ...S.btnSecondary, padding: "7px 12px", fontSize: 12 }} onClick={() => shareWithHelper(h)}>
                    📤 Send schedule
                  </button>
                  <button style={{ ...S.btnGhost, color: COLORS.blushDark, borderColor: COLORS.blush, padding: "7px 10px" }} onClick={() => removeHelper(h.id)}>✕</button>
                </div>
              </div>
            ))}

            {showHelperForm && (
              <div style={{ marginTop: 14, padding: 14, background: COLORS.blushLight, borderRadius: 12 }}>
                <div style={S.row}>
                  <div style={{ flex: 2 }}>
                    <label style={S.label}>Name</label>
                    <input style={S.input} placeholder="e.g. Grandma Sue" value={helperDraft.name} onChange={e => setHelperDraft({ ...helperDraft, name: e.target.value })} />
                  </div>
                  <div style={S.col}>
                    <label style={S.label}>Relationship</label>
                    <select style={S.input} value={helperDraft.relationship} onChange={e => setHelperDraft({ ...helperDraft, relationship: e.target.value })}>
                      <option value="">Select…</option>
                      {RELATIONSHIP_OPTIONS.map(r => <option key={r} value={r}>{r}</option>)}
                    </select>
                  </div>
                </div>
                <div style={S.row}>
                  <div style={{ flex: 2 }}>
                    <label style={S.label}>Contact (phone or email)</label>
                    <input style={S.input} placeholder="07700 900000 or email@example.com" value={helperDraft.contact} onChange={e => setHelperDraft({ ...helperDraft, contact: e.target.value })} />
                  </div>
                </div>
                <div style={{ marginBottom: 12 }}>
                  <label style={S.label}>Notes</label>
                  <input style={S.input} placeholder="e.g. Only available mornings, can't drive" value={helperDraft.notes} onChange={e => setHelperDraft({ ...helperDraft, notes: e.target.value })} />
                </div>
                <div style={{ display: "flex", gap: 8 }}>
                  <button style={S.btnPrimary} onClick={saveHelper}>Save helper</button>
                  <button style={S.btnGhost} onClick={() => setShowHelperForm(false)}>Cancel</button>
                </div>
              </div>
            )}

            {!showHelperForm && (
              <button style={{ ...S.btnPrimary, marginTop: 14 }} onClick={() => setShowHelperForm(true)}>＋ Add a helper</button>
            )}
          </div>

          {/* ── Break selector + coverage bar ── */}
          <div style={S.card}>
            <div style={S.sectionTitle}><Icon name="calendar" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Who's got the kids?</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 14 }}>
              {validBreaks.map(b => (
                <button key={b.id} style={S.chip(selBreak === b.name || (!selBreak && activeBreak?.name === b.name))} onClick={() => setSelBreak(b.name)}>{b.name}</button>
              ))}
            </div>

            {/* Coverage progress */}
            {activeBreak && (
              <div style={{ padding: "12px 14px", background: COLORS.cream, borderRadius: 10, marginBottom: 4 }}>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 8 }}>
                  <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.plum }}>
                    {breakStats.covered}/{breakStats.total} days covered
                  </div>
                  <div style={{ fontSize: 12, color: breakStats.covered === breakStats.total ? COLORS.sage : COLORS.orange, fontWeight: 700 }}>
                    {breakStats.covered === breakStats.total ? "✓ All sorted!" : `${breakStats.total - breakStats.covered} days to sort`}
                  </div>
                </div>
                <div style={{ background: COLORS.border, borderRadius: 6, height: 8, overflow: "hidden" }}>
                  <div style={{ height: "100%", borderRadius: 6, background: breakStats.covered === breakStats.total ? COLORS.sage : COLORS.orange, width: `${breakStats.total ? (breakStats.covered / breakStats.total) * 100 : 0}%`, transition: "width 0.4s" }} />
                </div>
                {breakStats.uncovered.length > 0 && breakStats.uncovered.length <= 5 && (
                  <div style={{ marginTop: 8, fontSize: 12, color: COLORS.muted }}>
                    ⚠️ Uncovered: {breakStats.uncovered.map(d => formatDate(d)).join(", ")}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* ── Day-by-day cards ── */}
          {activeBreak && getBreakDays(activeBreak).map(day => {
            const ds = day.toISOString().slice(0, 10);
            const rec = getRec(ds);
            const ct = COVERAGE_TYPES.find(t => t.id === rec.type);
            const isExpanded = expandedDay === ds;

            return (
              <div key={ds} style={{ ...S.card, borderLeft: `4px solid ${ct ? ct.border : COLORS.border}`, padding: 0, overflow: "hidden" }}>

                {/* Collapsed header */}
                <div style={{ padding: "13px 16px", cursor: "pointer", display: "flex", justifyContent: "space-between", alignItems: "center" }}
                  onClick={() => setExpandedDay(isExpanded ? null : ds)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: COLORS.plumMid, fontSize: 13, marginBottom: 4 }}>{formatDate(day)}</div>
                    {ct ? (
                      <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                        <span style={{ fontSize: 11, fontWeight: 700, padding: "2px 10px", borderRadius: 20, background: ct.bg, color: ct.color, border: `1px solid ${ct.border}` }}>{ct.label}</span>
                        {rec.coveredBy && <span style={{ fontSize: 12, color: COLORS.muted }}>with {rec.coveredBy}</span>}
                        {rec.dropoff && <span style={{ fontSize: 11, color: COLORS.muted }}>🕐 Drop {rec.dropoff}</span>}
                        {rec.pickup && <span style={{ fontSize: 11, color: COLORS.muted }}>· Pick-up {rec.pickup}</span>}
                      </div>
                    ) : (
                      <span style={S.italic}>Tap to assign cover</span>
                    )}
                  </div>
                  <span style={{ color: COLORS.muted, fontSize: 18, marginLeft: 10 }}>{isExpanded ? "▲" : "▼"}</span>
                </div>

                {/* Expanded detail */}
                {isExpanded && (
                  <div style={{ borderTop: `1px solid ${COLORS.border}`, padding: 16 }}>

                    {/* Coverage type */}
                    <div style={{ marginBottom: 14 }}>
                      <label style={S.label}>Who's got the kids?</label>
                      <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                        {COVERAGE_TYPES.map(t => (
                          <button key={t.id}
                            style={{ padding: "8px 14px", borderRadius: 20, border: `2px solid ${rec.type === t.id ? t.border : COLORS.border}`, background: rec.type === t.id ? t.bg : COLORS.white, color: rec.type === t.id ? t.color : COLORS.muted, fontSize: 12, fontWeight: 700, cursor: "pointer", transition: "all 0.12s" }}
                            onClick={() => { setRec(ds, { ...rec, type: t.id }); showToast(`${formatDate(day)} — ${t.label} ✓`); }}>
                            {t.label}
                          </button>
                        ))}
                      </div>
                    </div>

                    {/* Covered by — one or more helpers (tap several for e.g. both parents) */}
                    {rec.type && rec.type !== "free" && rec.type !== "work" && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={S.label}>Covered by</label>
                        {helpers.length > 0 ? (
                          <>
                            <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 8 }}>
                              {helpers.map(h => {
                                const ids = assignedCarerIds(rec);
                                const sel = ids.includes(h.id);
                                return (
                                  <button key={h.id}
                                    style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${sel ? COLORS.plumMid : COLORS.border}`, background: sel ? COLORS.blushLight : COLORS.white, color: sel ? COLORS.plum : COLORS.muted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                                    onClick={() => {
                                      const next = sel ? ids.filter(id => id !== h.id) : [...ids, h.id];
                                      const names = next.map(id => (helpers.find(x => x.id === id)?.name)).filter(Boolean);
                                      setRec(ds, {
                                        ...rec,
                                        coveredByIds: next,
                                        coveredById: next[0] ?? null,   // keep legacy single field in sync
                                        coveredBy: names.join(" & "),
                                      });
                                    }}>
                                    {sel ? "✓ " : ""}{h.name}
                                  </button>
                                );
                              })}
                            </div>
                            {assignedCarerIds(rec).length > 1 && (
                              <div style={{ fontSize: 11, color: COLORS.sage, fontWeight: 600, marginBottom: 8 }}>
                                Both carers assigned — each will see this day in their own summary.
                              </div>
                            )}
                          </>
                        ) : null}
                        <input style={S.input} placeholder="Or type a name" value={rec.coveredBy || ""} onChange={e => setRec(ds, { ...rec, coveredBy: e.target.value, coveredById: null, coveredByIds: [] })} />
                      </div>
                    )}

                    {/* Which children */}
                    {children.length > 1 && (
                      <div style={{ marginBottom: 12 }}>
                        <label style={S.label}>Which children</label>
                        <div style={{ display: "flex", gap: 7, flexWrap: "wrap" }}>
                          {children.map(c => {
                            const sel = (rec.childIds || []).includes(c.id);
                            return (
                              <button key={c.id}
                                style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${sel ? COLORS.sageMid : COLORS.border}`, background: sel ? COLORS.sageLight : COLORS.white, color: sel ? COLORS.sage : COLORS.muted, fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                                onClick={() => {
                                  const ids = rec.childIds || [];
                                  setRec(ds, { ...rec, childIds: sel ? ids.filter(i => i !== c.id) : [...ids, c.id] });
                                }}>
                                {c.name}
                              </button>
                            );
                          })}
                        </div>
                      </div>
                    )}

                    {/* Drop-off / Pick-up times */}
                    <div style={S.row}>
                      <div style={S.col}>
                        <label style={S.label}>Drop-off time</label>
                        <input style={S.input} placeholder="e.g. 9:00am" value={rec.dropoff || ""} onChange={e => setRec(ds, { ...rec, dropoff: e.target.value })} />
                      </div>
                      <div style={S.col}>
                        <label style={S.label}>Pick-up time</label>
                        <input style={S.input} placeholder="e.g. 5:30pm" value={rec.pickup || ""} onChange={e => setRec(ds, { ...rec, pickup: e.target.value })} />
                      </div>
                    </div>

                    {/* Location */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={S.label}>Location / address</label>
                      <input style={S.input} placeholder="e.g. Grandma's house, 12 Oak Road" value={rec.location || ""} onChange={e => setRec(ds, { ...rec, location: e.target.value })} />
                    </div>

                    {/* Notes */}
                    <div style={{ marginBottom: 12 }}>
                      <label style={S.label}>Notes for the helper</label>
                      <textarea style={{ ...S.input, height: 60, resize: "vertical" }} placeholder="e.g. Packed lunch in bag, allergic to nuts, bedtime is 7pm" value={rec.notes || ""} onChange={e => setRec(ds, { ...rec, notes: e.target.value })} />
                    </div>

                    {/* Quick share for this day */}
                    {rec.coveredBy && (
                      <div style={{ padding: "10px 14px", background: COLORS.sageLight, borderRadius: 10, fontSize: 13, color: COLORS.sage, fontWeight: 600, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span>Send day details to {rec.coveredBy}?</span>
                        <button style={{ ...S.btnSecondary, padding: "6px 12px", fontSize: 12 }}
                          onClick={() => {
                            const text = `Hi ${rec.coveredBy}! Can you cover ${formatDate(day)}?${rec.dropoff ? ` Drop-off: ${rec.dropoff}.` : ""}${rec.pickup ? ` Pick-up: ${rec.pickup}.` : ""}${rec.location ? ` Location: ${rec.location}.` : ""}${rec.notes ? ` Notes: ${rec.notes}` : ""} Thanks! 💜`;
                            navigator.clipboard.writeText(text);
                            showToast("Day details copied ✓");
                          }}>
                          📋 Copy
                        </button>
                      </div>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </>
      )}
    </div>
  );
}

function RemindersTab({ schedule, setSchedule, breaks, terms, todos, setTodos, push, onEnablePush, showToast }) {
  const [expanded, setExpanded] = useState(null); // reminder key or null
  const todayDS = toDS(new Date());

  const reminders = useMemo(
    () => [...buildReminders(schedule, breaks, terms), ...buildTodoReminders(todos)]
      .filter((r) => r.eventDate >= todayDS)
      .sort((a, b) => a.fireAt - b.fireAt),
    [schedule, breaks, terms, todos, todayDS]
  );

  const toggleTodoRemind = (r) => setTodos(prev => prev.map(t => t.id === r.todoId ? { ...t, remind: t.remind === false } : t));

  // Write an edit back onto the schedule record the reminder came from.
  const patch = (r, p) => {
    const rec = schedule[r.eventDate] || {};
    if (r.kind === "camp") {
      setSchedule({ ...schedule, [r.eventDate]: { ...rec, campReminder: { ...(rec.campReminder || {}), ...p } } });
    } else {
      const dd = rec.dayData || {};
      const acts = (dd.activities || []).map((a, i) => {
        const id = a.id || `${i}`;
        return id === r.actId ? { ...a, reminder: { ...(a.reminder || {}), ...p } } : a;
      });
      setSchedule({ ...schedule, [r.eventDate]: { ...rec, dayData: { ...dd, activities: acts } } });
    }
  };

  // ── Notification status card ────────────────────────────────────────────
  const StatusCard = () => {
    let msg, action = null, tone = COLORS.muted;
    if (!push.supported) {
      msg = "This browser can't do push notifications. On iPhone, open MumHQ from your Home Screen icon (not Safari) to enable them.";
    } else if (push.needsInstall) {
      msg = "On iPhone, notifications only work when MumHQ is added to your Home Screen. Tap Share → Add to Home Screen, then open MumHQ from the new icon and come back here.";
    } else if (push.permission === "denied") {
      msg = "Notifications are blocked. Turn them back on for MumHQ in your device settings, then reload.";
    } else if (push.subscribed) {
      msg = "Reminders are on for this device. 🔔";
      tone = COLORS.sage;
    } else {
      msg = "Turn on notifications to get reminders sent to this device — even when MumHQ is closed.";
      action = (
        <button style={{ ...S.btnPrimary, marginTop: 10 }} onClick={onEnablePush}>Turn on reminders</button>
      );
    }
    return (
      <div style={S.card}>
        <div style={S.sectionTitle}><Icon name="bell" size={16} color={BRAND.slate} style={{ marginRight: 8, verticalAlign: "-2px" }} />Notifications</div>
        <p style={{ fontSize: 13, color: tone, lineHeight: 1.5, margin: 0 }}>{msg}</p>
        {action}
      </div>
    );
  };

  return (
    <div>
      <StatusCard />

      <div style={S.card}>
        <div style={S.sectionTitle}>Upcoming reminders</div>
        <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 14, lineHeight: 1.5 }}>
          Holiday camps remind you at 8pm the night before. Term-time and weekend activities remind you 1 hour before they start. To-dos with a due date remind you at 9am on the day. Tap any activity reminder to change the time, add an address or notes, or switch it off.
        </p>

        {reminders.length === 0 ? (
          <p style={S.italic}>No upcoming reminders yet. Add activity days in the Schedule tab and they'll appear here.</p>
        ) : (
          reminders.map((r) => {
            const isOpen = expanded === r.key;
            if (r.kind === "todo") {
              return (
                <div key={r.key} style={{ border: `1px solid ${BRAND.border}`, borderRadius: 12, marginBottom: 10, padding: "12px 14px", display: "flex", alignItems: "center", gap: 10, opacity: r.enabled ? 1 : 0.55 }}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 600, color: BRAND.navy, fontSize: 13 }}>{r.summary}</div>
                    <div style={{ fontSize: 12, color: BRAND.muted, marginTop: 3 }}>{r.enabled ? fmtWhen(r.fireAt) : "Off"} · <span style={{ fontStyle: "italic" }}>{r.ruleLabel}</span></div>
                  </div>
                  <button onClick={() => toggleTodoRemind(r)} style={{ padding: "6px 12px", borderRadius: 20, border: `1.5px solid ${r.enabled ? COLORS.sageMid : BRAND.border}`, background: r.enabled ? COLORS.sageLight : BRAND.white, color: r.enabled ? COLORS.sage : BRAND.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}>{r.enabled ? "On" : "Off"}</button>
                </div>
              );
            }
            return (
              <div key={r.key} style={{ border: `1px solid ${BRAND.border}`, borderRadius: 12, marginBottom: 10, overflow: "hidden", opacity: r.enabled ? 1 : 0.55 }}>
                <div style={{ padding: "12px 14px", cursor: "pointer", display: "flex", alignItems: "center", gap: 10 }} onClick={() => setExpanded(isOpen ? null : r.key)}>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, color: COLORS.plumMid, fontSize: 13 }}>{stripEmoji(r.title)}</div>
                    <div style={{ fontSize: 12, color: BRAND.muted, marginTop: 3 }}>
                      {r.enabled ? fmtWhen(r.fireAt) : "Off"} · <span style={{ fontStyle: "italic" }}>{r.ruleLabel}</span>
                    </div>
                    {r.address && <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 2 }}>{r.address}</div>}
                  </div>
                  <span style={{ color: BRAND.muted, fontSize: 16 }}>{isOpen ? "▲" : "▼"}</span>
                </div>

                {isOpen && (
                  <div style={{ borderTop: `1px solid ${BRAND.border}`, padding: 14, background: BRAND.cream }}>
                    <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 12 }}>
                      <button
                        style={{ padding: "7px 14px", borderRadius: 20, border: `1.5px solid ${r.enabled ? COLORS.sageMid : BRAND.border}`, background: r.enabled ? COLORS.sageLight : BRAND.white, color: r.enabled ? COLORS.sage : BRAND.muted, fontSize: 12, fontWeight: 700, cursor: "pointer" }}
                        onClick={() => patch(r, { enabled: !r.enabled })}>
                        {r.enabled ? "✓ On" : "Off"}
                      </button>
                    </div>
                    <label style={S.label}>Reminder time {r.kind === "camp" ? "(night before)" : "(on the day)"}</label>
                    <input type="time" style={{ ...S.input, marginBottom: 12 }} value={r.time}
                      onChange={(e) => patch(r, { time: e.target.value })} />
                    <label style={S.label}>Address</label>
                    <input style={{ ...S.input, marginBottom: 12 }} placeholder="Venue / address" value={r.address}
                      onChange={(e) => patch(r, { address: e.target.value })} />
                    <label style={S.label}>Notes</label>
                    <input style={S.input} placeholder="e.g. packed lunch, kit needed…" value={r.notes}
                      onChange={(e) => patch(r, { notes: e.target.value })} />
                    <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 10 }}>
                      Reset to the default time by clearing it and reopening.
                    </div>
                  </div>
                )}
              </div>
            );
          })
        )}
      </div>
    </div>
  );
}

function MumHQ({ user, onSignOut }) {
  const [tab, setTab] = useState(0);
  const [profilePanel, setProfilePanel] = useState(null); // null | "children" | "breaks"
  const [showLegal, setShowLegal] = useState(false);
  const [children, setChildren] = useState([]);
  const [breaks, setBreaks] = useState([]);
  const [terms, setTerms] = useState([]);
  const [schedule, setSchedule] = useState({});
  const [childcare, setChildcare] = useState({});
  const [helpers, setHelpers] = useState([]);
  const [favourites, setFavourites] = useState({});
  const [reviews, setReviews] = useState({}); // { campId: [{ rating, body, author, date }] }
  const [schoolEvents, setSchoolEvents] = useState({});
  const [savedLists, setSavedLists] = useState([]);
  const [todos, setTodos] = useState([]);
  const [toast, setToast] = useState("");
  const [dataLoaded, setDataLoaded] = useState(false);
  const [push, setPush] = useState({ supported: false, needsInstall: false, permission: "default", subscribed: false });

  // ── Load this user's data from Supabase on sign-in ──────────────────────
  useEffect(() => {
    if (!user) { setDataLoaded(true); return; }
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase
        .from("family_data")
        .select("*")
        .eq("user_id", user.id)
        .maybeSingle();

      if (cancelled) return;

      if (error) {
        showToast("Couldn't load your data — check your connection");
      } else if (data) {
        // Trim names saved before trimming existed.
        setChildren((data.children || []).map(c => ({ ...c, name: (c.name || "").trim() })));
        setBreaks(data.breaks || []);
        setTerms(data.terms || []);
        setSchedule(data.schedule || {});
        setChildcare(data.childcare || {});
        setHelpers(data.helpers || []);
        setFavourites(data.favourites || {});
        setSchoolEvents(data.school_events || {});
        setSavedLists(data.saved_lists || []);
        setTodos(data.todos || []);
      } else {
        await supabase.from("family_data").insert({ user_id: user.id });
      }

      const { data: reviewRows } = await supabase.from("camp_reviews").select("*");
      if (reviewRows && !cancelled) {
        const grouped = {};
        reviewRows.forEach(r => {
          grouped[r.camp_id] = grouped[r.camp_id] || [];
          grouped[r.camp_id].push({ id: r.id, userId: r.user_id, rating: r.rating, body: r.body, author: r.author_name, date: new Date(r.created_at).toLocaleDateString("en-GB") });
        });
        setReviews(grouped);
      }

      if (!cancelled) setDataLoaded(true);
    })();
    return () => { cancelled = true; };
  }, [user?.id]);

  // ── Auto-save private family data back to Supabase, debounced ──────────
  const saveTimer = useRef(null);
  useEffect(() => {
    if (!dataLoaded || !user) return;
    if (saveTimer.current) clearTimeout(saveTimer.current);
    saveTimer.current = setTimeout(async () => {
      const { error } = await supabase.from("family_data").upsert({
        user_id: user.id,
        children, breaks, terms, schedule, childcare, helpers, favourites,
        school_events: schoolEvents, saved_lists: savedLists, todos,
      });
      if (error) showToast("Couldn't save changes — check your connection");
    }, 1200);
    return () => clearTimeout(saveTimer.current);
  }, [children, breaks, terms, schedule, childcare, helpers, favourites, schoolEvents, savedLists, todos, dataLoaded]);

  const showToast = useCallback((msg) => { setToast(msg); setTimeout(() => setToast(""), 2500); }, []);

  // ── Web Push: detect capability & existing subscription ────────────────
  const VAPID_PUBLIC = process.env.REACT_APP_VAPID_PUBLIC_KEY || "";
  useEffect(() => {
    const supported = typeof navigator !== "undefined" && "serviceWorker" in navigator && typeof window !== "undefined" && "PushManager" in window && "Notification" in window;
    const standalone = typeof window !== "undefined" && ((window.matchMedia && window.matchMedia("(display-mode: standalone)").matches) || window.navigator.standalone === true);
    const isIOS = typeof navigator !== "undefined" && /iphone|ipad|ipod/i.test(navigator.userAgent);
    const needsInstall = isIOS && !standalone; // iOS only exposes push to Home-Screen PWAs
    const permission = typeof Notification !== "undefined" ? Notification.permission : "default";
    setPush((p) => ({ ...p, supported, needsInstall, permission }));
    if (supported && navigator.serviceWorker && navigator.serviceWorker.ready) {
      navigator.serviceWorker.ready
        .then((reg) => reg.pushManager.getSubscription())
        .then((sub) => setPush((p) => ({ ...p, subscribed: !!sub })))
        .catch(() => {});
    }
  }, []);

  // Push the current upcoming reminders up to Supabase so the scheduled
  // function can deliver them. Best-effort: silently no-ops if the reminders
  // table doesn't exist yet or the network is down.
  const syncReminders = useCallback(async () => {
    if (!user || !push.subscribed) return;
    try {
      const now = Date.now();
      const horizon = now + 1000 * 60 * 60 * 24 * 120; // ~4 months ahead
      const rows = [...buildReminders(schedule, breaks, terms), ...buildTodoReminders(todos)]
        .filter((r) => r.enabled && r.fireAt.getTime() >= now && r.fireAt.getTime() <= horizon)
        .map((r) => ({
          user_id: user.id,
          dedupe_key: r.key,
          title: r.title,
          body: [r.summary, r.address, r.notes].filter(Boolean).join(" · ").slice(0, 300),
          url: "/",
          fire_at: r.fireAt.toISOString(),
          enabled: true,
        }));
      await supabase.from("reminders").delete().eq("user_id", user.id).is("sent_at", null);
      if (rows.length) await supabase.from("reminders").upsert(rows, { onConflict: "user_id,dedupe_key" });
    } catch (_e) { /* table not set up yet — ignore */ }
  }, [user, push.subscribed, schedule, breaks, terms, todos]);

  const enablePush = useCallback(async () => {
    if (!VAPID_PUBLIC) { showToast("Reminders aren't configured yet"); return; }
    try {
      const permission = await Notification.requestPermission();
      setPush((p) => ({ ...p, permission }));
      if (permission !== "granted") { showToast("Notifications weren't allowed"); return; }
      const reg = await navigator.serviceWorker.ready;
      let sub = await reg.pushManager.getSubscription();
      if (!sub) {
        sub = await reg.pushManager.subscribe({ userVisibleOnly: true, applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC) });
      }
      const j = sub.toJSON();
      const { error } = await supabase.from("push_subscriptions").upsert(
        { user_id: user.id, endpoint: sub.endpoint, p256dh: j.keys.p256dh, auth: j.keys.auth },
        { onConflict: "endpoint" }
      );
      if (error) { showToast("Couldn't save your subscription"); return; }
      setPush((p) => ({ ...p, subscribed: true }));
      showToast("Reminders switched on 🔔");
    } catch (_e) {
      showToast("Couldn't turn on reminders");
    }
  }, [VAPID_PUBLIC, user, showToast]);

  // Keep the reminders table in step with the schedule (debounced), once opted in.
  const remTimer = useRef(null);
  useEffect(() => {
    if (!dataLoaded || !user || !push.subscribed) return;
    if (remTimer.current) clearTimeout(remTimer.current);
    remTimer.current = setTimeout(() => { syncReminders(); }, 1500);
    return () => clearTimeout(remTimer.current);
  }, [schedule, breaks, terms, todos, push.subscribed, dataLoaded, user, syncReminders]);

  const tabs = [
    { label: "Find" },
    { label: "Schedule" },
    { label: "Calendar" },
    { label: "Share" },
    { label: "Lists" },
    { label: "Alerts" },
  ];

  const renderTab = () => {
    if (showLegal) return <LegalTab children={children} setChildren={setChildren} helpers={helpers} setHelpers={setHelpers} setSchedule={setSchedule} setChildcare={setChildcare} setBreaks={setBreaks} setTerms={setTerms} showToast={showToast} user={user} onSignOut={onSignOut} />;
    if (profilePanel === "children") return <ChildrenTab children={children} setChildren={setChildren} helpers={helpers} setHelpers={setHelpers} showToast={showToast} />;
    if (profilePanel === "breaks")   return <BreaksTab breaks={breaks} setBreaks={setBreaks} terms={terms} setTerms={setTerms} />;
    // tab 0 (Find) is rendered separately below and stays mounted, so the postcode
    // and results survive a trip to Breaks, Profiles or another tab.
    if (tab === 0) return null;
    if (tab === 1) return <ScheduleTab children={children} breaks={breaks} terms={terms} schedule={schedule} setSchedule={setSchedule} childcare={childcare} setChildcare={setChildcare} helpers={helpers} showToast={showToast} />;
    if (tab === 2) return <CalendarTab breaks={breaks} schedule={schedule} childcare={childcare} schoolEvents={schoolEvents} setSchoolEvents={setSchoolEvents} showToast={showToast} />;
    if (tab === 3) return <ShareTab breaks={breaks} terms={terms} schedule={schedule} children={children} childcare={childcare} helpers={helpers} showToast={showToast} />;
    if (tab === 4) return <ListsTab showToast={showToast} savedLists={savedLists} setSavedLists={setSavedLists} todos={todos} setTodos={setTodos} helpers={helpers} />;
    if (tab === 5) return <RemindersTab schedule={schedule} setSchedule={setSchedule} breaks={breaks} terms={terms} todos={todos} setTodos={setTodos} push={push} onEnablePush={enablePush} showToast={showToast} />;
  };

  return (
    <div style={S.app}>
      <header style={S.header}>
        <div style={S.logo}>
          <img src="/apple-touch-icon.png" alt="MumHQ"
            style={{ width: 48, height: 48, objectFit: "cover" }}
            onError={e => { e.target.style.display = "none"; e.target.parentNode.innerHTML = "🏡"; }} />
        </div>
        <div style={{ flex: 1 }}>
          <h1 style={S.appName}>MumHQ</h1>
          <p style={S.tagline}>Family life, sorted.</p>
        </div>
        <div style={{ display: "flex", flexDirection: "column", gap: 6, alignItems: "flex-end" }}>
          <button onClick={() => { setShowLegal(false); setProfilePanel(profilePanel === "children" ? null : "children"); }}
            style={{ padding: "6px 12px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: profilePanel === "children" ? BRAND.white : "rgba(255,255,255,0.15)", color: profilePanel === "children" ? BRAND.navy : BRAND.white, display: "flex", alignItems: "center", gap: 6 }}>
            Profiles
            {children.length > 0 && <span style={{ background: BRAND.sage, color: BRAND.white, borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{children.length}</span>}
          </button>
          <button onClick={() => { setShowLegal(false); setProfilePanel(profilePanel === "breaks" ? null : "breaks"); }}
            style={{ padding: "6px 12px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 12, fontWeight: 700, background: profilePanel === "breaks" ? BRAND.white : "rgba(255,255,255,0.15)", color: profilePanel === "breaks" ? BRAND.navy : BRAND.white, display: "flex", alignItems: "center", gap: 6 }}>
            Breaks
            {breaks.filter(b => b.name && b.startDate && b.endDate).length > 0 && <span style={{ background: BRAND.sage, color: BRAND.white, borderRadius: 10, padding: "1px 7px", fontSize: 11 }}>{breaks.filter(b => b.name && b.startDate && b.endDate).length}</span>}
          </button>
          <button onClick={onSignOut}
            style={{ padding: "4px 10px", borderRadius: 20, border: "none", cursor: "pointer", fontSize: 11, fontWeight: 600, background: "transparent", color: "rgba(255,255,255,0.6)" }}>
            Sign out
          </button>
        </div>
      </header>

      {profilePanel ? (
        <div style={{ ...S.nav, alignItems: "center", padding: "0 16px" }}>
          <button style={{ ...S.navBtn(false), color: BRAND.slate, fontWeight: 700 }} onClick={() => setProfilePanel(null)}>← Back</button>
          <span style={{ fontSize: 13, fontWeight: 700, color: BRAND.navy, marginLeft: 4 }}>{profilePanel === "children" ? "Profiles" : "Breaks"}</span>
        </div>
      ) : (
        <nav style={S.nav}>
          {tabs.map((t, i) => <button key={i} style={S.navBtn(!showLegal && tab === i)} onClick={() => { setTab(i); setShowLegal(false); }}>{t.label}</button>)}
        </nav>
      )}

      <main style={S.main}>
        {!showLegal && !profilePanel && tab === 1 && holidayPeriods(breaks, terms).length > 0 && (
          <ChildcareSummaryBar childcare={childcare} breaks={breaks} terms={terms} schedule={schedule} children={children} />
        )}
        {renderTab()}
        <div style={{ display: !showLegal && !profilePanel && tab === 0 ? "block" : "none" }}>
          <SearchTab children={children} breaks={breaks} terms={terms} schedule={schedule} setSchedule={setSchedule} favourites={favourites} setFavourites={setFavourites} reviews={reviews} setReviews={setReviews} showToast={showToast} user={user} onOpenBreaks={() => { setShowLegal(false); setProfilePanel("breaks"); }} />
        </div>
      </main>

      <footer style={S.footer}>
        <button style={S.footerBtn} onClick={() => { setShowLegal(s => !s); setProfilePanel(null); }}>
          {showLegal ? "← Back to app" : "Legal & Privacy"}
        </button>
      </footer>

      {toast && <div style={S.toast}>{toast}</div>}
    </div>
  );
}

// ─── Calendar helpers ─────────────────────────────────────────────────────────
const MONTHS = ["January","February","March","April","May","June","July","August","September","October","November","December"];
const WEEKDAYS = ["Mon","Tue","Wed","Thu","Fri","Sat","Sun"];

function getCalendarDays(year, month) {
  const first = new Date(year, month, 1);
  const last = new Date(year, month + 1, 0);
  const startDow = (first.getDay() + 6) % 7; // Mon=0
  const days = [];
  for (let i = 0; i < startDow; i++) days.push(null);
  for (let d = 1; d <= last.getDate(); d++) days.push(new Date(year, month, d));
  return days;
}

function isSameDay(a, b) {
  return a && b && a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth() && a.getDate() === b.getDate();
}

function getDayEvents(date, schedule, childcare, schoolEvents) {
  if (!date) return [];
  const ds = toDS(date);
  const events = [];
  const rec = schedule[ds] || {};
  const cc = childcare[ds] || {};
  if (rec.dayType === "activity") {
    (rec.dayData?.activities || []).forEach(a => events.push({ type: "activity", label: a.name, color: COLORS.sage }));
  } else if (rec.dayType === "holiday") {
    events.push({ type: "holiday", label: "Holiday", color: COLORS.plumMid });
  } else if (rec.dayType === "home") {
    events.push({ type: "home", label: "At home", color: "#8B5000" });
  }
  if (cc.type) {
    const ct = COVERAGE_TYPES.find(t => t.id === cc.type);
    if (ct) events.push({ type: "childcare", label: ct.label, color: ct.color });
  }
  (schoolEvents[ds] || []).forEach(e => events.push({ type: "school", label: e.title, color: COLORS.plumLight }));
  return events;
}

function isInBreak(date, breaks) {
  return breaks.filter(b => b.startDate && b.endDate).some(b => {
    const d = date.getTime();
    return d >= new Date(b.startDate).getTime() && d <= new Date(b.endDate).getTime();
  });
}

// ─── TAB: School Calendar ─────────────────────────────────────────────────────

// ─── LIST TEMPLATES ───────────────────────────────────────────────────────────
const LIST_TEMPLATES = {
  lifeadmin: {
    label: "Life Admin",
    sections: [
      { title: "Health & Medical", items: ["Register children with a GP","Book dental check-ups","Book eye tests","Check vaccinations are up to date","Order repeat prescriptions","Book flu jabs","Update the red book / health records"] },
      { title: "School & Childcare", items: ["Note this year's term dates","School application / deadline","Reconfirm Tax-Free Childcare (every 3 months)","Apply for or renew free childcare hours","Order school uniform","Label uniform & kit","Update emergency contacts at school","Book parents' evening","Pay club / activity fees"] },
      { title: "Money & Bills", items: ["Check car insurance renewal date","Check home / contents insurance renewal","Review energy tariff & submit meter readings","Renew TV licence","Check council tax band / payments","Review mobile & broadband contracts","Check Child Benefit is being claimed","Review the childcare budget"] },
      { title: "Documents & ID", items: ["Check passports are in date","Renew passports","Check driving licence is in date","Sort wills & guardianship","Store birth certificates safely","Keep home & car documents together"] },
      { title: "Home & Car", items: ["Book boiler service","Book MOT","Book car service","Test smoke & CO alarms","Note bin collection schedule","Seasonal clothes / shoe swap"] },
      { title: "Annual & Seasonal", items: ["Book summer holiday camps","Plan half-term childcare","Christmas & birthday gift planning","Book a family holiday","Spring clean & declutter"] },
    ],
  },
  birthday: {
    label: "Birthday Party Planning List",
    sections: [
      { title: "Party Basics", items: ["Birthday child's name","Age turning","Party date","Start time","End time","Budget","Guest count","Indoor or outdoor","Weather backup plan (if outdoors)"] },
      { title: "Venue", items: ["Venue name","Venue address","Pay deposit","Final payment due","Parking arrangements","Kitchen/prep area","Seating available","Tables available","Cleaning requirements","Decoration restrictions","Access/setup time","Pack-down time"] },
      { title: "Theme", items: ["Party theme","Theme colours","Fancy dress code (optional)","Matching invitations","Matching decorations","Matching cake","Matching party bags","Matching games","Matching tableware"] },
      { title: "Guest List", items: ["Create guest list","Parents' contact details","Number of children","Number of adults","RSVP tracking","Dietary requirements","Allergies","Special needs/accessibility"] },
      { title: "Invitations", items: ["Send invitations","RSVP deadline","Reminder message","Directions/map","Parking instructions","Contact number"] },
      { title: "Birthday Cake", items: ["Order or bake cake","Cake topper","Birthday candles","Number candles","Matches/lighter","Cake knife","Cake plates/napkins","Cake forks/spoons","Storage before serving","Transport plan"] },
      { title: "Food", items: ["Pizza","Sandwiches","Hot food","Finger food","Fruit","Vegetables","Gluten-free options","Vegetarian options","Allergy-friendly options","Chips","Popcorn","Crackers","Cheese","Fruit skewers","Cookies","Pretzels","Tea","Coffee","Adult snacks","Lunch (if applicable)","Adult drinks"] },
      { title: "Drinks", items: ["Water","Juice boxes","Soft drinks","Cordial","Ice","Cups","Straws","Drink dispensers","Cooler"] },
      { title: "Tableware", items: ["Plates","Bowls","Cups","Napkins","Cutlery","Serving spoons","Serving tongs","Tablecloths","Disposable bags for rubbish"] },
      { title: "Decorations", items: ["Welcome sign","Balloons","Balloon arch","Door decorations","Banners","Streamers","Hanging decorations","Ceiling decorations","Wall decorations","Table centrepieces","Confetti","Fairy lights","Balloon pump","Helium (if needed)","Balloon weights","Balloon ribbon","Balloon glue dots"] },
      { title: "Furniture & Setup", items: ["Guest tables","Food table","Cake table","Gift table","Activity tables","Chairs","High chair (if needed)","Shade/gazebo (outdoors)","Picnic rugs (if needed)"] },
      { title: "Entertainment", items: ["Music playlist","Bluetooth speaker","Charging cable","Extension cords","Microphone (optional)"] },
      { title: "Games & Activities", items: ["Plan activities","Age-appropriate games","Prizes","Certificates","Game supplies","Craft station","Colouring sheets","Crayons","Stickers","Bubble machine","Bubbles","Sidewalk chalk","Sports equipment"] },
      { title: "Entertainment Hire", items: ["Jumping castle","Soft play","Petting zoo","Face painter","Balloon artist","Magician","Character entertainer","Photo booth","Photographer"] },
      { title: "Party Bags", items: ["Party bags","Toys","Stickers","Lollies","Bubbles","Pencils","Colouring book","Thank-you tag","Personalised labels"] },
      { title: "Gifts", items: ["Gift table","Gift box/basket","Card box","Pen for gift labels","Record who gave each gift","Thank-you notes"] },
      { title: "Photos", items: ["Camera","Phone fully charged","Power bank","Tripod","Group photo list","Cake photos","Decoration photos","Family photos","Video clips"] },
      { title: "Cleaning Supplies", items: ["Garbage bags","Recycling bags","Paper towels","Cleaning spray","Wet wipes","Hand sanitiser","Broom","Dustpan","Vacuum (if needed)","Mop","Gloves"] },
      { title: "Safety & First Aid", items: ["First aid kit","Band-aids","Ice packs","Allergy medication (if supplied by parents)","Sunscreen","Insect repellent","Emergency contacts"] },
      { title: "Equipment", items: ["Extension cords","Power boards","Chargers","Batteries","Scissors","Sticky tape","Blu Tack","Zip ties","String","Command hooks"] },
      { title: "Shopping", items: ["Food shopping list","Decoration shopping list","Party bag shopping list","Drinks shopping list","Ice purchase","Fresh food pickup","Cake pickup"] },
      { title: "One Week Before", items: ["Confirm RSVPs","Confirm venue","Confirm entertainers","Confirm cake","Buy non-perishable food","Buy decorations","Prepare party bags","Print signs","Charge electronics"] },
      { title: "One Day Before", items: ["Buy fresh food","Pick up cake","Inflate balloons","Pack car","Prepare decorations","Chill drinks","Charge phone","Review checklist"] },
      { title: "Party Day — Before Guests Arrive", items: ["Set up decorations","Set up tables","Set up food","Set up drinks","Set up games","Set up cake table","Prepare music","Put candles on cake","Test speakers","Put out party bags"] },
      { title: "Party Day — During", items: ["Welcome guests","Take photos","Run games","Serve food","Serve cake","Sing Happy Birthday","Open presents (if planned)","Hand out party bags"] },
      { title: "Party Day — After", items: ["Pack gifts","Pack leftovers","Clean venue","Return hired items","Check for forgotten belongings","Thank helpers"] },
      { title: "After the Party", items: ["Send thank-you messages","Return borrowed items","Leave venue review","Upload/share photos","Store reusable decorations","Record ideas for next year"] },
    ],
  },
  packing: {
    label: "Family Packing List", columns: true, defaultColumns: ["Mum","Dad","Child 1","Child 2"],
    sections: [
      { title: "Travel Essentials", items: ["Suitcases","Carry-on bags","Backpacks","Packing cubes","Laundry bag","Day bag","Reusable shopping bag","Luggage tags","Travel locks","Neck pillows","Eye masks","Ear plugs","Reusable water bottles"] },
      { title: "Travel Documents", items: ["Passports","Visas (if required)","Driver's licence","Travel insurance documents","Flight tickets/boarding passes","Hotel confirmations","Car hire confirmation","Cruise documents","Itinerary","Emergency contacts","Copies of important documents","Health insurance cards","Cash","Credit/debit cards","Foreign currency"] },
      { title: "Clothing — Everyday", items: ["Underwear","Socks","Bras","Pyjamas","T-shirts","Shorts","Pants/jeans","Dresses","Skirts","Jumpers","Jackets","Activewear","Swim cover-up"] },
      { title: "Clothing — Shoes", items: ["Walking shoes","Sandals","Thongs/Flip-flops","Dress shoes","Trainers"] },
      { title: "Clothing — Accessories", items: ["Hat","Sunglasses","Belt","Jewellery","Watch","Hair accessories"] },
      { title: "Cold Weather Items", items: ["Coat","Thermals","Beanie","Gloves","Scarf","Thick socks","Waterproof jacket","Snow boots"] },
      { title: "Summer & Beach", items: ["Swimsuits","Board shorts","Beach towel","Beach bag","Flip-flops","Sunscreen","Aloe vera gel","Beach umbrella","Goggles","Snorkel","Water shoes","Inflatable toys","Pool toys"] },
      { title: "Snow Holiday", items: ["Ski jacket","Ski pants","Thermals","Snow boots","Ski socks","Gloves","Neck warmer","Goggles","Helmet","Skis/snowboard (if bringing)","Lift passes"] },
      { title: "Toiletries", items: ["Toothbrush","Toothpaste","Dental floss","Mouthwash","Shampoo","Conditioner","Body wash","Soap","Face cleanser","Moisturiser","Deodorant","Razor","Shaving cream","Hairbrush","Comb","Hair ties","Hair products","Lip balm","Makeup","Makeup remover","Cotton pads","Cotton buds","Nail clippers","Tweezers","Hand sanitiser"] },
      { title: "Health & First Aid", items: ["Prescription medication","Pain relief","Band-aids","Antiseptic cream","Thermometer","Motion sickness tablets","Allergy medication","Insect repellent","Bite cream","Sunscreen","Vitamins","First aid kit"] },
      { title: "Baby & Toddler", items: ["Nappies","Baby wipes","Nappy cream","Changing mat","Bottles","Formula","Baby food","Bibs","Dummy/Pacifier","Comfort toy","Baby blanket","Muslins","Pram","Baby carrier","Portable highchair","Portable cot","Baby monitor","Sterilising equipment"] },
      { title: "Kids Essentials", items: ["Favourite toy","Books","Colouring books","Crayons","Activity books","Travel games","Headphones","Comfort blanket","Stuffed toy","Water bottle","Snacks"] },
      { title: "Electronics", items: ["Mobile phones","Tablets","Laptops","Smartwatch","Camera","E-reader","Bluetooth speaker","Drone (if applicable)"] },
      { title: "Chargers", items: ["Phone chargers","Tablet chargers","Laptop charger","Camera charger","Smartwatch charger","Power bank","Charging cables","Spare cables"] },
      { title: "Travel Electronics", items: ["Universal travel adaptor","Power board","Extension lead","USB charging hub","Car charger"] },
      { title: "Food & Snacks", items: ["Snacks","Lunch boxes","Drink bottles","Baby snacks","Coffee/tea supplies","Reusable cutlery","Zip-lock bags","Cooler bag"] },
      { title: "Car Travel", items: ["Car seats","Booster seats","Phone mount","Car charger","Maps (offline if needed)","Blanket","Pillows","Wet wipes","Bin bags","Entertainment for kids"] },
      { title: "Outdoor Equipment", items: ["Picnic rug","Camping chairs","Torch","Lantern","Batteries","Insect spray","Rope","Multi-tool","Umbrella","Rain ponchos"] },
      { title: "Laundry", items: ["Laundry detergent","Stain remover","Clothes pegs","Travel clothesline","Dirty laundry bag","Fabric spray"] },
      { title: "Accommodation Extras", items: ["Door stop","Extension cord","Night light","Dishwashing tablets (if needed)","Coffee pods (if needed)"] },
      { title: "Pet Essentials", items: ["Pet food","Bowls","Lead","Harness","Waste bags","Pet bed","Toys","Medication","Vaccination records"] },
      { title: "Special Occasion Items", items: ["Birthday gifts","Cards","Wrapping paper","Decorations","Special outfit"] },
      { title: "Before You Leave Home", items: ["Lock all doors and windows","Turn off appliances","Empty rubbish bins","Water plants","Arrange pet care","Set security alarm","Hold mail (if required)","Check passports","Check wallets","Check keys","Check chargers","Empty fridge of perishables","Set air conditioning/heating","Take out recycling","Confirm transport to airport"] },
    ],
  },
  flight: {
    label: "Flight Packing List", columns: true, defaultColumns: ["Mum","Dad","Child 1","Child 2"],
    sections: [
      { title: "Travel Documents", items: ["Passports","Boarding passes","Driver's licence/Photo ID","Visas (if required)","Travel insurance documents","Travel itinerary","Hotel booking confirmations","Car hire confirmation","Emergency contact details","Wallet","Credit/debit cards","Cash","Foreign currency","Pen (for customs forms)"] },
      { title: "Electronics", items: ["Mobile phone","Tablet","Laptop","E-reader","Smartwatch","Camera","Phone charger","Charging cables","Power bank","Universal travel adaptor","Headphones","Earbuds","AirTag or luggage tracker (optional)"] },
      { title: "Clothing & Comfort", items: ["Lightweight jumper or hoodie","Jacket","Scarf or wrap","Spare underwear","Spare socks","Change of clothes","Comfortable shoes","Compression socks (long flights)","Eye mask","Neck pillow","Ear plugs","Blanket (if preferred)"] },
      { title: "Snacks & Drinks", items: ["Empty reusable water bottle","Water (after security)","Healthy snacks","Muesli bars","Crackers","Nuts (where permitted)","Fruit","Lollies for take-off and landing","Gum or mints","Baby snacks","Formula (if required)","Baby food pouches","Reusable cutlery"] },
      { title: "Toiletries", items: ["Toothbrush","Toothpaste","Lip balm","Hand sanitiser","Hand cream","Face wipes","Facial moisturiser","Deodorant","Tissues","Wet wipes","Hairbrush or comb","Hair ties","Glasses","Contact lenses","Contact lens solution","Spare contacts"] },
      { title: "Health & Medication", items: ["Prescription medication","Pain relief","Motion sickness tablets","Allergy medication","Asthma inhaler","Vitamins","Band-aids","Antibacterial wipes","Small first aid kit"] },
      { title: "Baby Essentials", items: ["Nappies","Baby wipes","Nappy bags","Changing mat","Dummy/Pacifier","Bottles","Formula","Baby food","Bib","Muslin cloths","Comfort blanket","Favourite toy","Spare outfit","Baby carrier","Lightweight blanket"] },
      { title: "Kids Entertainment", items: ["Tablet","Headphones","Downloaded movies/shows","Colouring book","Crayons","Activity book","Sticker book","Puzzle book","Card games","Travel games","Favourite toy","Soft toy","Books","Fidget toys"] },
      { title: "Sleep & Comfort", items: ["Blanket","Neck pillow","Eye mask","Ear plugs","Hoodie","Favourite teddy","Comfort blanket","Sleep aid for children (if normally used)"] },
      { title: "In-Flight Essentials", items: ["Sunglasses","Reading glasses","Notebook","Pen","Book or magazine","Travel pillow","Reusable shopping bag","Zip-lock bags","Laundry bag for dirty clothes"] },
      { title: "Before Boarding", items: ["Check passports are accessible","Download boarding passes","Download offline entertainment","Download offline maps","Charge all devices","Fill water bottle after security","Use the bathroom before boarding","Organise snacks","Keep medication within easy reach","Place entertainment items at the top of your bag"] },
      { title: "Before Landing", items: ["Complete customs/arrival forms","Pack away electronics","Check seat pocket","Check overhead locker","Collect headphones","Collect chargers","Collect children's toys","Refill water bottle if needed","Have passports ready","Have arrival documents ready"] },
    ],
  },
  roadtrip: {
    label: "Family Road Trip Packing List", columns: true, defaultColumns: ["Mum","Dad","Child 1","Child 2"],
    sections: [
      { title: "Vehicle Essentials", items: ["Driver's licence","Vehicle registration","Car insurance details","Roadside assistance membership","Vehicle manual","Spare car key","Sunglasses","Phone mount","Car charger","USB charging cables","Power bank","Offline maps downloaded","Navigation/GPS","Reusable shopping bags","Reusable water bottles"] },
      { title: "Car Safety & Emergency Kit", items: ["Spare tyre","Jack","Wheel brace","Tyre pressure gauge","Jumper leads","Torch","Spare batteries","Basic tool kit","Duct tape","Multi-tool","Tow rope","Emergency blanket","Rain poncho","Umbrella","Ice scraper (winter)","Fire extinguisher (optional)","Roadside emergency contact numbers"] },
      { title: "Travel Essentials", items: ["Wallet","Credit/debit cards","Cash","Driver's licence","Health insurance cards","Accommodation confirmations","Activity bookings","Emergency contacts","Itinerary"] },
      { title: "First Aid & Medication", items: ["Prescription medication","Pain relief","Motion sickness tablets","Allergy medication","Band-aids","Antiseptic cream","Thermometer","Insect repellent","Bite cream","Sunscreen","First aid kit"] },
      { title: "Baby Essentials", items: ["Nappies","Baby wipes","Nappy bags","Nappy cream","Portable changing mat","Bottles","Formula","Sterilised water","Baby food","Snacks","Muslin cloths","Dummy/Pacifier","Dummy clips","Comfort toy","Baby blanket","Spare clothes","Baby carrier","Pram","Portable highchair (if needed)","Portable cot (if required)"] },
      { title: "Kids Essentials", items: ["Favourite toy","Books","Colouring books","Crayons","Activity books","Sticker books","Card games","Travel games","Headphones","Tablet","Downloaded movies","Downloaded music","Soft toy","Blanket","Neck pillow","Water bottle"] },
      { title: "Food & Snacks", items: ["Water bottles","Cooler/esky","Ice bricks","Sandwiches","Fresh fruit","Crackers","Muesli bars","Popcorn","Trail mix","Cheese","Yoghurt pouches","Baby snacks","Baby food","Juice boxes","Reusable cutlery","Paper towels","Rubbish bags","Zip-lock bags"] },
      { title: "Picnic & Stopovers", items: ["Picnic blanket","Camping chairs","Fold-up table (optional)","Reusable plates","Cups","Cutlery","Napkins","Serving knife","Bottle opener","Coffee flask"] },
      { title: "Electronics", items: ["Mobile phones","Tablets","Camera","Smartwatch","Bluetooth speaker","Chargers","Charging cables","USB car charger","Power bank","Extension cord (for accommodation)"] },
      { title: "Comfort Items", items: ["Travel pillows","Blankets","Eye masks","Window shades","Travel fan","Cooling towels","Extra jumper","Spare shoes"] },
      { title: "Pet Essentials", items: ["Pet food","Water bowl","Lead","Harness","Waste bags","Blanket","Toys","Medication","Vaccination records"] },
      { title: "Road Trip Convenience Kit", items: ["Toilet paper","Baby wipes","Hand sanitiser","Paper towels","Plastic bags","Disposable gloves","Small towel","Change of clothes (easy to reach)","Spare shoes","Spare socks"] },
      { title: "Before You Leave", items: ["Check tyre pressure","Fill fuel tank","Check engine oil","Check coolant","Check windscreen washer fluid","Test lights","Charge all devices","Download maps","Download entertainment","Pack cooler with ice","Secure luggage","Install child seats correctly","Pack emergency kit","Lock house","Empty rubbish bins","Water plants","Arrange pet care (if needed)","Confirm accommodation bookings","Check weather forecast"] },
    ],
  },
  backtoschool: {
    label: "Back to School List", columns: true, defaultColumns: ["Child 1","Child 2"],
    sections: [
      { title: "School Bag Essentials", items: ["School backpack","Name label on backpack","Lunch box","Water bottle","Healthy snack (if permitted)","Pencil case (if required by school)","Reading book","Reading record/home-school diary","Homework folder","Library book (if needed)","Tissues (travel pack)","Spare hair ties (if needed)"] },
      { title: "School Uniform — Everyday", items: ["School polo shirts/shirts","School jumper/cardigan","Trousers","Shorts (summer)","Skirt","Pinafore dress","Summer dress (if applicable)","School socks","Tights","Belt (if needed)"] },
      { title: "School Uniform — Footwear", items: ["School shoes","Spare school shoes (optional)","Wellington boots (if required)"] },
      { title: "School Uniform — Outerwear", items: ["School coat","Waterproof jacket","Hat","Gloves","Scarf","Sun hat (summer)"] },
      { title: "PE Kit", items: ["PE T-shirt","PE shorts","Tracksuit bottoms","Hoodie/jumper (if required)","Trainers","PE socks","Drawstring PE bag","Swimming kit (if required)","Swimsuit or swim shorts","Towel","Goggles (if allowed)"] },
      { title: "Stationery", items: ["Pencil case","HB pencils","Coloured pencils","Eraser","Pencil sharpener","Ruler","Glue stick","Child-safe scissors (if requested)","Whiteboard pen (if requested)"] },
      { title: "Name Labelling", items: ["School uniform","Coat","Shoes","PE kit","Water bottle","Lunch box","Backpack","Pencil case","Reading folder","Hat","Gloves","Scarf"] },
      { title: "Lunch — Packed Lunch", items: ["Lunch box","Water bottle","Sandwiches/wraps","Fruit","Vegetables","Yoghurt","Healthy snack","Ice pack (if required)"] },
      { title: "Lunch — School Meals", items: ["Confirm meal booking","Check dietary requirements","Top up lunch account (if applicable)"] },
      { title: "Personal Care", items: ["Hand sanitiser (if permitted)","Pocket tissues","Lip balm (winter)","Spare underwear (Reception/KS1)","Spare socks","Spare change of clothes (Reception)","Hairbrush (if needed)"] },
      { title: "School Paperwork", items: ["Emergency contact details updated","Medical information updated","Allergy information provided","Photo permission forms","Local trip permission forms","School meal forms","Club registrations","Wraparound care bookings","ParentPay or school payment account set up","Reading diary signed","School calendar added to family calendar"] },
      { title: "Technology", items: ["Laptop or tablet (if required)","Device charger","Headphones (if requested)","Login details recorded","Educational apps downloaded (if required)"] },
      { title: "Classroom Supplies (If Requested)", items: ["Tissues","Baby wipes","Glue sticks","Dry whiteboard pens","Art apron","Apron for messy play (Reception)"] },
      { title: "Seasonal — Summer", items: ["Sun hat","Sunscreen (apply before school if allowed)","Refillable water bottle"] },
      { title: "Seasonal — Winter", items: ["Warm coat","Hat","Gloves","Scarf","Waterproof trousers (if required)","Wellington boots"] },
      { title: "School Travel — Walking", items: ["Hi-vis jacket (if needed)","Umbrella","Waterproof coat"] },
      { title: "School Travel — Car", items: ["Car seat or booster seat (where legally required)"] },
      { title: "School Travel — Bus", items: ["Bus pass (if applicable)"] },
      { title: "Home Preparation", items: ["Create a homework space","Organise school uniforms","Prepare lunch supplies","Stock healthy snacks","Set up school bag station","Prepare shoe storage area","Create a family calendar","Set up morning routine","Set up evening routine"] },
      { title: "The Night Before School", items: ["Lay out school uniform","Pack school bag","Pack PE kit","Pack reading book","Prepare lunch","Fill water bottle","Charge devices (if required)","Check weather forecast","Set alarm","Early bedtime"] },
      { title: "School Morning Checklist", items: ["Get dressed","Eat breakfast","Brush teeth","Brush hair","Apply sunscreen (summer)","Put on coat","Put on school shoes","Grab school bag","Grab lunch box","Grab water bottle","Grab PE kit (if needed)","Check reading book","Leave on time!"] },
      { title: "Weekly School Check", items: ["Wash school uniform","Wash PE kit","Refill lunch supplies","Check homework","Return library books","Recharge devices","Restock stationery","Empty school bag","Wash water bottle","Clean lunch box","Check school newsletters"] },
    ],
  },
  uniform: {
    label: "School Uniform List", columns: true, defaultColumns: ["Child 1","Child 2"],
    sections: [
      { title: "Everyday Uniform — Tops", items: ["School polo shirts","School shirts","Blouses","School jumper","School cardigan","School fleece (if applicable)","School blazer (if required)"] },
      { title: "Everyday Uniform — Bottoms", items: ["Trousers","Shorts","Skirts","Pinafore dresses","Summer dresses (gingham or school style)"] },
      { title: "Socks & Tights", items: ["School socks","Ankle socks (summer)","Knee-high socks (if required)","Tights","Spare socks","Spare tights"] },
      { title: "School Shoes", items: ["School shoes","Velcro school shoes (younger children)","Lace-up school shoes","Black trainers (if permitted)","Shoe polish","Spare shoelaces","Insoles (if required)"] },
      { title: "Outerwear", items: ["School coat","Waterproof jacket","Lightweight raincoat","Fleece jacket (if required)"] },
      { title: "Winter Accessories", items: ["Hat","Gloves","Scarf","Ear warmers"] },
      { title: "Summer Accessories", items: ["Sun hat","Cap (if permitted)","Sunglasses (school trips if allowed)"] },
      { title: "PE Kit — Clothing", items: ["PE T-shirt","PE shorts","Tracksuit bottoms","Leggings","Hoodie or sweatshirt","PE socks"] },
      { title: "PE Kit — Footwear", items: ["Trainers","Plimsolls (if required)"] },
      { title: "PE Kit — Extras", items: ["Drawstring PE bag","Waterproof bag for muddy clothes","Spare hair ties","Sweat towel"] },
      { title: "Swimming Kit (If Required)", items: ["Swimsuit","Swim shorts","Swimming towel","Goggles (if permitted)","Swim cap (if required)","Waterproof bag"] },
      { title: "School Accessories", items: ["School backpack","Lunch box","Water bottle","Book bag","Reading folder","Homework folder","Pencil case (if required)","Reading diary","Library bag (if required)"] },
      { title: "Name Labelling", items: ["Polo shirts","Shirts","Jumpers","Cardigans","Trousers","Shorts","Skirts","Dresses","Socks","Tights","Coat","Waterproof jacket","Hat","Gloves","Scarf","School shoes","Trainers","PE kit","Swimming kit","Backpack","Lunch box","Water bottle","Book bag","Reading folder","Pencil case"] },
      { title: "Spare Clothes (Recommended)", items: ["Spare underwear","Spare socks","Spare tights","Spare polo shirt","Spare trousers","Spare leggings","Spare jumper"] },
      { title: "Care & Maintenance", items: ["Wash uniform weekly","Wash PE kit weekly","Clean school shoes","Polish leather shoes","Replace worn socks","Replace tights","Check for missing name labels","Repair loose buttons","Repair hems","Remove stains promptly"] },
      { title: "Growth Check", items: ["Check trouser length","Check skirt length","Check jumper fit","Check coat still fits","Check shoe size","Check trainer size","Replace items that are too small"] },
      { title: "Replacement Shopping List", items: ["Extra polo shirts","Extra jumpers","Extra trousers","Extra skirts","Extra socks","Extra tights","Replacement shoes","Replacement trainers","New PE kit","New backpack","New lunch box","New water bottle"] },
      { title: "Termly Uniform Check", items: ["Uniform still fits","Shoes still fit","Trainers still fit","Coat still fits","PE kit complete","Swimming kit complete","Name labels still attached","Spare uniform available","Lunch box in good condition","Water bottle clean and leak-free","Backpack in good condition","Stock up on replacement items"] },
    ],
  },
  christmas: {
    label: "Ultimate Christmas Planning List",
    sections: [
      { title: "Christmas Countdown — October", items: ["Set Christmas budget","Decide where Christmas Day will be hosted","Book travel and accommodation","Book Santa experiences","Book Christmas events","Create gift list","Check Christmas decorations","Purchase wrapping supplies","Start gift shopping"] },
      { title: "Christmas Countdown — November", items: ["Continue Christmas shopping","Order personalised gifts","Order Christmas cards","Plan Christmas menu","Order Christmas ham/turkey (if required)","Purchase non-perishable groceries","Plan Elf activities (if applicable)","Put up Christmas tree","Decorate the house","Book grocery delivery slot"] },
      { title: "Christmas Countdown — December", items: ["Finish gift shopping","Wrap presents","Label presents","Write Christmas cards","Mail Christmas cards","Buy fresh groceries","Prepare desserts","Prepare freezer meals","Confirm Christmas Day guests","Prepare guest bedrooms","Charge cameras and devices","Prepare Christmas Eve boxes","Watch Christmas movies","Attend Christmas events"] },
      { title: "Christmas Events", items: ["Christmas lights drive","Christmas markets","Santa visit","School Christmas concert","Nursery Christmas concert","Carol service","Christmas church service","Work Christmas party","Family Christmas lunch","Friends Christmas catch-up","Christmas movie night","Christmas baking day","Gingerbread decorating","Christmas craft day","Christmas pyjama day","Christmas Eve traditions"] },
      { title: "Gift Planner — Immediate Family", items: ["Partner — gift ideas","Partner — purchased","Partner — wrapped","Child 1 — gift ideas","Child 1 — purchased","Child 1 — wrapped","Child 1 — batteries included (if required)","Child 2 — gift ideas","Child 2 — purchased","Child 2 — wrapped","Child 2 — batteries included","Baby — gift ideas","Baby — purchased","Baby — wrapped"] },
      { title: "Gift Planner — Parents & In-Laws", items: ["Mum — gift ideas","Mum — purchased","Mum — wrapped","Dad — gift ideas","Dad — purchased","Dad — wrapped","Mother-in-law — gift ideas","Mother-in-law — purchased","Mother-in-law — wrapped","Father-in-law — gift ideas","Father-in-law — purchased","Father-in-law — wrapped"] },
      { title: "Gift Planner — Extended Family & Friends", items: ["Grandparents — gift ideas","Grandparents — purchased","Grandparents — wrapped","Brother — gift ideas","Brother — purchased","Brother — wrapped","Sister — gift ideas","Sister — purchased","Sister — wrapped","Nieces & Nephews — gift ideas","Nieces & Nephews — purchased","Nieces & Nephews — wrapped","Friends — gift ideas","Friends — purchased","Friends — wrapped"] },
      { title: "Gift Planner — Teachers & Others", items: ["Teacher gifts","Teaching assistant gifts","Nursery staff gifts","Childminder gifts","Coach/instructor gifts"] },
      { title: "Gift Planner — Pets", items: ["Christmas treats","Toys","Stocking"] },
      { title: "Stocking Fillers", items: ["Chocolates","Books","Puzzles","Socks","Bath products","Stationery","Hair accessories","Toys","Gift cards","Snacks"] },
      { title: "Gift Wrapping Supplies", items: ["Wrapping paper","Gift bags","Gift boxes","Ribbon","Bows","Gift tags","Sticky tape","Scissors","Tissue paper","Name labels","Marker pen"] },
      { title: "Christmas Menu — Christmas Eve", items: ["Breakfast","Lunch","Dinner","Dessert","Drinks"] },
      { title: "Christmas Breakfast", items: ["Croissants","Pancakes","Bacon","Eggs","Fruit","Juice","Coffee","Tea"] },
      { title: "Christmas Lunch — Main", items: ["Turkey","Ham","Beef","Vegetarian option","Roast potatoes","Carrots","Parsnips","Brussels sprouts","Broccoli","Cauliflower","Peas"] },
      { title: "Christmas Lunch — Sides", items: ["Stuffing","Yorkshire puddings","Gravy","Cranberry sauce","Bread sauce","Pigs in blankets"] },
      { title: "Christmas Desserts", items: ["Christmas pudding","Trifle","Pavlova","Cheesecake","Mince pies","Christmas cake","Ice cream","Custard","Cream"] },
      { title: "Christmas Drinks", items: ["Water","Soft drinks","Juice","Tea","Coffee","Hot chocolate","Beer","Wine","Sparkling wine","Spirits","Children's drinks"] },
      { title: "Christmas Grocery — Fresh Produce", items: ["Potatoes","Carrots","Parsnips","Brussels sprouts","Broccoli","Cauliflower","Salad","Fresh herbs","Lemons","Fruit"] },
      { title: "Christmas Grocery — Meat & Dairy", items: ["Turkey","Ham","Beef","Bacon","Sausages","Milk","Butter","Cream","Cheese","Yoghurt","Eggs"] },
      { title: "Christmas Grocery — Bakery & Pantry", items: ["Bread","Bread rolls","Croissants","Mince pies","Flour","Sugar","Gravy","Stock cubes","Stuffing mix","Cranberry sauce","Cooking oil","Salt","Pepper","Herbs","Spices"] },
      { title: "Christmas Grocery — Frozen & Snacks", items: ["Ice","Frozen vegetables","Ice cream","Crisps","Crackers","Nuts","Chocolates","Popcorn","Biscuits","Lollies"] },
      { title: "Christmas Grocery — Drinks", items: ["Tea","Coffee","Soft drinks","Juice","Sparkling water","Beer","Wine","Champagne","Spirits"] },
      { title: "Christmas Baking", items: ["Gingerbread","Sugar cookies","Mince pies","Christmas cake","Shortbread","Rocky road","Fudge","Brownies"] },
      { title: "Decorations — Indoors", items: ["Christmas tree","Tree skirt","Lights","Baubles","Star/angel","Stockings","Garland","Wreath","Candles","Table centrepiece"] },
      { title: "Decorations — Outdoors", items: ["Outdoor lights","Extension cords","Garden decorations","Door wreath"] },
      { title: "Christmas Eve", items: ["Wrap final presents","Fill stockings","Prepare Santa snacks","Prepare reindeer food","Put presents under the tree","Chill drinks","Set Christmas table","Charge camera","Lay out Christmas pyjamas","Read Christmas stories"] },
      { title: "Christmas Day — Morning", items: ["Open stockings","Breakfast","Open presents","Family photos","Prepare lunch"] },
      { title: "Christmas Day — Afternoon & Evening", items: ["Christmas lunch","Dessert","Games","Walk","Relax","Christmas movie","Leftovers","Cheese board","Hot chocolate","Family games","Prepare Boxing Day leftovers"] },
      { title: "Boxing Day", items: ["Store decorations safely","Organise leftovers","Freeze extra food","Send thank-you messages","Return borrowed items","Donate unwanted packaging","Make returns or exchanges","Record gift ideas for next year","Back up Christmas photos"] },
    ],
  },
  foodshop: {
    label: "Food Shop List",
    sections: [
      { title: "Dairy & Eggs", items: ["Milk","Butter","Cheese","Yoghurt","Eggs","Cream","Cottage cheese","Cream cheese","Sour cream"] },
      { title: "Bread & Bakery", items: ["Bread","Wraps","Bagels","English muffins","Crumpets","Croissants","Bread rolls","Burger buns","Hot dog buns","Pitta bread"] },
      { title: "Fruit", items: ["Bananas","Apples","Oranges","Grapes","Strawberries","Blueberries","Pears","Lemons","Limes","Avocados"] },
      { title: "Vegetables", items: ["Potatoes","Sweet potatoes","Carrots","Broccoli","Cauliflower","Lettuce","Spinach","Tomatoes","Cucumbers","Capsicum/Peppers","Mushrooms","Onions","Garlic","Courgettes","Green beans"] },
      { title: "Meat & Seafood", items: ["Chicken","Beef mince","Steak","Sausages","Bacon","Ham","Fish","Salmon","Prawns","Deli meat"] },
      { title: "Freezer", items: ["Frozen vegetables","Frozen fruit","Frozen chips","Frozen pizza","Ice cream","Frozen meals","Ice cubes"] },
      { title: "Pantry", items: ["Rice","Pasta","Noodles","Flour","Sugar","Brown sugar","Salt","Pepper","Herbs","Spices","Stock cubes","Cooking oil","Olive oil","Vinegar","Soy sauce","Tomato sauce","BBQ sauce","Mayonnaise","Mustard","Honey","Peanut butter","Jam"] },
      { title: "Breakfast", items: ["Cereal","Porridge oats","Granola","Pancake mix","Maple syrup","Toast spreads"] },
      { title: "Lunchbox Items", items: ["Bread","Cheese","Ham","Crackers","Fruit","Yoghurt","Muesli bars","Rice cakes","Popcorn","Juice boxes"] },
      { title: "Snacks", items: ["Crisps","Biscuits","Crackers","Popcorn","Nuts","Trail mix","Chocolate","Lollies"] },
      { title: "Drinks", items: ["Water","Juice","Soft drinks","Sparkling water","Tea","Coffee","Hot chocolate"] },
      { title: "Household Cleaning", items: ["Washing-up liquid","Dishwasher tablets","Laundry detergent","Fabric softener","Multi-purpose cleaner","Bathroom cleaner","Toilet cleaner","Glass cleaner","Sponges","Bin bags","Paper towels"] },
      { title: "Household Essentials", items: ["Toilet paper","Kitchen roll","Tissues","Foil","Baking paper","Cling film","Sandwich bags","Bin liners"] },
      { title: "Toiletries", items: ["Shampoo","Conditioner","Body wash","Soap","Toothpaste","Toothbrushes","Deodorant","Hand wash","Moisturiser"] },
      { title: "Baby Essentials", items: ["Nappies","Baby wipes","Formula","Baby food","Nappy cream","Baby snacks"] },
      { title: "Pet Supplies", items: ["Pet food","Pet treats","Waste bags","Cat litter (if required)"] },
      { title: "This Week's Meal Plan", items: ["Monday","Tuesday","Wednesday","Thursday","Friday","Saturday","Sunday"] },
      { title: "Before Checkout", items: ["Check vouchers/coupons","Bring reusable shopping bags","Check loyalty card","Collect prescriptions (if needed)","Buy ice (if required)","Check fuel while out"] },
    ],
  },
};

// ─── TAB: Lists ───────────────────────────────────────────────────────────────
// ── Lists tab: To-do lists + existing checklist templates ─────────────────────
function ListsTab({ showToast, savedLists, setSavedLists, todos, setTodos, helpers }) {
  const [mode, setMode] = useState("todo");
  return (
    <div>
      <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
        {[["todo", "To-do"], ["checklists", "Checklists"]].map(([id, label]) => {
          const on = mode === id;
          return (
            <button key={id} onClick={() => setMode(id)}
              style={{ flex: 1, padding: "9px 10px", borderRadius: 10, fontSize: 13, fontWeight: 600, cursor: "pointer", border: `1px solid ${on ? BRAND.navy : BRAND.border}`, background: on ? BRAND.navy : BRAND.white, color: on ? BRAND.white : BRAND.muted }}>
              {label}
            </button>
          );
        })}
      </div>
      {mode === "todo"
        ? <TodoView todos={todos} setTodos={setTodos} helpers={helpers} showToast={showToast} />
        : <ChecklistsView showToast={showToast} savedLists={savedLists} setSavedLists={setSavedLists} />}
    </div>
  );
}

function TodoView({ todos, setTodos, helpers, showToast }) {
  const [filter, setFilter] = useState("all");
  const [quickText, setQuickText] = useState("");
  const [expandedId, setExpandedId] = useState(null);
  const [showDone, setShowDone] = useState(false);

  const today = toDS(new Date());

  const ownerName = (id) => helpers.find(h => String(h.id) === String(id))?.name || "";
  const ownerColor = (id) => { const i = helpers.findIndex(h => String(h.id) === String(id)); return i >= 0 ? OWNER_COLORS[i % OWNER_COLORS.length] : BRAND.grayMid; };
  const cat = (id) => TODO_CATEGORIES.find(c => c.id === id);

  const update = (id, patch) => setTodos(prev => prev.map(t => t.id === id ? { ...t, ...patch } : t));
  const remove = (id) => { setTodos(prev => prev.filter(t => t.id !== id)); setExpandedId(null); showToast("Task deleted"); };
  const add = () => {
    const text = quickText.trim();
    if (!text) return;
    const t = { id: Date.now() + Math.random(), list: "todo", text, status: "todo", due: "", ownerId: "", category: "", priority: false, waitingOn: "", waitingSince: "", link: "", notes: "", repeat: "none", remind: true, createdAt: new Date().toISOString(), completedAt: null };
    setTodos(prev => [t, ...prev]);
    setQuickText("");
  };
  const toggleDone = (t) => {
    if (t.status !== "done" && t.repeat && t.repeat !== "none" && t.due) {
      const next = advanceDue(t.due, t.repeat);
      update(t.id, { due: next, status: "todo", waitingSince: "", completedAt: null });
      showToast(`Done — next due ${new Date(next + "T12:00:00").toLocaleDateString("en-GB", { day: "numeric", month: "short" })}`);
      return;
    }
    update(t.id, t.status === "done" ? { status: "todo", completedAt: null } : { status: "done", completedAt: new Date().toISOString() });
  };
  const setStatus = (t, status) => {
    if (status === "waiting") update(t.id, { status: "waiting", waitingSince: t.waitingSince || today });
    else update(t.id, { status, completedAt: status === "done" ? new Date().toISOString() : null });
  };

  const listTodos = todos;
  const active = listTodos.filter(t => t.status !== "done");
  const done = listTodos.filter(t => t.status === "done");
  const isOverdue = (t) => t.due && t.due < today && t.status !== "done";
  const waitingCount = active.filter(t => t.status === "waiting").length;
  const overdueCount = active.filter(isOverdue).length;
  const repeatCount = active.filter(t => t.repeat && t.repeat !== "none").length;
  const shown = active.filter(t => filter === "waiting" ? t.status === "waiting" : filter === "overdue" ? isOverdue(t) : filter === "repeat" ? (t.repeat && t.repeat !== "none") : true);

  const fmtDue = (ds) => { try { return new Date(ds + "T12:00:00").toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" }); } catch { return ds; } };
  const daysSince = (ds) => { if (!ds) return 0; const a = new Date(ds + "T12:00:00"), b = new Date(today + "T12:00:00"); return Math.max(0, Math.round((b - a) / 86400000)); };

  const filterChip = (id, label, n) => {
    const on = filter === id;
    return (
      <button key={id} onClick={() => setFilter(id)}
        style={{ fontSize: 12, padding: "6px 11px", borderRadius: 20, cursor: "pointer", border: `1px solid ${on ? "#D7E4DB" : BRAND.border}`, background: on ? BRAND.sagePale : BRAND.white, color: on ? "#4f6b58" : BRAND.muted, fontWeight: on ? 600 : 500 }}>
        {label}{n > 0 && <span style={{ opacity: 0.6 }}> {n}</span>}
      </button>
    );
  };

  const renderMeta = (t) => {
    const c = cat(t.category);
    const over = isOverdue(t);
    return (
      <div style={{ display: "flex", gap: 6, flexWrap: "wrap", alignItems: "center" }}>
        {t.status === "waiting" && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 9px", borderRadius: 20, background: "#F5EFE6", color: "#7a5a2e", display: "inline-flex", alignItems: "center", gap: 5 }}>
            <Icon name="clock" size={12} color="#7a5a2e" />Waiting{t.waitingOn ? ` on ${t.waitingOn}` : ""}{t.waitingSince ? ` · ${daysSince(t.waitingSince)}d` : ""}
          </span>
        )}
        {t.due && t.status !== "waiting" && (
          <span style={{ display: "inline-flex", alignItems: "center", gap: 4, fontSize: 11.5, color: over ? "#B4524E" : BRAND.muted, fontWeight: over ? 600 : 400 }}>
            <Icon name="calendar" size={12} color={over ? "#B4524E" : BRAND.muted} />{fmtDue(t.due)}{over ? ` · ${daysSince(t.due)}d late` : ""}
          </span>
        )}
        {t.ownerId && ownerName(t.ownerId) && (
          <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 20, background: BRAND.gray, color: BRAND.muted, display: "inline-flex", alignItems: "center", gap: 4 }}>
            <span style={{ width: 6, height: 6, borderRadius: "50%", background: ownerColor(t.ownerId) }} />{ownerName(t.ownerId)}
          </span>
        )}
        {c && <span style={{ fontSize: 11, fontWeight: 600, padding: "2px 8px", borderRadius: 6, background: c.bg, color: c.text }}>{c.label}</span>}
        {t.link && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, color: BRAND.muted }}><Icon name="link" size={12} color={BRAND.muted} />link</span>}
        {t.repeat && t.repeat !== "none" && <span style={{ display: "inline-flex", alignItems: "center", gap: 3, fontSize: 11.5, color: BRAND.muted }}><Icon name="repeat" size={12} color={BRAND.muted} />repeats</span>}
      </div>
    );
  };

  const renderEditor = (t) => (
    <div style={{ padding: "2px 2px 16px 31px", display: "flex", flexDirection: "column", gap: 10 }}>
      <input value={t.text} onChange={e => update(t.id, { text: e.target.value })} style={{ ...S.input, marginBottom: 0 }} />
      <div style={{ display: "flex", gap: 10 }}>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Due date</label>
          <input type="date" value={t.due} onChange={e => update(t.id, { due: e.target.value })} style={{ ...S.input, marginBottom: 0 }} />
        </div>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Owner</label>
          <select value={t.ownerId} onChange={e => update(t.id, { ownerId: e.target.value })} style={{ ...S.input, marginBottom: 0 }}>
            <option value="">Anyone</option>
            {helpers.map(h => <option key={h.id} value={h.id}>{h.name}{h.relationship ? ` (${h.relationship})` : ""}</option>)}
          </select>
        </div>
      </div>
      {helpers.length === 0 && <div style={{ fontSize: 11, color: BRAND.muted, marginTop: -4 }}>Add family members under Carers &amp; Helpers to assign owners.</div>}
      <div>
        <label style={S.label}>Category</label>
        <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
          {TODO_CATEGORIES.map(c => {
            const on = t.category === c.id;
            return <button key={c.id} onClick={() => update(t.id, { category: on ? "" : c.id })}
              style={{ fontSize: 12, fontWeight: 600, padding: "5px 10px", borderRadius: 8, cursor: "pointer", border: `1px solid ${on ? c.dot : BRAND.border}`, background: on ? c.bg : BRAND.white, color: on ? c.text : BRAND.muted }}>{c.label}</button>;
          })}
        </div>
      </div>
      <div>
        <label style={S.label}>Status</label>
        <div style={{ display: "flex", gap: 6 }}>
          {[["todo", "To do"], ["waiting", "Waiting on"]].map(([id, label]) => {
            const on = (t.status === "done" ? "todo" : t.status) === id;
            return <button key={id} onClick={() => setStatus(t, id)}
              style={{ fontSize: 12, fontWeight: 600, padding: "6px 12px", borderRadius: 8, cursor: "pointer", border: `1px solid ${on ? BRAND.slate : BRAND.border}`, background: on ? "#EEF2F8" : BRAND.white, color: on ? "#3a5a8a" : BRAND.muted }}>{label}</button>;
          })}
        </div>
      </div>
      {t.status === "waiting" && <input value={t.waitingOn} onChange={e => update(t.id, { waitingOn: e.target.value })} placeholder="Waiting on… e.g. GP surgery" style={{ ...S.input, marginBottom: 0 }} />}
      <div style={{ display: "flex", gap: 10, alignItems: "flex-start" }}>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Repeats</label>
          <select value={t.repeat || "none"} onChange={e => update(t.id, { repeat: e.target.value })} style={{ ...S.input, marginBottom: 0 }}>
            {TODO_REPEATS.map(r => <option key={r.id} value={r.id}>{r.label}</option>)}
          </select>
        </div>
        <div style={{ flex: 1 }}>
          <label style={S.label}>Reminder</label>
          <div onClick={() => t.due && update(t.id, { remind: t.remind === false })} style={{ display: "flex", alignItems: "center", gap: 8, cursor: t.due ? "pointer" : "default", padding: "8px 0" }}>
            <span style={{ width: 34, height: 20, borderRadius: 20, background: (t.remind !== false && t.due) ? BRAND.sage : BRAND.border, position: "relative", flexShrink: 0, transition: "background 0.15s" }}>
              <span style={{ position: "absolute", top: 2, left: (t.remind !== false && t.due) ? 16 : 2, width: 16, height: 16, borderRadius: "50%", background: "#fff", transition: "left 0.15s", boxShadow: "0 1px 2px rgba(0,0,0,0.2)" }} />
            </span>
            <span style={{ fontSize: 13, color: t.due ? BRAND.ink : BRAND.muted }}>{!t.due ? "Set a due date" : t.remind === false ? "Off" : "On the day, 9am"}</span>
          </div>
        </div>
      </div>
      <input value={t.link} onChange={e => update(t.id, { link: e.target.value })} placeholder="Link or reference (optional)" style={{ ...S.input, marginBottom: 0 }} />
      <textarea value={t.notes} onChange={e => update(t.id, { notes: e.target.value })} placeholder="Notes (optional)" rows={2} style={{ ...S.input, marginBottom: 0, resize: "vertical", fontFamily: "inherit" }} />
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <button onClick={() => update(t.id, { priority: !t.priority })} style={{ ...S.btnGhost, fontSize: 12 }}>{t.priority ? "★ Priority" : "☆ Mark priority"}</button>
        <button onClick={() => remove(t.id)} style={{ background: "none", border: "none", color: "#B4524E", fontSize: 12, fontWeight: 600, cursor: "pointer" }}>Delete</button>
      </div>
    </div>
  );

  const renderTask = (t) => {
    const expanded = expandedId === t.id;
    const hasMeta = t.due || (t.ownerId && ownerName(t.ownerId)) || t.category || t.status === "waiting" || t.link || (t.repeat && t.repeat !== "none");
    return (
      <div key={t.id} style={{ borderBottom: `1px solid ${BRAND.border}` }}>
        <div style={{ display: "flex", gap: 11, padding: "12px 2px", alignItems: "flex-start" }}>
          <div role="checkbox" aria-checked="false" aria-label={`Mark "${t.text}" done`} tabIndex={0} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleDone(t); } }} onClick={() => toggleDone(t)} style={{ width: 18, height: 18, borderRadius: 5, border: `1.6px solid ${BRAND.grayMid}`, marginTop: 1, flexShrink: 0, cursor: "pointer" }} />
          <div style={{ flex: 1, minWidth: 0, cursor: "pointer" }} onClick={() => setExpandedId(expanded ? null : t.id)}>
            <div style={{ fontSize: 14, color: BRAND.ink, lineHeight: 1.3, marginBottom: hasMeta ? 6 : 0 }}>{t.text}</div>
            {hasMeta && renderMeta(t)}
          </div>
          <div role="button" aria-pressed={!!t.priority} aria-label={t.priority ? "Remove priority" : "Mark as priority"} tabIndex={0} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); update(t.id, { priority: !t.priority }); } }} onClick={() => update(t.id, { priority: !t.priority })} style={{ cursor: "pointer", marginTop: 1, flexShrink: 0 }}>
            <Icon name="star" size={17} color={t.priority ? "#C9A24A" : "#C6CDD3"} style={t.priority ? { fill: "#C9A24A" } : {}} />
          </div>
        </div>
        {expanded && renderEditor(t)}
      </div>
    );
  };

  return (
    <div>
      <div style={S.card}>
        <div style={{ display: "flex", gap: 8, marginBottom: 14 }}>
          <input value={quickText} onChange={e => setQuickText(e.target.value)} onKeyDown={e => e.key === "Enter" && add()} placeholder="Add a task…" style={{ ...S.input, marginBottom: 0, flex: 1 }} />
          <button aria-label="Add task" onClick={add} style={{ ...S.btnPrimary, width: 44, padding: 0, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 22, fontWeight: 500 }}>+</button>
        </div>

        <div style={{ display: "flex", gap: 7, flexWrap: "wrap", marginBottom: 4 }}>
          {filterChip("all", "All", active.length)}
          {filterChip("waiting", "Waiting on", waitingCount)}
          {filterChip("overdue", "Overdue", overdueCount)}
          {filterChip("repeat", "Repeats", repeatCount)}
        </div>

        {shown.length === 0
          ? <div style={{ textAlign: "center", padding: "22px 0 6px", fontSize: 13, color: BRAND.muted }}>{active.length === 0 ? "Nothing here yet — add your first task above." : "No tasks match this filter."}</div>
          : shown.map(renderTask)}
      </div>

      {done.length > 0 && (
        <div style={{ ...S.card, marginTop: 14 }}>
          <button onClick={() => setShowDone(s => !s)} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, fontWeight: 600, color: BRAND.muted, textTransform: "uppercase", letterSpacing: "0.5px", background: "none", border: "none", cursor: "pointer", padding: 0 }}>
            <span style={{ display: "inline-block", transform: showDone ? "none" : "rotate(-90deg)", transition: "transform 0.15s" }}>▾</span>Done ({done.length})
          </button>
          {showDone && done.map(t => (
            <div key={t.id} style={{ display: "flex", gap: 11, padding: "10px 2px 0", alignItems: "flex-start" }}>
              <div role="checkbox" aria-checked="true" aria-label={`Mark "${t.text}" not done`} tabIndex={0} onKeyDown={e => { if (e.key === "Enter" || e.key === " ") { e.preventDefault(); toggleDone(t); } }} onClick={() => toggleDone(t)} style={{ width: 18, height: 18, borderRadius: 5, background: BRAND.sage, display: "flex", alignItems: "center", justifyContent: "center", marginTop: 1, cursor: "pointer", flexShrink: 0 }}>
                <Icon name="check" size={12} color="#fff" />
              </div>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, color: BRAND.grayMid, textDecoration: "line-through" }}>{t.text}</div>
                <div style={{ fontSize: 11, color: BRAND.grayMid, marginTop: 2 }}>{ownerName(t.ownerId) ? `Done · ${ownerName(t.ownerId)}` : "Done"}{t.completedAt ? ` · ${new Date(t.completedAt).toLocaleDateString("en-GB", { weekday: "short" })}` : ""}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function ChecklistsView({ showToast, savedLists, setSavedLists }) {
  const [activeListId, setActiveListId] = useState(null);
  const [showNewForm, setShowNewForm] = useState(false);
  const [newTemplate, setNewTemplate] = useState("birthday");
  const [newName, setNewName] = useState("");
  const [editingName, setEditingName] = useState(false);
  const [newItemText, setNewItemText] = useState({});
  const [editingCol, setEditingCol] = useState(null);
  const [editMode, setEditMode] = useState(false);

  const activeList = savedLists.find(l => l.id === activeListId);
  const template = activeList ? LIST_TEMPLATES[activeList.templateKey] : null;
  const isColumnMode = !!(template && template.columns);
  const columnNames = activeList?.columnNames || template?.defaultColumns || ["Mum","Dad","Child 1","Child 2"];

  const ck = (listId, section, item) => `${section}|||${item}`;

  const toggle = (section, item) => setSavedLists(prev => prev.map(l => { if (l.id !== activeListId) return l; const key = ck(l.id, section, item); return { ...l, checks: { ...l.checks, [key]: !l.checks[key] } }; }));
  const toggleCol = (section, item, colIdx) => setSavedLists(prev => prev.map(l => { if (l.id !== activeListId) return l; const key = `${ck(l.id, section, item)}|||col${colIdx}`; return { ...l, checks: { ...l.checks, [key]: !l.checks[key] } }; }));
  const renameColumn = (colIdx, name) => { setSavedLists(prev => prev.map(l => { if (l.id !== activeListId) return l; const cols = [...(l.columnNames || template?.defaultColumns || [])]; cols[colIdx] = name; return { ...l, columnNames: cols }; })); setEditingCol(null); };
  const addCustomItem = (section) => { const text = (newItemText[section] || "").trim(); if (!text) return; setSavedLists(prev => prev.map(l => { if (l.id !== activeListId) return l; return { ...l, customItems: { ...l.customItems, [section]: [...(l.customItems[section] || []), text] } }; })); setNewItemText(prev => ({ ...prev, [section]: "" })); };
  const removeCustomItem = (section, item) => setSavedLists(prev => prev.map(l => { if (l.id !== activeListId) return l; const newChecks = { ...l.checks }; delete newChecks[ck(l.id, section, item)]; return { ...l, customItems: { ...l.customItems, [section]: (l.customItems[section] || []).filter(i => i !== item) }, checks: newChecks }; }));
  const removeTemplateItem = (section, item) => setSavedLists(prev => prev.map(l => { if (l.id !== activeListId) return l; const removed = l.removedItems || {}; return { ...l, removedItems: { ...removed, [section]: [...(removed[section] || []), item] } }; }));
  const isRemoved = (section, item) => !!(activeList?.removedItems?.[section]?.includes(item));
  const renameList = (name) => { setSavedLists(prev => prev.map(l => l.id === activeListId ? { ...l, name } : l)); setEditingName(false); };
  const deleteList = (id) => { setSavedLists(prev => prev.filter(l => l.id !== id)); if (activeListId === id) setActiveListId(null); showToast("List deleted"); };
  const createList = () => { if (!newName.trim()) return; const id = Date.now(); const tmpl = LIST_TEMPLATES[newTemplate]; const list = { id, templateKey: newTemplate, name: newName.trim(), checks: {}, customItems: {}, removedItems: {}, columnNames: tmpl.columns ? [...tmpl.defaultColumns] : undefined }; setSavedLists(prev => [...prev, list]); setActiveListId(id); setShowNewForm(false); setNewName(""); setEditMode(false); showToast(`"${list.name}" created`); };

  const totalItems = (list) => { const tmpl = LIST_TEMPLATES[list.templateKey]; return tmpl.sections.reduce((n, s) => n + s.items.filter(i => !list.removedItems?.[s.title]?.includes(i)).length + (list.customItems[s.title] || []).length, 0); };
  const doneItems = (list) => { const tmpl = LIST_TEMPLATES[list.templateKey]; return tmpl.sections.reduce((n, s) => { const allInSection = [...s.items.filter(i => !list.removedItems?.[s.title]?.includes(i)), ...(list.customItems[s.title] || [])]; return n + allInSection.filter(item => { const isCol = tmpl.columns; if (isCol) { return list.columnNames ? list.columnNames.some((_, ci) => list.checks[`${s.title}|||${item}|||col${ci}`]) : false; } return list.checks[ck(list.id, s.title, item)]; }).length; }, 0); };
  const progressFor = (list) => { const t = totalItems(list); return t ? Math.round((doneItems(list) / t) * 100) : 0; };

  // ── My Lists screen ───────────────────────────────────────────────────────
  if (!activeList) {
    return (
      <div>
        <div style={S.card}>
          <div style={{ fontSize: 16, fontWeight: 700, color: BRAND.navy, marginBottom: 16 }}>My Lists</div>
          {savedLists.length === 0 && !showNewForm && <div style={{ textAlign: "center", padding: "20px 0 24px", fontSize: 13, color: BRAND.muted }}>No lists yet. Create your first one from a template.</div>}
          {savedLists.map(list => { const pct = progressFor(list); return (
            <div key={list.id} onClick={() => { setActiveListId(list.id); setEditMode(false); }}
              style={{ padding: 14, borderRadius: 12, marginBottom: 10, cursor: "pointer", background: BRAND.gray, border: `1.5px solid ${BRAND.border}`, display: "flex", alignItems: "center", gap: 14 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.navy, marginBottom: 2 }}>{list.name}</div>
                <div style={{ fontSize: 12, color: BRAND.muted, marginBottom: 8 }}>{LIST_TEMPLATES[list.templateKey]?.label}</div>
                <div style={{ background: BRAND.border, borderRadius: 3, height: 5 }}><div style={{ width: `${pct}%`, height: 5, borderRadius: 3, background: pct === 100 ? BRAND.sage : BRAND.slate, transition: "width 0.3s" }} /></div>
                <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 4 }}>{doneItems(list)} of {totalItems(list)} done</div>
              </div>
              <div style={{ fontSize: 20, color: BRAND.border }}>›</div>
            </div>
          ); })}
          {showNewForm ? (
            <div style={{ background: BRAND.sagePale, borderRadius: 12, padding: 16, marginTop: 8 }}>
              <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.navy, marginBottom: 12 }}>New List</div>
              <label style={S.label}>Template</label>
              <select style={{ ...S.input, marginBottom: 12 }} value={newTemplate} onChange={e => setNewTemplate(e.target.value)}>
                {Object.entries(LIST_TEMPLATES).map(([key, tmpl]) => <option key={key} value={key}>{tmpl.label}</option>)}
              </select>
              <label style={S.label}>Name your list</label>
              <input style={{ ...S.input, marginBottom: 14 }} placeholder="e.g. Mia's 7th Birthday" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === "Enter" && createList()} autoFocus />
              <div style={{ display: "flex", gap: 8 }}>
                <button style={S.btnPrimary} onClick={createList}>Create list</button>
                <button style={S.btnGhost} onClick={() => { setShowNewForm(false); setNewName(""); }}>Cancel</button>
              </div>
            </div>
          ) : (
            <button style={{ ...S.btnSecondary, width: "100%", justifyContent: "center", marginTop: 8 }} onClick={() => setShowNewForm(true)}>New list</button>
          )}
        </div>
      </div>
    );
  }

  // ── Active list view ──────────────────────────────────────────────────────
  return (
    <div>
      <div style={S.card}>
        <div style={{ display: "flex", alignItems: "center", marginBottom: 12, gap: 8 }}>
          <button onClick={() => { setActiveListId(null); setEditMode(false); setEditingName(false); }}
            style={{ background: "none", border: "none", color: BRAND.slate, fontWeight: 700, fontSize: 14, cursor: "pointer", padding: "0 4px 0 0" }}>← My Lists</button>
          <div style={{ flex: 1 }} />
          <button onClick={() => { setEditMode(!editMode); setEditingName(false); }}
            style={{ ...S.btnGhost, fontSize: 12, padding: "6px 14px", background: editMode ? BRAND.navy : "transparent", color: editMode ? BRAND.white : BRAND.muted, border: `1.5px solid ${editMode ? BRAND.navy : BRAND.border}` }}>
            {editMode ? "Done editing" : "Edit"}
          </button>
        </div>
        {editMode ? (
          <input autoFocus defaultValue={activeList.name} style={{ ...S.input, fontSize: 17, fontWeight: 700, marginBottom: 8 }}
            onBlur={e => renameList(e.target.value)} onKeyDown={e => e.key === "Enter" && e.target.blur()} />
        ) : (
          <div style={{ fontSize: 17, fontWeight: 700, color: BRAND.navy, marginBottom: 4 }}>{activeList.name}</div>
        )}
        <div style={{ fontSize: 12, color: BRAND.muted, marginBottom: 10 }}>{LIST_TEMPLATES[activeList.templateKey]?.label} · {doneItems(activeList)} of {totalItems(activeList)} completed</div>
        {editMode && isColumnMode && (
          <div style={{ background: BRAND.sagePale, borderRadius: 10, padding: 12, marginBottom: 10 }}>
            <div style={{ fontSize: 12, fontWeight: 700, color: BRAND.navy, marginBottom: 10 }}>Rename columns</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {columnNames.map((col, ci) => (
                <div key={ci} style={{ display: "flex", flexDirection: "column", gap: 4, minWidth: 80 }}>
                  <label style={{ ...S.label, marginBottom: 2 }}>Column {ci + 1}</label>
                  <input style={{ ...S.input, fontSize: 13, padding: "7px 10px" }} defaultValue={col}
                    onBlur={e => renameColumn(ci, e.target.value || col)} onKeyDown={e => e.key === "Enter" && e.target.blur()} />
                </div>
              ))}
            </div>
          </div>
        )}
        {editMode && <button onClick={() => deleteList(activeList.id)} style={{ fontSize: 12, color: "#C0392B", background: "none", border: "none", cursor: "pointer", padding: 0, marginBottom: 8 }}>Delete this list</button>}
        <div style={{ background: BRAND.border, borderRadius: 4, height: 6 }}>
          <div style={{ width: `${progressFor(activeList)}%`, height: 6, borderRadius: 4, background: progressFor(activeList) === 100 ? BRAND.sage : BRAND.slate, transition: "width 0.3s" }} />
        </div>
        {progressFor(activeList) === 100 && <div style={{ fontSize: 12, color: BRAND.sage, fontWeight: 700, marginTop: 8 }}>All done!</div>}
      </div>

      {template.sections.map(section => {
        const allItems = [...section.items.filter(i => !isRemoved(section.title, i)), ...(activeList.customItems[section.title] || [])];
        if (isColumnMode) {
          return (
            <div key={section.title} style={{ ...S.card, overflowX: "auto" }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.navy, marginBottom: 12 }}>{section.title}</div>
              <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 360 }}>
                <thead><tr>
                  <th style={{ textAlign: "left", fontSize: 11, fontWeight: 700, color: BRAND.muted, padding: "0 0 10px", textTransform: "uppercase", letterSpacing: "0.4px" }}>Item</th>
                  {columnNames.map((col, ci) => (
                    <th key={ci} style={{ textAlign: "center", padding: "0 6px 10px", minWidth: 60 }}>
                      <span onClick={() => editMode && setEditingCol(ci)} style={{ fontSize: 11, fontWeight: 700, color: BRAND.slate, cursor: editMode ? "pointer" : "default", borderBottom: editMode ? `1px dashed ${BRAND.border}` : "none" }}>{col}</span>
                    </th>
                  ))}
                </tr></thead>
                <tbody>
                  {allItems.map((item, i) => {
                    const isCustom = (activeList.customItems[section.title] || []).includes(item);
                    return (
                      <tr key={i} style={{ background: i % 2 === 0 ? BRAND.white : BRAND.gray }}>
                        <td style={{ fontSize: 13, color: BRAND.navy, padding: "8px 0 8px 4px", borderBottom: `1px solid ${BRAND.border}` }}>
                          {item}
                          {editMode && <button onClick={() => isCustom ? removeCustomItem(section.title, item) : removeTemplateItem(section.title, item)} style={{ background: "none", border: "none", color: "#C0392B", cursor: "pointer", fontSize: 14, marginLeft: 8 }}>×</button>}
                        </td>
                        {columnNames.map((_, ci) => { const key = `${ck(activeList.id, section.title, item)}|||col${ci}`; const checked = !!activeList.checks[key]; return (
                          <td key={ci} style={{ textAlign: "center", padding: "8px 6px", borderBottom: `1px solid ${BRAND.border}` }}>
                            <div onClick={() => toggleCol(section.title, item, ci)}
                              style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${checked ? BRAND.sage : BRAND.border}`, background: checked ? BRAND.sage : BRAND.white, cursor: "pointer", margin: "0 auto", display: "flex", alignItems: "center", justifyContent: "center" }}>
                              {checked && <span style={{ color: "#fff", fontSize: 12, lineHeight: 1 }}>✓</span>}
                            </div>
                          </td>
                        ); })}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                <input style={{ ...S.input, fontSize: 12, flex: 1 }} placeholder="Add item…" value={newItemText[section.title] || ""}
                  onChange={e => setNewItemText(prev => ({ ...prev, [section.title]: e.target.value }))}
                  onKeyDown={e => e.key === "Enter" && addCustomItem(section.title)} />
                <button style={{ ...S.btnGhost, fontSize: 12, padding: "8px 12px" }} onClick={() => addCustomItem(section.title)}>Add</button>
              </div>
            </div>
          );
        }
        const doneCt = allItems.filter(item => activeList.checks[ck(activeList.id, section.title, item)]).length;
        return (
          <div key={section.title} style={S.card}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
              <div style={{ fontSize: 14, fontWeight: 700, color: BRAND.navy }}>{section.title}</div>
              <div style={{ fontSize: 11, color: BRAND.muted }}>{doneCt}/{allItems.length}</div>
            </div>
            {allItems.map((item, i) => { const checked = !!activeList.checks[ck(activeList.id, section.title, item)]; const isCustom = (activeList.customItems[section.title] || []).includes(item); return (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "8px 0", borderBottom: i < allItems.length - 1 ? `1px solid ${BRAND.border}` : "none" }}>
                <div onClick={() => toggle(section.title, item)}
                  style={{ width: 20, height: 20, borderRadius: 5, border: `2px solid ${checked ? BRAND.sage : BRAND.border}`, background: checked ? BRAND.sage : BRAND.white, cursor: "pointer", flexShrink: 0, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {checked && <span style={{ color: "#fff", fontSize: 13, lineHeight: 1 }}>✓</span>}
                </div>
                <span style={{ flex: 1, fontSize: 13, color: checked ? BRAND.muted : BRAND.navy, textDecoration: checked ? "line-through" : "none" }}>{item}</span>
                {editMode && <button onClick={() => isCustom ? removeCustomItem(section.title, item) : removeTemplateItem(section.title, item)} style={{ background: "none", border: "none", color: "#C0392B", cursor: "pointer", fontSize: 14, padding: "0 4px" }}>×</button>}
              </div>
            ); })}
            <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
              <input style={{ ...S.input, fontSize: 12, flex: 1 }} placeholder="Add item…" value={newItemText[section.title] || ""}
                onChange={e => setNewItemText(prev => ({ ...prev, [section.title]: e.target.value }))}
                onKeyDown={e => e.key === "Enter" && addCustomItem(section.title)} />
              <button style={{ ...S.btnGhost, fontSize: 12, padding: "8px 12px" }} onClick={() => addCustomItem(section.title)}>Add</button>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ─── TAB: Legal ───────────────────────────────────────────────────────────────
function LegalTab({ children, setChildren, helpers, setHelpers, setSchedule, setChildcare, setBreaks, setTerms, showToast, user, onSignOut }) {
  const [showDelete, setShowDelete] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const today = new Date().toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });

  const deleteAll = async () => {
    setDeleting(true);
    // Full account deletion runs server-side in the `delete-account` Edge Function
    // (supabase/functions/delete-account). It removes every row this user owns —
    // reminders, push subscriptions, reviews, family data — and then the login itself.
    // Nothing is cleared here unless the server confirms it worked, so a failure never
    // leaves someone half-deleted while being told they're gone.
    const { data, error } = await supabase.functions.invoke("delete-account", { body: {} });
    if (error || !data?.ok) {
      setDeleting(false);
      showToast(`Couldn't delete your account. Try again, or email ${CONTACT_EMAIL}`);
      return;
    }

    setChildren([]); setHelpers([]); setSchedule({}); setChildcare({}); setBreaks([]); setTerms([]);
    setShowDelete(false); setConfirmed(false); setDeleting(false);
    showToast("Your account has been deleted");
    onSignOut();
  };

  const Section = ({ title, children: c }) => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ fontSize: 13, fontWeight: 700, color: BRAND.navy, marginBottom: 8 }}>{title}</div>
      <div style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.7 }}>{c}</div>
    </div>
  );

  return (
    <div>
      <div style={S.card}>
        <div style={S.sectionTitle}>Privacy Policy</div>
        <div style={{ fontSize: 11, color: BRAND.muted, marginBottom: 18 }}>Last updated: {today}</div>

        <Section title="1. Who we are">MumHQ is a planning tool for families — holiday camps, after-school activities, school terms, childcare and family to-dos. MumHQ uses secure cloud-based account storage (powered by Supabase) so that your family's plan can be accessed from any device and shared with co-parents and carers. To use MumHQ you will create a personal account secured by an email address and password.</Section>

        <Section title="2. What information is processed">To provide the service, MumHQ processes the following personal data that you choose to enter:<ul style={{ marginTop: 8, paddingLeft: 18 }}><li>Children's first names and ages</li><li>Children's activity interests and preferences</li><li>Carers' and helpers' names, relationships, and contact details (phone or email)</li><li>School break dates</li><li>Holiday camp and activity bookings and notes</li><li>Activity reviews and ratings you choose to submit (these are shared with other parents using MumHQ)</li><li>To-do lists, reminders and notes you create</li><li>Your postcode, if you enter one to find activities near you</li><li>Text you paste or type into the AI Inbox, so it can be turned into calendar events and tasks</li><li>If you turn on reminders: a notification subscription for your device, so reminders can be sent to it</li><li>Your account email address (used for login and account recovery)</li></ul><br /><strong>Beyond your email address, you are not required to enter any other information.</strong> Where you enter personal data about others (for example, a carer's email address), you are responsible for ensuring you have their permission to do so.</Section>

        <Section title="3. Lawful basis for processing">Under the UK Data Protection Act 2018 and the UK General Data Protection Regulation (UK GDPR), MumHQ processes personal data on the basis of <strong>performance of a service requested by you</strong> (Article 6(1)(b) UK GDPR). You have actively chosen to use MumHQ and to enter information into it in order to plan and organise your family's activities, childcare and schedule.</Section>

        <Section title="4. How your data is stored">All data entered into MumHQ is stored securely in your <strong>personal account database</strong>, hosted on Supabase — a GDPR-compliant cloud infrastructure provider based in the European Union. Your data is protected by your account login credentials and is only accessible to you when signed in. The one exception is reviews you choose to post, which are shared with other parents (see section 5). MumHQ does not share your personal data with advertisers or analytics providers.</Section>

        <Section title="5. Reviews shared with other parents">If you post a review or rating for a holiday camp or activity, it is shared with other parents using MumHQ to help them choose. Reviews are shown without your name or email address, but are linked to your account so they can be managed. <>You can ask for any review you have submitted to be removed by emailing <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: BRAND.slate }}>{CONTACT_EMAIL}</a>, and deleting your account deletes your reviews too. Other parents can report a review or hide reviews from a particular person; reported reviews that are abusive or include personal details will be removed. Please don't include your children's names or other personal information in review text.</></Section>

        <Section title="6. Your responsibilities">As the person entering information into MumHQ, you are responsible for: the accuracy of the information you enter; obtaining consent from any third parties whose personal details you enter; any information you choose to share externally via the Share tab; and keeping your account credentials secure.</Section>

        <Section title="7. Your rights under UK GDPR">You have the following rights: <strong>access</strong> (view all data via the Profiles tab); <strong>rectification</strong> (edit any information at any time); <strong>erasure</strong> (delete all data using the button below); <strong>restriction</strong> (choose not to enter certain information); <strong>portability</strong> (export your schedule via the Share tab). You also have the right to lodge a complaint with the Information Commissioner's Office (ICO) at ico.org.uk or by calling 0303 123 1113. We will respond to formal data rights requests within 30 days.</Section>

        <Section title="8. Data retention">Your data is retained in your MumHQ account for as long as your account remains active. If you delete your account, your login and all associated data are deleted from our live systems straight away. Any copies held in routine backups are removed within 30 days.</Section>

        <Section title="9. Third-party services">MumHQ uses the following third-party services:<ul style={{ marginTop: 8, paddingLeft: 18 }}><li><strong>Supabase</strong> — secure account authentication and cloud database storage. GDPR-compliant, EU-based. Privacy policy: supabase.com/privacy.</li><li><strong>Anthropic Claude API</strong> — powers the AI Inbox (turning emails, letters and invitations into events and tasks). Only the text you paste or type is sent; no profile data is included. Privacy policy: anthropic.com/privacy.</li><li><strong>postcodes.io</strong> — looks up the postcode you enter so we can show how far activities are from you. Only the postcode is sent, never your name or account details.</li><li><strong>Apple, Google and Mozilla push services</strong> — if you turn on reminders, your device's notification service delivers them. They receive the reminder text and a device address, not your account details.</li><li><strong>Replit</strong> — hosts the MumHQ website. Like any web host, it may log your IP address for security.</li><li><strong>Google Fonts</strong> — supplies the typeface MumHQ uses, so Google receives your IP address when the app loads.</li></ul>MumHQ does not use advertising networks, social media trackers or analytics services.</Section>

        <Section title="10. Children's data">MumHQ is designed to be used by parents and guardians. First names and ages of children are processed solely for personalising your family's holiday plan. We recommend using first names only.</Section>

        <Section title="11. Contact">MumHQ is the data controller for the personal data you provide. If you have questions about this Privacy Policy or wish to exercise your data rights, email us at <a href={`mailto:${CONTACT_EMAIL}`} style={{ color: BRAND.slate }}>{CONTACT_EMAIL}</a>. You also have the right to contact the ICO directly at ico.org.uk or 0303 123 1113.</Section>
      </div>

      <div style={S.card}>
        <div style={S.sectionTitle}>Your Data</div>
        <p style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.6, marginBottom: 16 }}>You can edit or delete any information in your account at any time. All data is stored securely and only accessible when you are signed in.</p>
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
          <div style={{ padding: "10px 14px", background: BRAND.sagePale, borderRadius: 8, fontSize: 13, color: BRAND.navy }}><strong>{children.length}</strong> child profile{children.length !== 1 ? "s" : ""} stored</div>
          <div style={{ padding: "10px 14px", background: BRAND.sagePale, borderRadius: 8, fontSize: 13, color: BRAND.navy }}><strong>{helpers.length}</strong> carer{helpers.length !== 1 ? "s" : ""} stored</div>
        </div>
        <p style={{ fontSize: 12, color: BRAND.muted, marginTop: 12, lineHeight: 1.5 }}>To edit individual profiles, use the Profiles button at the top of the app. To permanently delete your account and all your data, use the button below.</p>
      </div>

      <div style={{ ...S.card, border: "1.5px solid #FDEAED" }}>
        <div style={{ ...S.sectionTitle, color: "#C0392B" }}>Delete your account</div>
        <p style={{ fontSize: 13, color: BRAND.muted, lineHeight: 1.6, marginBottom: 16 }}>This permanently deletes your MumHQ login and everything in it — child profiles, carer details, school breaks and terms, your schedule, to-dos, reminders and any reviews you've submitted. You'll be signed out straight away. This can't be undone.</p>
        {!showDelete ? (
          <button style={{ background: "#C0392B", color: "#fff", border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: "pointer" }} onClick={() => setShowDelete(true)}>Delete account</button>
        ) : (
          <div style={{ background: "#FFF5F5", borderRadius: 10, padding: 16, border: "1px solid #FDEAED" }}>
            <p style={{ fontSize: 13, fontWeight: 600, color: "#C0392B", marginBottom: 12 }}>Are you sure? This will permanently delete:</p>
            <ul style={{ fontSize: 13, color: BRAND.muted, paddingLeft: 18, marginBottom: 16, lineHeight: 1.7 }}>
              <li>Your login (email and password)</li><li>All child profiles and preferences</li><li>All carer and helper details</li><li>All school break and term dates</li><li>Your schedule, calendar, to-dos and reminders</li><li>All reviews you have submitted</li>
            </ul>
            <label style={{ display: "flex", alignItems: "flex-start", gap: 10, fontSize: 13, color: BRAND.navy, marginBottom: 16, cursor: "pointer" }}>
              <input type="checkbox" checked={confirmed} onChange={e => setConfirmed(e.target.checked)} style={{ marginTop: 2 }} />
              I understand this action is permanent and cannot be undone
            </label>
            <div style={{ display: "flex", gap: 8 }}>
              <button style={{ background: confirmed ? "#C0392B" : BRAND.grayMid, color: "#fff", border: "none", borderRadius: 8, padding: "11px 18px", fontSize: 14, fontWeight: 600, cursor: confirmed && !deleting ? "pointer" : "not-allowed", opacity: confirmed ? 1 : 0.5 }} disabled={!confirmed || deleting} onClick={deleteAll}>{deleting ? "Deleting…" : "Yes, delete my account"}</button>
              <button style={S.btnGhost} onClick={() => { setShowDelete(false); setConfirmed(false); }}>Cancel</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

// Downscale/re-encode an image file to a JPEG data URL before sending to the
// parser — keeps phone photos small (payload + cost) and converts HEIC to JPEG.
function downscaleImage(file, maxDim = 1600, quality = 0.8) {
  return new Promise((resolve, reject) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      URL.revokeObjectURL(url);
      let { width, height } = img;
      if (Math.max(width, height) > maxDim) {
        const scale = maxDim / Math.max(width, height);
        width = Math.round(width * scale);
        height = Math.round(height * scale);
      }
      const canvas = document.createElement("canvas");
      canvas.width = width; canvas.height = height;
      canvas.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(canvas.toDataURL("image/jpeg", quality));
    };
    img.onerror = () => { URL.revokeObjectURL(url); reject(new Error("Could not read image")); };
    img.src = url;
  });
}

function CalendarTab({ breaks, schedule, childcare, schoolEvents, setSchoolEvents, showToast }) {
  const today = new Date();
  const [viewYear, setViewYear] = useState(today.getFullYear());
  const [viewMonth, setViewMonth] = useState(today.getMonth());
  const [selectedDate, setSelectedDate] = useState(null);
  const [showParser, setShowParser] = useState(false);
  const [emailText, setEmailText] = useState("");
  const [parsedEvents, setParsedEvents] = useState([]);
  const [parsing, setParsing] = useState(false);
  const [selectedParsed, setSelectedParsed] = useState({});

  const days = getCalendarDays(viewYear, viewMonth);
  const prevMonth = () => { if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); } else setViewMonth(m => m - 1); };
  const nextMonth = () => { if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); } else setViewMonth(m => m + 1); };

  const selectedDs = selectedDate ? toDS(selectedDate) : undefined;
  const selectedEvents = selectedDate ? (schoolEvents[selectedDs] || []) : [];

  // Add a school event to a date
  const addEvent = (ds, event) => {
    setSchoolEvents(prev => ({ ...prev, [ds]: [...(prev[ds] || []), { ...event, id: Date.now() + Math.random() }] }));
  };
  const removeEvent = (ds, id) => {
    setSchoolEvents(prev => ({ ...prev, [ds]: (prev[ds] || []).filter(e => e.id !== id) }));
  };

  // Manual add form
  const [manualTitle, setManualTitle] = useState("");
  const [manualTime, setManualTime] = useState("");
  const [manualNote, setManualNote] = useState("");

  const saveManual = () => {
    if (!selectedDate || !manualTitle.trim()) return;
    addEvent(selectedDs, { title: manualTitle.trim(), time: manualTime, note: manualNote, source: "manual" });
    setManualTitle(""); setManualTime(""); setManualNote("");
    showToast("Event added ✓");
  };

  // AI email parser
  const runParse = async (body) => {
    setParsing(true); setParsedEvents([]);
    try {
      const { data, error } = await supabase.functions.invoke("parse-inbox", { body });
      if (error) throw new Error(error.message || "Parser unavailable");
      if (data?.error) throw new Error(data.error);
      const parsed = Array.isArray(data?.events) ? data.events : [];
      setParsedEvents(parsed);
      const sel = {};
      parsed.forEach((_, i) => sel[i] = true);
      setSelectedParsed(sel);
      if (parsed.length === 0) showToast("No dated events found — try a clearer photo or more text");
    } catch (err) {
      console.error("Parser error:", err);
      showToast("Could not parse — " + (err?.message || "please try again"));
      setParsedEvents([]);
    }
    setParsing(false);
  };

  const parseEmail = () => {
    if (!emailText.trim()) return;
    runParse({ text: emailText, year: viewYear });
  };

  const parseImageFile = async (file) => {
    if (!file) return;
    try {
      const dataUrl = await downscaleImage(file);
      const base64 = dataUrl.split(",")[1];
      runParse({ image: base64, mediaType: "image/jpeg", year: viewYear });
    } catch (e) {
      showToast("Couldn't read that image — try another photo");
    }
  };

  const addParsedEvents = () => {
    let count = 0;
    parsedEvents.forEach((ev, i) => {
      if (!selectedParsed[i]) return;
      if (!ev.date) return;
      addEvent(ev.date, { title: ev.title, time: ev.time || "", note: ev.note || "", source: "email" });
      count++;
    });
    showToast(`${count} event${count !== 1 ? "s" : ""} added to calendar ✓`);
    setParsedEvents([]); setEmailText(""); setShowParser(false);
    // Jump to month of first added event
    if (parsedEvents[0]?.date) {
      const d = new Date(parsedEvents[0].date);
      setViewMonth(d.getMonth()); setViewYear(d.getFullYear());
    }
  };

  const EVENT_COLORS = {
    school: CAT.school,
    activity: CAT.activity,
    holiday: CAT.holiday,
    home: CAT.home,
    childcare: CAT.childcare,
  };

  return (
    <div>
      {/* ── Header bar ── */}
      <div style={S.card}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 4 }}>
          <div style={{ fontSize: 16, fontWeight: 600, color: BRAND.navy, display: "flex", alignItems: "center", gap: 8 }}>
            <IconCalendar size={18} color={BRAND.slate} /> School Calendar
          </div>
          <button style={{ background: BRAND.white, color: BRAND.muted, border: `1.5px solid ${BRAND.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 12, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 6, whiteSpace: "nowrap" }} onClick={() => setShowParser(!showParser)}>
            {showParser ? "Close" : <><IconMail size={15} color={BRAND.muted} /> Paste school email</>}
          </button>
        </div>
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap", fontSize: 11 }}>
          {Object.entries(EVENT_COLORS).map(([type, c]) => (
            <div key={type} style={{ display: "flex", alignItems: "center", gap: 4, color: BRAND.muted }}>
              <div style={{ width: 8, height: 8, borderRadius: "50%", background: c.dot }} />
              {type === "school" ? "School event" : type === "activity" ? "Camp/Activity" : type === "holiday" ? "Holiday" : type === "home" ? "At home" : "Childcare"}
            </div>
          ))}
        </div>
      </div>

      {/* ── Email parser ── */}
      {showParser && (
        <div style={{ ...S.card, border: `1.5px solid ${BRAND.slate}` }}>
          <div style={{ fontSize: 15, fontWeight: 600, color: BRAND.navy, marginBottom: 14, display: "flex", alignItems: "center", gap: 8 }}>
            <IconMail size={16} color={BRAND.slate} /> Extract events from an email or photo
          </div>
          <p style={{ fontSize: 13, color: BRAND.muted, marginBottom: 12, lineHeight: 1.6 }}>
            Paste a school email, or add a photo of a party invitation or letter. AI pulls out the dates and events and lets you add them to your calendar in one tap.
          </p>
          <textarea
            style={{ ...S.input, height: 120, resize: "vertical", marginBottom: 10, fontSize: 13 }}
            placeholder={"Paste your school email here…\n\nExample:\n\"Dear Parents, a reminder that Class 3's assembly is on Friday 15th June at 9:30am. Please also note Dress Up Day is on Monday 22nd June.\""}
            value={emailText}
            onChange={e => setEmailText(e.target.value)}
          />
          <button style={S.btnPrimary} onClick={parseEmail} disabled={parsing || !emailText.trim()}>
            {parsing ? "Reading…" : "Extract events"}
          </button>

          <div style={{ display: "flex", alignItems: "center", gap: 10, margin: "14px 0" }}>
            <div style={{ flex: 1, height: 1, background: BRAND.border }} />
            <span style={{ fontSize: 12, color: BRAND.muted }}>or</span>
            <div style={{ flex: 1, height: 1, background: BRAND.border }} />
          </div>
          <label style={{ ...S.btnSecondary, display: "inline-flex", alignItems: "center", justifyContent: "center", gap: 8, cursor: parsing ? "default" : "pointer", opacity: parsing ? 0.6 : 1, width: "100%" }}>
            <Icon name="image" size={16} color={BRAND.slate} /> Add a photo of an invite or letter
            <input type="file" accept="image/*" disabled={parsing} style={{ display: "none" }}
              onChange={e => { const f = e.target.files && e.target.files[0]; e.target.value = ""; parseImageFile(f); }} />
          </label>

          {/* Parsed results */}
          {parsedEvents.length > 0 && (
            <div style={{ marginTop: 16 }}>
              <div style={{ fontSize: 13, fontWeight: 600, color: BRAND.navy, marginBottom: 10 }}>
                Found {parsedEvents.length} event{parsedEvents.length !== 1 ? "s" : ""} — tick the ones to add:
              </div>
              {parsedEvents.map((ev, i) => (
                <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: 10, padding: "10px 0", borderBottom: `1px solid ${COLORS.border}` }}>
                  <input type="checkbox" checked={!!selectedParsed[i]} onChange={e => setSelectedParsed({ ...selectedParsed, [i]: e.target.checked })}
                    style={{ marginTop: 3, accentColor: BRAND.slate, width: 16, height: 16, flexShrink: 0 }} />
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: COLORS.ink }}>{ev.title}</div>
                    <div style={{ fontSize: 12, color: COLORS.muted, marginTop: 2 }}>
                      📅 {ev.date ? new Date(ev.date + "T12:00:00").toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long" }) : "Date unclear"}
                      {ev.time && <span>  🕐 {ev.time}</span>}
                    </div>
                    {ev.note && <div style={{ fontSize: 12, color: COLORS.muted, fontStyle: "italic", marginTop: 2 }}>📝 {ev.note}</div>}
                  </div>
                </div>
              ))}
              <div style={{ display: "flex", gap: 8, marginTop: 14 }}>
                <button style={S.btnPrimary} onClick={addParsedEvents}
                  disabled={!Object.values(selectedParsed).some(Boolean)}>
                  Add selected to calendar
                </button>
                <button style={S.btnGhost} onClick={() => { setParsedEvents([]); setEmailText(""); }}>Clear</button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── Month navigator ── */}
      <div style={{ ...S.card, padding: "14px 16px" }}>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
          <button style={{ ...S.btnGhost, fontSize: 18, padding: "4px 12px" }} onClick={prevMonth}>‹</button>
          <div style={{ fontWeight: 600, fontSize: 16, color: BRAND.navy }}>{MONTHS[viewMonth]} {viewYear}</div>
          <button style={{ ...S.btnGhost, fontSize: 18, padding: "4px 12px" }} onClick={nextMonth}>›</button>
        </div>

        {/* Weekday headers */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 2, marginBottom: 4 }}>
          {WEEKDAYS.map(d => (
            <div key={d} style={{ textAlign: "center", fontSize: 11, fontWeight: 500, color: BRAND.muted, letterSpacing: "0.04em", padding: "4px 0" }}>{d}</div>
          ))}
        </div>

        {/* Day grid */}
        <div style={{ display: "grid", gridTemplateColumns: "repeat(7, 1fr)", gap: 3 }}>
          {days.map((date, i) => {
            if (!date) return <div key={i} />;
            const ds = toDS(date);
            const isToday = isSameDay(date, today);
            const isSelected = isSameDay(date, selectedDate);
            const inBreak = isInBreak(date, breaks);
            const dayEvts = getDayEvents(date, schedule, childcare, schoolEvents);
            const schoolEvts = schoolEvents[ds] || [];
            const isWeekend = date.getDay() === 0 || date.getDay() === 6;

            return (
              <div key={i}
                onClick={() => setSelectedDate(isSelected ? null : date)}
                style={{
                  borderRadius: 8,
                  padding: "6px 4px",
                  cursor: "pointer",
                  background: isSelected ? BRAND.navy : isToday ? "#EEF2F7" : inBreak ? BRAND.sagePale : BRAND.white,
                  border: isSelected ? `1.5px solid ${BRAND.navy}` : `1px solid ${BRAND.border}`,
                  minHeight: 52,
                  transition: "all 0.1s",
                }}>
                <div style={{ textAlign: "center", fontSize: 12, fontWeight: isToday || isSelected ? 600 : 400, color: isSelected ? BRAND.white : isWeekend ? BRAND.grayMid : BRAND.ink, marginBottom: 3 }}>
                  {date.getDate()}
                </div>
                <div style={{ display: "flex", flexWrap: "wrap", gap: 2, justifyContent: "center" }}>
                  {dayEvts.slice(0, 3).map((ev, ei) => {
                    const ec = EVENT_COLORS[ev.type] || EVENT_COLORS.school;
                    return <div key={ei} style={{ width: 6, height: 6, borderRadius: "50%", background: isSelected ? "rgba(255,255,255,0.8)" : ec.dot }} />;
                  })}
                  {dayEvts.length > 3 && <div style={{ fontSize: 8, color: isSelected ? "rgba(255,255,255,0.7)" : BRAND.muted }}>+{dayEvts.length - 3}</div>}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Selected day detail ── */}
      {selectedDate && (
        <div style={S.card}>
          <div style={{ fontWeight: 600, fontSize: 15, color: BRAND.navy, marginBottom: 14 }}>
            {selectedDate.toLocaleDateString("en-GB", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
          </div>

          {/* Existing events */}
          {(() => {
            const allEvts = getDayEvents(selectedDate, schedule, childcare, schoolEvents);
            if (allEvts.length === 0) return <div style={{ fontSize: 13, color: BRAND.muted, fontStyle: "italic", marginBottom: 14 }}>Nothing planned yet</div>;
            return (
              <div style={{ marginBottom: 16 }}>
                {allEvts.map((ev, i) => {
                  const ec = EVENT_COLORS[ev.type] || EVENT_COLORS.school;
                  const schoolEv = ev.type === "school" ? (schoolEvents[selectedDs] || []).find(e => e.title === ev.label) : null;
                  return (
                    <div key={i} style={{ display: "flex", alignItems: "center", gap: 8, padding: "7px 10px", borderRadius: 8, background: ec.bg, border: `1px solid ${ec.border}`, marginBottom: 6 }}>
                      <div style={{ width: 8, height: 8, borderRadius: "50%", background: ec.dot, flexShrink: 0 }} />
                      <div style={{ flex: 1 }}>
                        <span style={{ fontSize: 13, fontWeight: 500, color: BRAND.ink }}>{ev.label}</span>
                        {schoolEv?.time && <span style={{ fontSize: 12, color: BRAND.muted, marginLeft: 8 }}>{schoolEv.time}</span>}
                        {schoolEv?.note && <div style={{ fontSize: 11, color: BRAND.muted, marginTop: 2 }}>{schoolEv.note}</div>}
                      </div>
                      {schoolEv && (
                        <button onClick={() => removeEvent(selectedDs, schoolEv.id)} style={{ background: "none", border: "none", cursor: "pointer", color: BRAND.muted, fontSize: 14, padding: 0 }}>✕</button>
                      )}
                    </div>
                  );
                })}
              </div>
            );
          })()}

          {/* Add school event manually */}
          <div style={{ borderTop: `1px solid ${BRAND.border}`, paddingTop: 14 }}>
            <div style={{ fontSize: 12, fontWeight: 600, color: BRAND.muted, textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 10 }}>Add school event</div>
            <div style={S.row}>
              <div style={{ flex: 2 }}>
                <label style={S.label}>Event</label>
                <input style={S.input} placeholder="e.g. Class assembly, Sports Day" value={manualTitle} onChange={e => setManualTitle(e.target.value)}
                  onKeyDown={e => e.key === "Enter" && saveManual()} />
              </div>
              <div style={S.col}>
                <label style={S.label}>Time</label>
                <input style={S.input} placeholder="e.g. 9:30am" value={manualTime} onChange={e => setManualTime(e.target.value)} />
              </div>
            </div>
            <div style={{ marginBottom: 10 }}>
              <label style={S.label}>Notes</label>
              <input style={S.input} placeholder="e.g. Dress up as a tiger, bring props" value={manualNote} onChange={e => setManualNote(e.target.value)} />
            </div>
            <button style={S.btnPrimary} onClick={saveManual} disabled={!manualTitle.trim()}>Add event</button>
          </div>
        </div>
      )}

      {!selectedDate && (
        <div style={{ ...S.emptyState, padding: "16px 0" }}>
          <div style={{ fontSize: 13, color: BRAND.muted }}>Tap any day to see details or add a school event</div>
        </div>
      )}
    </div>
  );
}
export default function App() {
  return (
    <AuthGate>
      {(user, signOut) => <MumHQ user={user} onSignOut={signOut} />}
    </AuthGate>
  );
}
